import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Routes, Route, Link, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  LayoutDashboard,
  Users,
  User,
  Shield,
  Package,
  MessageSquare,
  BarChart3,
  DollarSign,
  Settings,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Plus,
  RefreshCw,
  Eye,
  Edit3,
  Edit2,
  Trash2,
  Copy,
  ExternalLink,
  Pencil,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  Search,
  Sparkles,
  Zap,
  Globe,
  Lock,
  TestTube,
  FileText,
  Calendar,
  Tag,
  Image,
  Layers,
  Link2,
  Info,
  Building,
  Building2,
  Scale,
  CreditCard,
  TrendingUp,
  PieChart,
  Activity,
  Menu,
  X,
  Star,
  LogIn,
  Database,
  Files,
  BookOpen,
  ClipboardList,
  Download,
  Plug,
  Map,
  MapPin,
  XCircle,
  Wrench,
  Heart,
  CheckCircle,
  UserPlus,
  Wifi,
  WifiOff,
  Save,
  Loader2,
  Camera,
  ArrowUpRight,
  Scissors,
  Paintbrush,
  Palette,
  Utensils,
  Leaf,
  ShoppingBag,
  LayoutGrid,
  Rocket,
  Music,
  Check,
  Upload,
  Key,
  Briefcase,
  CalendarHeart,
  HeartHandshake,
  BadgeCheck,
  Share,
  ArrowUp
} from "lucide-react";
import { jsPDF } from "jspdf";
import PrivatePortalsSection from "./PrivatePortalsSection";
import LinkedDashboardsManager from "@/components/admin/LinkedDashboardsManager";
import UpdatesManager from "@/components/admin/UpdatesManager";
import PilotsDashboard from "./pilots/PilotsDashboard";
import CategoryManagementPage from "./CategoryManagementPage";
import AdminCategoryMasterPage from "./AdminCategoryMasterPage";
import AdminHostApplicationsPage from "./AdminHostApplicationsPage";
// Iter 9.29 - new admin surfaces
import AdminProductHubPage from "./AdminProductHubPage";
import AdminApplicationsPage from "./AdminApplicationsPage";
import AdminDocsIndexPage from "./AdminDocsIndexPage";
// Iter 9.30 - Launch Control
import AdminLaunchControlPage from "./AdminLaunchControlPage";
import InstructorPilotsPage from "./InstructorPilotsPage";
import TemplatesAdminPage from "./TemplatesAdminPage";
import ProductMaintenanceHub from "./ProductMaintenanceHub";
import InvestorPortalsPage from "./InvestorPortalsPage";
import DefaultsInspoAdminPage from "./DefaultsInspoAdminPage";
import DashboardEditorPage from "./DashboardEditorPage";
import LaunchControlPanel from "@/components/admin/LaunchControlPanel";
import FinancialsSection from "@/components/admin/FinancialsSection";
import CoreFilesSection from "@/components/admin/CoreFilesSection";
import AirtablePeoplePage from "@/components/admin/PeoplePage/AirtablePeoplePage";
import RoadmapSection from "@/components/admin/RoadmapSection";
import UpdatesSection from "@/components/admin/UpdatesSection";
import MarketingSection from "@/components/admin/MarketingSection";
import InnerCircleSection from "@/components/admin/InnerCircleSection";
import AdminAllProfiles from "@/components/admin/AdminAllProfiles";
// Iter 9.39b - SuperAdminPage deleted. Controls moved to Settings → Access Control (password locked).
import HostDashboardTemplateEditor from "@/components/admin/HostDashboardTemplateEditor";
import MirroredProfileEditor from "@/components/admin/MirroredProfileEditor";
import ComprehensiveAdminPanel from "./ComprehensiveAdminPanel";
import AdminCopyEditorPage from "./AdminCopyEditorPage";
import AdminMasterProductsPage from "./AdminMasterProductsPage";
import AdminAdvocatesPage from "./AdminAdvocatesPage";
import AdminServiceCatalogPage from "./AdminServiceCatalogPage";
import AdminBrandsPage from "./AdminBrandsPage";
import AdminFaqPage from "./AdminFaqPage";
import AdminUpdatesPage from "./AdminUpdatesPage";
import AdminMessagesPage from "./AdminMessagesPage";
import AdminMeTemplatePage from "./AdminMeTemplatePage";
// Iter 9.38 - master databases (services / occasions / experiences)
import AdminMasterServicesPage from "./AdminMasterServicesPage";
import AdminPendingUGCPage from "./AdminPendingUGCPage";
import AdminFeatureAccessMatrixPage from "./AdminFeatureAccessMatrixPage";
import AdminMediaLibraryPage from "./AdminMediaLibraryPage";
import AdminPreferenceVisibilityPage from "./AdminPreferenceVisibilityPage";
import AdminMasterComponentsPage from "./AdminMasterComponentsPage";
import AdminMasterInspoPage from "./AdminMasterInspoPage";
import AdminMasterOccasionsPage from "./AdminMasterOccasionsPage";
import AdminPreferenceShareTemplatesPage from "./AdminPreferenceShareTemplatesPage";
import AdminMasterExperiencesPage from "./AdminMasterExperiencesPage";
import AdminDatabaseBrowserSection from "@/components/admin/AdminDatabaseBrowserSection";
import CategoryTemplateEditor from "@/components/admin/CategoryTemplateEditor";
import UnifiedTopBar from "@/components/UnifiedTopBar";
import PortalsHub from "@/components/admin/PortalsHub";
import UserflowsViewer from "@/components/admin/UserflowsViewer";
import UserflowsVisualPage from "@/components/admin/UserflowsVisualPage";
import AdminMasterIntakesPage from "@/pages/admin/AdminMasterIntakesPage";
import AdminMasterFeaturesPage from "@/pages/admin/AdminMasterFeaturesPage";
import InsightsAtlasPage from "@/pages/admin/InsightsAtlasPage";
import BrandPilotsAdminPage from "@/pages/admin/BrandPilotsAdminPage";
import UserflowsCatalogPage from "@/pages/admin/UserflowsCatalogPage";
import AdminRoadmapPage from "@/pages/admin/AdminRoadmapPage";
import PeopleHub from "@/components/admin/PeopleHub";
import AdminSettingsHub from "@/components/admin/AdminSettingsHub";
// VIP Lists functionality is now inside pilot dashboard Lists tab

// Wrapper component for CategoryManagementPage inside admin layout
function CategoryManagementSection() {
  return <CategoryManagementPage />;
}

import { isSuperAdminEmail, SUPER_ADMIN_EMAILS } from '@/config/platform';

const SUPER_ADMIN_EMAIL = SUPER_ADMIN_EMAILS[0];

// Admin Layout with Sidebar Navigation
export default function AdminDashboard() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [beehiivStatus, setBeehiivStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Navigation structure - Restructured per user request (Issue #17)
  // - Inner Circle moved as tab under People
  // - Marketing, Product (with Defaults & Inspo), Features moved as tabs under Roadmap  
  // - Updates moved under Messages
  // Admin navigation - 5 tabs only (Iter 9.29 consolidation per user request)
  // Product is now an umbrella for: Category Master, Applications, Master Products,
  // Service Catalog, Defaults & Inspo, /me Template Editor.
  const navItems = [
    { path: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/users", icon: Users, label: "People" },
    { path: "/admin/product", icon: Map, label: "Product" },
    { path: "/admin/financials", icon: DollarSign, label: "Financials" },
    { path: "/admin/settings", icon: Settings, label: "Settings" },
  ];

  // Fetch Beehiiv status on mount
  useEffect(() => {
    const fetchBeehiivStatus = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/beehiiv/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBeehiivStatus(data);
        }
      } catch (err) {
        console.error("Failed to fetch Beehiiv status:", err);
      }
    };
    if (isAuthenticated && (
      user?.role === 'super_admin' || user?.role === 'admin' ||
      user?.is_super_admin || SUPER_ADMIN_EMAILS.includes(user?.email)
    )) {
      fetchBeehiivStatus();
    }
  }, [isAuthenticated, user]);

  const handleBeehiivSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/beehiiv/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ direction: "both" })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Synced: ${data.synced_to_beehiiv} to Beehiiv, ${data.synced_from_beehiiv} from Beehiiv`);
        // Refresh status
        const statusRes = await fetch(`${API}/admin/beehiiv/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (statusRes.ok) {
          setBeehiivStatus(await statusRes.json());
        }
      } else {
        toast.error("Sync failed");
      }
    } catch (err) {
      toast.error("Error syncing with Beehiiv");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && (
      user?.role === 'super_admin' || user?.role === 'admin' || 
      user?.is_super_admin || SUPER_ADMIN_EMAILS.includes(user?.email)
    )) {
      setAuthorized(true);
    }
    setLoading(false);
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access the admin panel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/">Return Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="admin-dashboard">
      {/* Unified Top Bar */}
      <UnifiedTopBar variant="admin" />
      
      {/* Main Content - no sidebar, tab-based like /me */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Horizontal Tab Navigation - matching /me style */}
          <div className="flex items-center justify-center mb-6 bg-gray-100 p-1 rounded-2xl" data-testid="admin-tab-bar">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                             location.pathname.startsWith(item.path + "/");
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                    isActive 
                      ? "bg-white shadow-sm font-medium" 
                      : "text-gray-600 hover:text-black"
                  }`}
                  data-testid={`admin-tab-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Route Content */}
          <Routes>
            <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardSection />} />
            <Route path="/super" element={<Navigate to="/admin/settings" replace />} />
            {/* Iter 9.39b - SuperAdminPage deleted. Legacy route redirects to Settings → Access Control. */}
            <Route path="/legacy-config" element={<Navigate to="/admin/settings" replace />} />
            <Route path="/users" element={<PeopleHub />} />
            <Route path="/people" element={<Navigate to="/admin/users" replace />} />
            <Route path="/inner-circle" element={<InnerCircleSection />} />
            <Route path="/portals/pilots/:category/:tab" element={<PilotsDashboard />} />
            <Route path="/portals/*" element={<Navigate to="/admin/product" replace />} />
            <Route path="/pilots/categories" element={<CategoryManagementSection />} />
            <Route path="/category-master" element={<AdminCategoryMasterPage />} />
            <Route path="/host-applications" element={<AdminHostApplicationsPage />} />
            {/* Iter 9.33 - restore old sidebar nav (PRODUCT + HOST DASHBOARDS) per user request */}
            <Route path="/product" element={<ProductSection />} />
            <Route path="/product/applications" element={<AdminApplicationsPage />} />
            <Route path="/product/category-master" element={<AdminCategoryMasterPage />} />
            <Route path="/product/master-products" element={<AdminMasterProductsPage />} />
            <Route path="/product/templates" element={<HostDashboardTemplateEditor />} />
            <Route path="/product/defaults" element={<DefaultsInspoAdminPage />} />
            <Route path="/product/service-catalog" element={<AdminServiceCatalogPage />} />
            <Route path="/product/brands" element={<AdminBrandsPage />} />
            {/* Iter 9.38 - master databases */}
            <Route path="/product/master-services" element={<AdminMasterServicesPage />} />
            <Route path="/product/master-occasions" element={<AdminMasterOccasionsPage />} />
            <Route path="/product/preference-share-templates" element={<AdminPreferenceShareTemplatesPage />} />
            <Route path="/product/master-experiences" element={<AdminMasterExperiencesPage />} />
            <Route path="/product/me-template" element={<AdminMeTemplatePage />} />
            <Route path="/product/help-faq" element={<AdminFaqPage />} />
            <Route path="/product/updates" element={<AdminUpdatesPage />} />
            <Route path="/product/messages" element={<AdminMessagesPage />} />
            <Route path="/help-faq" element={<AdminFaqPage />} />
            <Route path="/updates" element={<AdminUpdatesPage />} />
            <Route path="/messages" element={<AdminMessagesPage />} />
            <Route path="/docs" element={<AdminDocsIndexPage />} />
            <Route path="/pilots/:category/*" element={<PilotsDashboard />} />
            <Route path="/pilots" element={<Navigate to="/admin/product" replace />} />
            <Route path="/product/*" element={<ProductSection />} />
            <Route path="/messages/*" element={<MessagesSection />} />
            <Route path="/analytics" element={<AnalyticsSection />} />
            <Route path="/financials" element={<FinancialsSection />} />
            <Route path="/settings" element={<AdminSettingsHub />} />
            <Route path="/beehiiv" element={<Navigate to="/admin/users" replace />} />
            <Route path="/access/*" element={<Navigate to="/admin/super" replace />} />
            <Route path="/activity/*" element={<NDAIntakeActivitySection />} />
            <Route path="/integrations/*" element={<IntegrationsSection />} />
            <Route path="/progress/*" element={<ProgressJournalSection />} />
            <Route path="/roadmap" element={<RoadmapSection />} />
            <Route path="/marketing" element={<MarketingSection />} />
            <Route path="/updates" element={<Navigate to="/admin/product" replace />} />
          <Route path="/instructor-pilots/:profileId" element={<InstructorPilotsPage />} />
          <Route path="/instructor-pilots" element={<InstructorPilotsPage />} />
          <Route path="/templates" element={<TemplatesAdminPage />} />
          <Route path="/host-templates" element={<HostDashboardTemplateEditor />} />
          <Route path="/profile-editor" element={<MirroredProfileEditor />} />
          {/* Portal Templates Admin removed - now integrated into portal settings */}
          <Route path="/maintenance" element={<ProductMaintenanceHub />} />
          <Route path="/investor-portals" element={<InvestorPortalsPage />} />
          <Route path="/defaults" element={<DefaultsInspoAdminPage />} />
          <Route path="/dashboard-editor" element={<DashboardEditorPage />} />
          <Route path="/dashboard-editor/:pilotCategory" element={<DashboardEditorPage />} />
          <Route path="/hub/*" element={<AdminHubSection />} />
          <Route path="/control-panel" element={<ComprehensiveAdminPanel />} />
          <Route path="/copy-editor" element={<AdminCopyEditorPage />} />
          <Route path="/products" element={<AdminMasterProductsPage />} />
          <Route path="/master-products" element={<AdminMasterProductsPage />} />
          <Route path="/advocates" element={<AdminAdvocatesPage />} />
          <Route path="/launch-control" element={<LaunchControlPanel />} />
          <Route path="/pending-ugc" element={<AdminPendingUGCPage />} />
          <Route path="/feature-access" element={<AdminFeatureAccessMatrixPage />} />
          <Route path="/media-library" element={<AdminMediaLibraryPage />} />
          <Route path="/preference-visibility" element={<AdminPreferenceVisibilityPage />} />
          <Route path="/master-components" element={<AdminMasterComponentsPage />} />
          <Route path="/support" element={<SupportSection />} />
        </Routes>
        </div>
      </main>
    </div>
  );
}

// ============================================================================
// DASHBOARD SECTION (with Analytics sub-tab)
// ============================================================================
function DashboardSection() {
  const [activeView, setActiveView] = useState("overview");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [beehiivStatus, setBeehiivStatus] = useState(null);

  useEffect(() => {
    fetchStats();
    const fetchBeehiivStatus = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/beehiiv/status`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setBeehiivStatus(await res.json());
      } catch (err) { /* silent */ }
    };
    fetchBeehiivStatus();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Sub-tabs: Overview | Analytics */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="dashboard-sub-tabs">
          <button
            onClick={() => setActiveView("overview")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${activeView === "overview" ? "bg-white shadow-sm text-black" : "text-gray-500 hover:text-gray-700"}`}
            data-testid="dashboard-tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveView("analytics")}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${activeView === "analytics" ? "bg-white shadow-sm text-black" : "text-gray-500 hover:text-gray-700"}`}
            data-testid="dashboard-tab-analytics"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Analytics
          </button>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats} data-testid="dashboard-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {activeView === "overview" && (
        <>
          {/* Stats Grid - 3 cards only */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <StatCard
              title="Total Users"
              value={stats?.users?.total || 0}
              subtitle="All registered users"
              icon={Users}
              color="blue"
            />
            <StatCard
              title="Active Pilots"
              value={stats?.pilots?.active || stats?.pilots?.host_intakes || 0}
              subtitle={`${stats?.pilots?.host_intakes || 0} onboarded`}
              icon={Sparkles}
              color="purple"
            />
            <StatCard
              title="Total Events"
              value={stats?.events?.total || 0}
              subtitle={`${stats?.events?.total_rsvps || 0} RSVPs`}
              icon={Calendar}
              color="green"
            />
          </div>

          {/* Quick Actions - Recent Activity Panel */}
          <QuickActionsPanel />
        </>
      )}

      {activeView === "analytics" && (
        <AnalyticsSection />
      )}
    </div>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    purple: "bg-purple-50 text-purple-600",
    green: "bg-green-50 text-green-600",
    emerald: "bg-emerald-50 text-emerald-600",
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          </div>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickAction({ icon: Icon, label, href }) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
    >
      <Icon className="w-5 h-5 text-gray-600" />
      <span className="text-sm font-medium">{label}</span>
      <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
    </Link>
  );
}

// ============================================================================
// QUICK ACTIONS PANEL - Recent Activity Monitoring
// ============================================================================
function QuickActionsPanel() {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/dashboard/recent-activity`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setActivity(data);
        }
      } catch (err) {
        console.error("Error fetching activity:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {/* Pending Messages */}
      <Card className="border-l-4 border-l-red-500">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-red-500" />
              Pending Messages
            </CardTitle>
            <Badge variant="secondary" className="bg-red-100 text-red-700">
              {activity?.pending_messages?.count || 0}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-4 flex justify-center">
              <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
            </div>
          ) : activity?.pending_messages?.items?.length > 0 ? (
            <div className="space-y-2">
              {activity.pending_messages.items.slice(0, 3).map((msg) => (
                <div key={msg.message_id} className="p-2 bg-gray-50 rounded text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px]">
                      {msg.message_type}
                    </Badge>
                    <span className="text-gray-400">
                      {new Date(msg.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="line-clamp-1 text-gray-600">{msg.content}</p>
                </div>
              ))}
              <Link
                to="/admin/messages"
                className="block text-center text-xs text-purple-600 hover:underline mt-2"
              >
                View all messages →
              </Link>
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-4">No pending messages</p>
          )}
        </CardContent>
      </Card>

      {/* Updates */}
      <Card className="border-l-4 border-l-purple-500">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Send className="w-4 h-4 text-purple-500" />
              Updates
            </CardTitle>
            <Badge variant="secondary" className="bg-purple-100 text-purple-700">
              {activity?.draft_updates?.count || 0}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-4 flex justify-center">
              <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
            </div>
          ) : activity?.draft_updates?.items?.length > 0 ? (
            <div className="space-y-2">
              {activity.draft_updates.items.slice(0, 2).map((update) => (
                <div key={update.update_id} className="p-2 bg-gray-50 rounded text-xs">
                  <p className="font-medium line-clamp-1">{update.title}</p>
                  <p className="text-gray-400 text-[10px] mt-0.5">
                    {update.update_type} • {update.status || 'Published'}
                  </p>
                </div>
              ))}
              <Link
                to="/admin/product"
                className="block text-center text-xs text-purple-600 hover:underline mt-2"
              >
                View all updates →
              </Link>
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-4">No updates yet</p>
          )}
        </CardContent>
      </Card>

      {/* New Pilots This Week */}
      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-green-500" />
              New Pilots This Week
            </CardTitle>
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              {activity?.new_pilots_this_week?.count || 0}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-4 flex justify-center">
              <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
            </div>
          ) : activity?.new_pilots_this_week?.items?.length > 0 ? (
            <div className="space-y-2">
              {activity.new_pilots_this_week.items.slice(0, 3).map((pilot) => (
                <div key={pilot.member_id} className="p-2 bg-gray-50 rounded text-xs">
                  <p className="font-medium">{pilot.name}</p>
                  <p className="text-gray-400 text-[10px]">
                    {pilot.company_name} • {pilot.sub_category_name}
                  </p>
                </div>
              ))}
              <Link
                to="/admin/portals"
                className="block text-center text-xs text-purple-600 hover:underline mt-2"
              >
                View all pilots →
              </Link>
            </div>
          ) : (
            <p className="text-xs text-gray-500 text-center py-4">No new pilots this week</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// PRIVATE PORTALS SECTION
// ============================================================================
function PortalsSection() {
  const [activeTab, setActiveTab] = useState("pilots");
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [newInvite, setNewInvite] = useState({
    name: "",
    email: "",
    company: "",
    pilot_type: "beauty"
  });
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  
  // Fetch pilots on mount
  useEffect(() => {
    const fetchPilots = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/pilots`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPilots(data.portals || []);
        }
      } catch (err) {
        console.error("Failed to fetch pilots:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPilots();
  }, []);
  
  // Create pilot invite
  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/pilots/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newInvite)
      });
      
      if (res.ok) {
        const data = await res.json();
        setInviteUrl(data.invite?.invite_url || "");
        toast.success("Invite created!");
        setNewInvite({ name: "", email: "", company: "", pilot_type: "beauty" });
      }
    } catch (err) {
      toast.error("Failed to create invite");
    } finally {
      setCreatingInvite(false);
    }
  };
  
  const copyInviteUrl = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Invite URL copied!");
  };
  
  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">Private Portals</h1>
          <p className="text-gray-500">Pilots, Investors, Team, Partners</p>
        </div>
        <Button onClick={() => setShowInviteDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          New Invite
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pilots">Pilots ({pilots.length})</TabsTrigger>
          <TabsTrigger value="instructor">Instructor</TabsTrigger>
          <TabsTrigger value="investors">Investors</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="partners">Partners</TabsTrigger>
        </TabsList>

        <TabsContent value="pilots" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Pilot Partners</CardTitle>
              <CardDescription>MUA, Hair, Nail, Events, Wellness pilots</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : pilots.length === 0 ? (
                <div className="text-center py-8">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No pilots yet</p>
                  <p className="text-sm text-gray-500 mt-1">Create an invite to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pilots.map(pilot => (
                    <div key={pilot.portal_id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{pilot.user_name || pilot.business_name || "Unknown"}</p>
                        <p className="text-sm text-gray-500">{pilot.user_email || "No email"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={pilot.status === "active" ? "default" : "secondary"}>
                          {pilot.status || "pending"}
                        </Badge>
                        <Badge variant="outline">{pilot.pilot_type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instructor" className="mt-6">
          <PortalPlaceholder 
            icon={Users}
            title="Instructor Portals"
            description="Data Dreaming and other instructor partners"
            features={["Workshop management", "Content access", "Student tracking", "Revenue share"]}
          />
        </TabsContent>

        <TabsContent value="investors" className="mt-6">
          <InvestorPortalManager API={API} getAuthHeaders={() => {
            const token = localStorage.getItem('session_token');
            return token ? { Authorization: `Bearer ${token}` } : {};
          }} />
        </TabsContent>

        <TabsContent value="team" className="mt-6">
          <PortalPlaceholder 
            icon={Users}
            title="Team Portals"
            description="Internal team members and contractors"
            features={["Onboarding NDA", "Wave contractor setup", "Access permissions", "Task assignments"]}
          />
        </TabsContent>

        <TabsContent value="partners" className="mt-6">
          <PortalPlaceholder 
            icon={Link2}
            title="Partner Portals"
            description="Integration and strategic partners"
            features={["API documentation", "Integration status", "Update notifications", "Support chat"]}
          />
        </TabsContent>
      </Tabs>
      
      {/* Create Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Pilot Invite</DialogTitle>
            <DialogDescription>Generate a new invite link for a pilot partner</DialogDescription>
          </DialogHeader>
          
          {inviteUrl ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium mb-2">Invite created!</p>
                <div className="flex items-center gap-2">
                  <Input value={inviteUrl} readOnly className="flex-1 text-sm" />
                  <Button onClick={copyInviteUrl} size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button onClick={() => { setInviteUrl(""); setShowInviteDialog(false); }} className="w-full">
                Done
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newInvite.name}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Partner name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newInvite.email}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="partner@example.com"
                  />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input
                    value={newInvite.company}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <Label>Pilot Type</Label>
                  <select
                    value={newInvite.pilot_type}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, pilot_type: e.target.value }))}
                    className="w-full h-10 px-3 border rounded-md"
                  >
                    <option value="beauty">Beauty</option>
                    <option value="event">Event</option>
                    <option value="instructor">Instructor</option>
                    <option value="wellness">Wellness</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateInvite} disabled={creatingInvite}>
                  {creatingInvite ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Create Invite
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Investor Portal Manager - CRUD for investor portal access
function InvestorPortalManager({ API, getAuthHeaders }) {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newInvestor, setNewInvestor] = useState({ name: '', email: '', type: 'angel', access_level: 'standard' });

  useEffect(() => { loadInvestors(); }, []);

  const loadInvestors = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/investor-portals`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setInvestors(Array.isArray(data) ? data : (data.portals || []));
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const addInvestor = async () => {
    if (!newInvestor.name || !newInvestor.email) return toast.error('Name and email required');
    try {
      const res = await fetch(`${API}/admin/investor-portals`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newInvestor)
      });
      if (res.ok) {
        toast.success('Investor portal created');
        setShowAdd(false);
        setNewInvestor({ name: '', email: '', type: 'angel', access_level: 'standard' });
        loadInvestors();
      } else {
        const d = await res.json();
        toast.error(d.detail || 'Failed to create');
      }
    } catch (err) { toast.error('Failed to create investor portal'); }
  };

  const removeInvestor = async (portalId) => {
    try {
      const res = await fetch(`${API}/admin/investor-portals/${portalId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (res.ok) { toast.success('Removed'); loadInvestors(); }
    } catch (err) { toast.error('Failed to remove'); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Investor Portals</CardTitle>
            <CardDescription>Manage investor access to business plans, financials, and documents</CardDescription>
          </div>
          <Button onClick={() => setShowAdd(true)} className="gap-2" data-testid="add-investor-btn">
            <Plus className="h-4 w-4" /> Add Investor
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : investors.length === 0 ? (
          <div className="text-center py-8">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium">No investors yet</p>
            <p className="text-sm text-gray-500 mt-1">Add an investor to create their private portal</p>
          </div>
        ) : (
          <div className="space-y-3">
            {investors.map(inv => (
              <div key={inv.portal_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                    {(inv.name || 'I')[0]}
                  </div>
                  <div>
                    <p className="font-medium">{inv.name}</p>
                    <p className="text-sm text-gray-500">{inv.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{inv.type || 'angel'}</Badge>
                  <Badge variant={inv.status === 'active' ? 'default' : 'secondary'}>{inv.status || 'active'}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const url = `${window.location.origin}/investor/portal/${inv.portal_id}`;
                      navigator.clipboard.writeText(url);
                      toast.success('Portal link copied');
                    }}
                    data-testid={`copy-investor-link-${inv.portal_id}`}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => removeInvestor(inv.portal_id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAdd && (
          <div className="mt-6 p-4 border rounded-lg bg-gray-50">
            <h3 className="font-medium mb-3">Add New Investor</h3>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Name" value={newInvestor.name} onChange={e => setNewInvestor({...newInvestor, name: e.target.value})} data-testid="investor-name-input" />
              <Input placeholder="Email" value={newInvestor.email} onChange={e => setNewInvestor({...newInvestor, email: e.target.value})} data-testid="investor-email-input" />
              <Select value={newInvestor.type} onValueChange={v => setNewInvestor({...newInvestor, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="angel">Angel Investor</SelectItem>
                  <SelectItem value="vc">VC Fund</SelectItem>
                  <SelectItem value="strategic">Strategic Partner</SelectItem>
                  <SelectItem value="advisor">Advisor</SelectItem>
                </SelectContent>
              </Select>
              <Select value={newInvestor.access_level} onValueChange={v => setNewInvestor({...newInvestor, access_level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="full">Full Access</SelectItem>
                  <SelectItem value="limited">Limited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={addInvestor} data-testid="save-investor-btn">Create Portal</Button>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PortalPlaceholder({ icon: Icon, title, description, features }) {
  return (
    <Card>
      <CardContent className="py-12">
        <div className="text-center mb-6">
          <Icon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="font-medium text-lg">{title}</p>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
        <div className="max-w-md mx-auto">
          <p className="text-xs text-gray-400 uppercase font-medium mb-2">Planned Features</p>
          <ul className="space-y-2">
            {features.map((feature, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-gray-300" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// PRODUCT SECTION - Side Navigation Layout
// ============================================================================
// Import centralized category config
import { getAllCategories, getCategoryPreset, CATEGORY_ORDER } from "../../lib/categoryConfig";
const PILOT_CATEGORIES = getAllCategories();


// Inline Pilot Portal Manager - shows portal members, intake link, and stats for a category
function InlinePilotPortalManager({ catKey, catName, API }) {
  const [portals, setPortals] = useState([]);
  const [activeHosts, setActiveHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIntakePreview, setShowIntakePreview] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = { Authorization: `Bearer ${token}` };
        const singularMap = { nails: "nail", lashes: "lash", events: "event", instructors: "instructor" };
        const searchKey = singularMap[catKey] || catKey;

        // Fetch pilot portals
        const [portalRes, eventsRes] = await Promise.all([
          fetch(`${API}/admin/portals`, { headers }),
          fetch(`${API}/admin/category/${catKey}/events`, { headers }),
        ]);

        if (portalRes.ok) {
          const data = await portalRes.json();
          const pilots = (data.pilots || []).filter(p => 
            p.category === catKey || p.category === searchKey || 
            p.service_type === catKey || p.service_type === searchKey
          );
          setPortals(pilots);
        }

        // Fetch active events/hosts for this category
        if (eventsRes.ok) {
          const eData = await eventsRes.json();
          setActiveHosts(eData.events || []);
        }
      } catch {}
      setLoading(false);
    };
    fetchData();
  }, [catKey, API]);

  if (loading) return <div className="py-2 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>;

  const activePortals = portals.filter(p => p.magic_link_claimed || p.intake_complete);
  const pendingPortals = portals.filter(p => !p.magic_link_claimed && !p.intake_complete);

  return (
    <div className="space-y-4" data-testid={`pilot-portal-inline-${catKey}`}>
      {/* Active Host Dashboards */}
      {activeHosts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <p className="text-xs font-semibold text-gray-700">Active Dashboards ({activeHosts.length})</p>
          </div>
          <div className="space-y-2">
            {activeHosts.map((host) => (
              <div key={host.event_id} className="flex items-center justify-between p-2.5 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{host.title || host.name}</p>
                    <p className="text-[10px] text-gray-400">
                      Host: {host.host_name || 'Unassigned'} · {host.rsvp_count || 0} clients
                    </p>
                  </div>
                </div>
                <a href={`/host/v2/${host.event_id}`} target="_blank" rel="noreferrer"
                  className="px-2 py-1 text-[10px] border rounded hover:bg-gray-100 text-blue-600"
                  data-testid={`enter-host-dash-${host.event_id}`}>
                  Enter Dashboard
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Formal Pilot Portals (Invites) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-700">
            Pilot Invites ({portals.length}) {activePortals.length > 0 && <span className="text-green-600">· {activePortals.length} active</span>} {pendingPortals.length > 0 && <span className="text-amber-600">· {pendingPortals.length} pending</span>}
          </p>
        </div>
      
        {portals.length > 0 ? (
          <div className="space-y-2">
            {portals.slice(0, 5).map((portal) => (
              <div key={portal.portal_id} className="flex items-center justify-between p-2.5 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={`w-2 h-2 rounded-full ${portal.intake_complete ? 'bg-green-500' : portal.magic_link_claimed ? 'bg-yellow-500' : 'bg-gray-300'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{portal.name}</p>
                    <p className="text-[10px] text-gray-400">
                      {portal.intake_complete ? 'Intake complete' : portal.magic_link_claimed ? 'Link claimed' : 'Invite pending'}
                      {portal.city && ` · ${portal.city}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {portal.magic_link_token && (
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/pilot/intake/${portal.magic_link_token}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Intake link copied!");
                      }}
                      className="px-2 py-1 text-[10px] border rounded hover:bg-gray-100"
                      data-testid={`copy-intake-link-${portal.portal_id}`}
                    >
                      Copy Intake Link
                    </button>
                  )}
                  {portal.magic_link_token && (
                    <button
                      onClick={() => window.open(`/pilot/intake/${portal.magic_link_token}?preview=true`, '_blank')}
                      className="px-2 py-1 text-[10px] border rounded hover:bg-gray-100"
                      data-testid={`preview-intake-${portal.portal_id}`}
                    >
                      Preview
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : activeHosts.length === 0 ? (
          <p className="text-xs text-gray-400 py-2">No pilot portals or dashboards for {catName} yet</p>
        ) : null}
      </div>
    </div>
  );
}


// Category Dashboard - Linked Orgs with nested Members
// Iter 9.34: HOST DASHBOARDS sidebar redirects to per-category template editor.
// Doing the redirect inside useEffect avoids the 'Cannot update component while rendering' warning.
function CategoryRedirect({ catKey, navigate }) {
  useEffect(() => {
    const target = `/admin/product/templates?cat=${catKey}`;
    const current = window.location.pathname + window.location.search;
    if (current !== target) navigate(target);
  }, [catKey, navigate]);
  return (
    <div className="p-8 text-center text-sm text-gray-500" data-testid={`category-redirect-${catKey}`}>
      Loading {catKey} template editor…
    </div>
  );
}

function CategoryLinkedOrgs({ catKey, catName, API }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState(null);

  useEffect(() => {
    const fetchOrgsWithMembers = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/category/${catKey}/linked-orgs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOrgs(data.orgs || []);
        }
      } catch {}
      setLoading(false);
    };
    fetchOrgsWithMembers();
  }, [catKey, API]);

  return (
    <Card data-testid={`linked-orgs-${catKey}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-500" />
            Linked Organizations & Members
          </CardTitle>
          <Badge variant="outline" className="text-xs">{orgs.length} orgs</Badge>
        </div>
        <p className="text-xs text-gray-500">Organizations linked to {catName} with their members</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
        ) : orgs.length > 0 ? (
          <div className="space-y-2">
            {orgs.map((org, i) => (
              <div key={i} className="border rounded-lg overflow-hidden">
                {/* Org Header - clickable to expand */}
                <button
                  onClick={() => setExpandedOrg(expandedOrg === org.org_id ? null : org.org_id)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left"
                  data-testid={`org-row-${org.org_id}`}
                >
                  <div className="flex items-center gap-2.5">
                    {org.logo_url ? (
                      <img src={org.logo_url} alt="" className="w-7 h-7 rounded-lg object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600">{(org.name || "?")[0]}</div>
                    )}
                    <div>
                      <p className="text-sm font-medium">{org.name}</p>
                      <p className="text-[10px] text-gray-400">{org.member_count || 0} members</p>
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedOrg === org.org_id ? "rotate-180" : ""}`} />
                </button>
                {/* Expanded Members List */}
                {expandedOrg === org.org_id && (
                  <div className="border-t bg-gray-50 p-2 space-y-1.5">
                    {(org.members || []).length > 0 ? (
                      org.members.map((member, j) => (
                        <div key={j} className="flex items-center justify-between px-2 py-1.5 bg-white rounded-md text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar className="h-5 w-5">
                              <AvatarImage src={member.picture || member.profile_image} />
                              <AvatarFallback className="bg-purple-100 text-purple-600 text-[8px]">
                                {(member.name || "?")[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate">{member.name || member.email}</span>
                          </div>
                          <Badge variant="outline" className="text-[10px] capitalize shrink-0">{member.role || "member"}</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-gray-400 text-center py-1">No members in this org</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 py-2">No organizations linked to {catName} yet</p>
        )}
      </CardContent>
    </Card>
  );
}

function CategoryLinkedEvents({ catKey, catName, API }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_events = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/category/${catKey}/events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEvents(data.events || []);
        }
      } catch {}
      setLoading(false);
    };
    fetch_events();
  }, [catKey, API]);

  return (
    <Card data-testid={`linked-events-${catKey}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4 text-green-500" />
            Linked Events & Experiences
          </CardTitle>
          <Badge variant="outline" className="text-xs">{events.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
        ) : events.length > 0 ? (
          <div className="space-y-2">
            {events.slice(0, 8).map((evt, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="font-medium truncate">{evt.title || evt.name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-gray-400">{evt.rsvp_count || 0} RSVPs</span>
                  <a href={`/host/v2/${evt.event_id}`} className="text-blue-600 hover:underline">View</a>
                </div>
              </div>
            ))}
            {events.length > 8 && <p className="text-xs text-gray-400">+{events.length - 8} more</p>}
          </div>
        ) : (
          <p className="text-xs text-gray-400 py-2">No events linked to {catName}</p>
        )}
      </CardContent>
    </Card>
  );
}

function CategoryMembers({ catKey, catName, API }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_members = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/category/${catKey}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMembers(data.members || []);
        }
      } catch {}
      setLoading(false);
    };
    fetch_members();
  }, [catKey, API]);

  return (
    <Card data-testid={`category-members-${catKey}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Users className="w-4 h-4 text-orange-500" />
            Members
          </CardTitle>
          <Badge variant="outline" className="text-xs">{members.length}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" /></div>
        ) : members.length > 0 ? (
          <div className="space-y-2">
            {members.slice(0, 10).map((member, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={member.picture || member.profile_image} />
                    <AvatarFallback className="bg-purple-100 text-purple-600 text-[10px]">
                      {(member.name || "?")[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <span className="font-medium truncate block">{member.name || member.email}</span>
                    {member.company && <span className="text-gray-400 truncate block">{member.company}</span>}
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px] capitalize shrink-0">{member.role || "member"}</Badge>
              </div>
            ))}
            {members.length > 10 && <p className="text-xs text-gray-400">+{members.length - 10} more</p>}
          </div>
        ) : (
          <p className="text-xs text-gray-400 py-2">No members in {catName}</p>
        )}
      </CardContent>
    </Card>
  );
}


function ProductSection() {
  const [activeSection, setActiveSection] = useState(() => {
    // Iter 9.40 Pass F - read ?section=… from URL so deep-links land in the right tab
    try {
      const params = new URLSearchParams(window.location.search);
      const s = params.get("section");
      if (s) return s;
    } catch { /* SSR-safe */ }
    return "launch-control";
  });
  // Iter 9.40 Pass D § 23 - collapsible nav groups (default all open)
  const [expandedNavGroups, setExpandedNavGroups] = useState({
    main: true, templates: true, masters: true, programs: true, ops: true,
  });
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const [pilotStats, setPilotStats] = useState({});
  // Iter 9.40 Slice C⁶ - live category branding (icon_url + color_light) from
  // db.categories so the HOST DASHBOARDS sidebar mirrors Master Components → Categories.
  const [liveCats, setLiveCats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/admin/portals/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setPilotStats(await res.json());
      } catch {}
    };
    fetchStats();
    // Live category branding sync (Master Components is the SSOT)
    const fetchLiveCats = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/admin/categories`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data.categories || []);
          const byKey = {};
          list.forEach(c => {
            const key = (c.category_id || "").replace(/^cat_/, "") || c.slug || c.key;
            if (key) byKey[key] = {
              icon_url: c.icon_url || c.branding?.icon_url,
              color_light: c.light_hex || c.color_light || c.branding?.color_light || c.primary_color,
              color_dark: c.dark_hex || c.color_dark || c.branding?.color_dark || c.accent_color,
              label: c.label || c.name,
            };
          });
          setLiveCats(byKey);
        }
      } catch {}
    };
    fetchLiveCats();
  }, []);

  const NAV_ITEMS = [
    { id: "launch-control", label: "Launch Control", icon: Rocket, group: "main" },
    // Templates: editors that compose the user-facing experience
    { id: "metemplate", label: "/me Template", icon: FileText, group: "templates" },
    // Master DBs (Master Components at the top - absorbs Master Categories design tokens, media library, design system)
    { id: "master-components", label: "Master Components", icon: Package, group: "masters" },
    { id: "inspo", label: "Master Inspo", icon: Image, group: "masters" },
    { id: "master-experiences", label: "Master Experiences", icon: Sparkles, group: "masters" },
    { id: "defaults", label: "Master Preferences", icon: Settings, group: "masters" },
    { id: "master-products", label: "Master Products", icon: Package, group: "masters" },
    { id: "master-services", label: "Master Services", icon: Briefcase, group: "masters" },
    { id: "master-occasions", label: "Master Occasions", icon: CalendarHeart, group: "masters" },
    { id: "preference-share-templates", label: "Share pages", icon: ArrowUp, group: "templates" },
    { id: "master-intakes", label: "Master Intakes", icon: FileText, group: "masters" },
    { id: "master-features", label: "Master Features", icon: Layers, group: "masters" },
    { id: "pending-ugc", label: "Pending UGC", icon: Layers, group: "masters" },
    // Programs
    { id: "brand-pilots", label: "Brand Pilots", icon: Star, group: "programs" },
    { id: "insights-atlas", label: "Insights", icon: BarChart3, group: "programs" },
    { id: "portals", label: "Portals", icon: HeartHandshake, group: "programs" },
    // Ops
    { id: "database", label: "Database Browser", icon: Database, group: "ops" },
    { id: "messages", label: "Messages", icon: MessageSquare, group: "ops" },
    { id: "core-files", label: "Core Files", icon: BookOpen, group: "ops" },
    { id: "integrations", label: "Integrations", icon: Plug, group: "ops" },
  ];

  // Iter 9.40 Pass D § 23 - collapsible nav groups (admin sidebar nesting).
  const NAV_GROUPS = [
    { id: "main",      label: "Overview" },
    { id: "templates", label: "Templates" },
    { id: "masters",   label: "Master DBs" },
    { id: "programs",  label: "Programs & Insights" },
    { id: "ops",       label: "Ops & Tools" },
  ];

  // Iter 9.40 Slice C⁷ - collapsible Product side-nav so the editor area can
  // claim full width when desired.
  const [navCollapsed, setNavCollapsed] = useState(false);

  return (
    <div className="flex h-[calc(100vh-140px)]" data-testid="product-section">
      {/* Side Navigation */}
      <div
        className={`${navCollapsed ? "w-10" : "w-56"} border-r bg-gray-50/50 overflow-y-auto shrink-0 transition-all duration-200`}
        data-testid="product-sidenav"
      >
        <div className={navCollapsed ? "p-1" : "p-4"}>
          <div className="flex items-center justify-between mb-3">
            {!navCollapsed && <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Product</h2>}
            <button
              type="button"
              onClick={() => setNavCollapsed(v => !v)}
              className="p-1 rounded hover:bg-gray-200 text-gray-500 ml-auto"
              title={navCollapsed ? "Expand" : "Collapse"}
              data-testid="product-nav-collapse"
            >
              {navCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
            </button>
          </div>
          {!navCollapsed && (
          <nav className="space-y-3" data-testid="product-nav">
            {NAV_GROUPS.map((g) => {
              const items = NAV_ITEMS.filter((it) => it.group === g.id);
              if (items.length === 0) return null;
              const isOpen = expandedNavGroups[g.id] !== false; // default open
              const isTemplates = g.id === "templates";
              return (
                <div key={g.id} data-testid={`nav-group-${g.id}`}>
                  <button
                    onClick={() => setExpandedNavGroups((p) => ({ ...p, [g.id]: !isOpen }))}
                    className="w-full flex items-center justify-between text-[10px] font-bold text-gray-500 uppercase tracking-wider px-2 mb-1"
                    data-testid={`nav-group-toggle-${g.id}`}
                  >
                    <span>{g.label}</span>
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                  {isOpen && (
                    <div className="space-y-0.5">
                      {items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                              activeSection === item.id ? "bg-white shadow-sm font-medium text-black" : "text-gray-600 hover:bg-white/60"
                            }`}
                            data-testid={`product-nav-${item.id}`}
                          >
                            <Icon className="w-4 h-4 shrink-0" />
                            {item.label}
                          </button>
                        );
                      })}
                      {isTemplates && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <button
                            onClick={() => setExpandedNavGroups((p) => ({ ...p, host_dashboards_sub: !(p.host_dashboards_sub !== false) }))}
                            className="w-full flex items-center justify-between text-[10px] font-bold text-gray-500 uppercase tracking-wider px-2 mb-1"
                            data-testid="nav-group-toggle-host-dashboards"
                          >
                            <span>Host Dashboards</span>
                            {expandedNavGroups.host_dashboards_sub !== false ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          </button>
                          {expandedNavGroups.host_dashboards_sub !== false && PILOT_CATEGORIES.map(cat => {
                            const Icon = cat.icon;
                            // Iter 9.40 Slice C⁶ - Master Components is the SSOT for category branding.
                            // Fall back to centralized config only when live db.categories has nothing yet.
                            const live = liveCats[cat.key] || {};
                            const bgColor = live.color_light || cat.bgColor;
                            const iconUrl = live.icon_url;
                            return (
                              <button
                                key={cat.key}
                                onClick={() => setActiveSection(`pilot-${cat.key}`)}
                                className={`w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm transition-colors text-left ${
                                  activeSection === `pilot-${cat.key}` ? "bg-white shadow-sm font-medium text-black" : "text-gray-600 hover:bg-white/60"
                                }`}
                                data-testid={`pilot-nav-${cat.key}`}
                              >
                                <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 overflow-hidden" style={{ backgroundColor: bgColor }}>
                                  {iconUrl ? (
                                    <img
                                      src={iconUrl}
                                      alt={cat.name}
                                      className="w-3 h-3 object-contain"
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        const fb = e.target.parentElement.querySelector("[data-fb]");
                                        if (fb) fb.style.display = "flex";
                                      }}
                                    />
                                  ) : null}
                                  <Icon
                                    data-fb
                                    className={`w-3 h-3 ${iconUrl ? "hidden" : ""}`}
                                    style={{ color: cat.iconColor, display: iconUrl ? "none" : "flex" }}
                                  />
                                </div>
                                <span className="flex-1">{live.label || cat.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeSection === "launch-control" && (
          <AdminLaunchControlPage />
        )}

        {activeSection === "defaults" && (
          <div>
            <h1 className="text-xl font-bold mb-1">Master Preferences</h1>
            <p className="text-sm text-gray-500 mb-4">Canonical preference schema - pulled live from <code className="text-xs bg-stone-100 px-1 rounded">db.categories.preference_fields</code>. Toggle visibility, add options with image uploads, and sync defaults across the platform.</p>
            <AdminPreferenceVisibilityPage />
          </div>
        )}

        {activeSection === "inspo" && (
          <div data-testid="master-inspo-section">
            <AdminMasterInspoPage embedded={true} />
          </div>
        )}

        {activeSection === "messages" && (
          <MessagesSection />
        )}

        {activeSection === "metemplate" && (
          <div data-testid="me-template-section">
            <AdminMeTemplatePage embedded={true} />
          </div>
        )}

        {activeSection === "category-master" && (
          <div data-testid="category-master-section">
            <AdminCategoryMasterPage />
          </div>
        )}

        {activeSection === "core-files" && (
          <CoreFilesSection />
        )}

        {activeSection === "integrations" && (
          <ProductIntegrationsSection />
        )}

        {activeSection === "pending-ugc" && (
          <AdminPendingUGCPage />
        )}

        {activeSection === "feature-access" && (
          <AdminFeatureAccessMatrixPage />
        )}

        {activeSection === "media-library" && (
          <AdminMediaLibraryPage />
        )}

        {activeSection === "preference-visibility" && (
          <AdminPreferenceVisibilityPage />
        )}

        {activeSection === "master-components" && (
          <AdminMasterComponentsPage />
        )}

        {activeSection === "experiences" && (
          <AdminExperiencePresetsSection />
        )}

        {activeSection === "master-products" && (
          <AdminMasterProductsPage />
        )}

        {/* Iter 9.38 - Master databases */}
        {activeSection === "master-services" && (
          <AdminMasterServicesPage />
        )}

        {activeSection === "master-occasions" && (
          <AdminMasterOccasionsPage />
        )}

        {activeSection === "preference-share-templates" && (
          <AdminPreferenceShareTemplatesPage />
        )}

        {activeSection === "master-experiences" && (
          <AdminMasterExperiencesPage />
        )}

        {/* Iter 9.39b - Master Intakes (single source for all intake questions) */}
        {activeSection === "master-intakes" && (
          <AdminMasterIntakesPage />
        )}

        {/* Iter 9.39e - Master Features kanban */}
        {activeSection === "master-features" && (
          <AdminMasterFeaturesPage />
        )}

        {/* Iter 9.40 Pass D - Brand Pilot Program */}
        {activeSection === "brand-pilots" && (
          <BrandPilotsAdminPage />
        )}

        {/* Iter 9.40 Pass E - Insights Atlas */}
        {activeSection === "insights-atlas" && (
          <InsightsAtlasPage />
        )}

        {/* Iter 9.40 Pass H - Roadmap timeline */}
        {activeSection === "roadmap" && (
          <AdminRoadmapPage />
        )}

        {/* Iter 9.39a - Master Brands */}
        {activeSection === "master-brands" && (
          <AdminBrandsPage />
        )}

        {/* Iter 9.39a - Portals (Advocate / Pilot / Subscribers / Investors / Commissions) */}
        {activeSection === "portals" && (
          <PortalsHub />
        )}

        {/* Iter 9.40 Pass F - Userflows catalog with screenshots + inline copy editing */}
        {activeSection === "userflows" && (
          <UserflowsCatalogPage />
        )}

        {activeSection === "database" && (
          <AdminDatabaseBrowserSection />
        )}

        {activeSection === "analytics" && (
          <AdminCategoryAnalytics />
        )}

        {/* Iter 9.36 - clicking a category in HOST DASHBOARDS renders the per-category template editor INLINE (sidebar persists) */}
        {activeSection.startsWith("pilot-") && (() => {
          const catKey = activeSection.replace("pilot-", "");
          return <HostDashboardTemplateEditor categoryProp={catKey} embedded={true} />;
        })()}
        {false && activeSection.startsWith("pilot-LEGACY") && (() => {
          const catKey = activeSection.replace("pilot-", "");
          const cat = PILOT_CATEGORIES.find(c => c.key === catKey);
          if (!cat) return null;
          const Icon = cat.icon;
          return (
            <div className="space-y-5" data-testid={`category-dashboard-${catKey}`}>
              {/* Category Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: cat.bgColor }}>
                    <Icon className="w-5 h-5" style={{ color: cat.iconColor }} />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold">{cat.name} Dashboard</h1>
                    <p className="text-sm text-gray-500">Category management, linked orgs, events, and members</p>
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{pilotStats[catKey] || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Hosts</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{pilotStats[`${catKey}_pending`] || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Pending</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{pilotStats[`${catKey}_intake`] || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Intakes</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold">{pilotStats[`${catKey}_events`] || 0}</p>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Events</p>
                  </CardContent>
                </Card>
              </div>

              {/* Linked Orgs Section */}
              <CategoryLinkedOrgs catKey={catKey} catName={cat.name} API={API} />

              {/* Linked Events/Experiences Section */}
              <CategoryLinkedEvents catKey={catKey} catName={cat.name} API={API} />

              {/* Pilot Portal Section */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-500" />
                      Pilot Portal
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <InlinePilotPortalManager catKey={catKey} catName={cat.name} API={API} />
                </CardContent>
              </Card>

              {/* Host Dashboard Settings - existing category template editor */}
              <CategoryTemplateEditor embedded={true} initialCategory={catKey} />
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// Admin Events Tracker - shows all events across the platform
function AdminEventsTracker() {
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/admin/events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setEvents(Array.isArray(data) ? data : (data.events || []));
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchEvents();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>All Events</CardTitle>
        <CardDescription>Track all events across the platform</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : events.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No events found</p>
        ) : (
          <div className="space-y-3">
            {events.map(evt => (
              <div key={evt.event_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center overflow-hidden">
                    {evt.cover_image ? (
                      <img src={evt.cover_image?.startsWith('/') ? `${API.replace('/api','')}${evt.cover_image}` : evt.cover_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Calendar className="w-5 h-5 text-purple-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{evt.name || 'Unnamed Event'}</p>
                    <p className="text-xs text-gray-500">{evt.date || 'No date'} - {evt.location || evt.city || 'No location'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{evt.org_name || evt.category || 'unknown'}</Badge>
                  <Badge variant={evt.status === 'active' ? 'default' : 'secondary'}>{evt.status || 'draft'}</Badge>
                  <span className="text-xs text-gray-400">{evt.rsvp_count || 0} RSVPs</span>
                  <a href={`/host/v2/${evt.event_id}`} className="text-xs text-purple-600 hover:underline ml-2">
                    Manage
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductPlaceholder({ icon: Icon, title, description, details }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Icon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p className="font-medium text-lg">{title}</p>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
        <p className="text-xs text-gray-400 mt-4 max-w-md mx-auto">{details}</p>
      </CardContent>
    </Card>
  );
}

// /me Template Editor - Manages editable text content on /me page + Inspo subfolder defaults
function MeTemplateEditor({ inspoOnly = false }) {
  const [meTemplateState, setMeTemplateState] = useState({});
  const [inspoDefaults, setInspoDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDefaultFolder, setNewDefaultFolder] = useState("");
  const [selectedDefaultCat, setSelectedDefaultCat] = useState("");
  
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const templateFields = [
    { key: "hosting_cta_title", label: "Hosting CTA Title", placeholder: "Host Your Own Experience" },
    { key: "hosting_cta_description", label: "Hosting CTA Description", placeholder: "Create and manage your events..." },
    { key: "hosting_cta_button", label: "Hosting CTA Button", placeholder: "View My Events" },
    { key: "dashboard_title", label: "Dashboard Tab", placeholder: "Dashboard" },
    { key: "defaults_title", label: "Defaults Tab", placeholder: "Defaults" },
    { key: "inspo_title", label: "Inspo Tab", placeholder: "Inspiration" },
    { key: "experience_title", label: "Experience Tab", placeholder: "Experiences" },
    { key: "settings_title", label: "Settings Tab", placeholder: "Settings" },
    { key: "upcoming_title", label: "Upcoming Section", placeholder: "Upcoming" },
    { key: "hosting_title", label: "Hosting Section", placeholder: "Hosting" },
    { key: "preferences_title", label: "Preferences Section", placeholder: "Preference Card" },
  ];

  // Experiences template configuration
  const experienceToggles = [
    { key: "show_upcoming_events", label: "Show upcoming events", default: true },
    { key: "show_past_events", label: "Show past events", default: true },
    { key: "show_hosting_events", label: "Show hosting/created events", default: true },
    { key: "show_shared_events", label: "Show shared events from orgs", default: true },
    { key: "show_rsvp_history", label: "Show RSVP history", default: true },
    { key: "show_preference_card", label: "Show preference card on events", default: true },
    { key: "show_planning_presets", label: "Show planning presets for new experiences", default: false },
    { key: "show_linked_orgs", label: "Show linked organizations section", default: true },
    { key: "show_linked_portals", label: "Show linked portals section", default: true },
    { key: "show_booking_calendar", label: "Show booking calendar (for hosts)", default: false },
    { key: "enable_event_creation", label: "Allow users to create events", default: true },
    { key: "enable_quick_share", label: "Quick share preference card", default: true },
  ];
  
  const inspoCategories = [
    { id: "hair", name: "Hair", color: "#f59e0b" },
    { id: "makeup", name: "Makeup", color: "#ec4899" },
    { id: "nails", name: "Nails", color: "#8b5cf6" },
    { id: "lashes", name: "Lashes", color: "#06b6d4" },
    { id: "wellness", name: "Wellness", color: "#10b981" },
    { id: "culinary", name: "Culinary", color: "#f97316" },
    { id: "travel", name: "Travel", color: "#3b82f6" },
    { id: "style", name: "Retail", color: "#ef4444" },
    { id: "instructor", name: "Instructor", color: "#6366f1" },
    { id: "event", name: "Event", color: "#d946ef" },
    { id: "dispensary", name: "Dispensary", color: "#84cc16" },
  ];
  
  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (!token) return;
    Promise.all([
      fetch(`${API}/admin/me-template`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { sections: {} }),
      fetch(`${API}/admin/inspo-subfolder-defaults`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : { folders: {} }),
    ]).then(([tpl, inspo]) => {
      setMeTemplateState(tpl.sections || {});
      setInspoDefaults(inspo.folders || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [API]);
  
  const saveTemplate = async () => {
    setSaving(true);
    const token = localStorage.getItem("session_token");
    try {
      await fetch(`${API}/admin/me-template`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ sections: meTemplateState })
      });
      toast.success("Template saved!");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };
  
  const saveInspoDefaults = async () => {
    const token = localStorage.getItem("session_token");
    try {
      await fetch(`${API}/admin/inspo-subfolder-defaults`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ folders: inspoDefaults })
      });
      toast.success("Inspo defaults saved!");
    } catch { toast.error("Failed to save"); }
  };
  
  const addDefaultFolder = (cat) => {
    if (!newDefaultFolder.trim() || !cat) return;
    const updated = { ...inspoDefaults };
    if (!updated[cat]) updated[cat] = [];
    updated[cat] = [...updated[cat], newDefaultFolder.trim()];
    setInspoDefaults(updated);
    setNewDefaultFolder("");
  };
  
  const removeDefaultFolder = (cat, folder) => {
    const updated = { ...inspoDefaults };
    updated[cat] = (updated[cat] || []).filter(f => f !== folder);
    setInspoDefaults(updated);
  };
  
  if (loading) return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" /></div>;

  // If inspoOnly mode, only show inspo subfolder defaults
  if (inspoOnly) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Inspo Subfolder Defaults</CardTitle>
              <p className="text-xs text-gray-500 mt-1">Default subfolder suggestions per category. Users can customize their own.</p>
            </div>
            <Button onClick={saveInspoDefaults} size="sm" variant="outline" data-testid="save-inspo-defaults-btn">
              <Save className="w-4 h-4 mr-1" /> Save Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {inspoCategories.map(cat => {
              const catFolders = inspoDefaults[cat.id] || [];
              return (
                <div key={cat.id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      <span className="text-sm font-medium">{cat.name}</span>
                      <Badge variant="outline" className="text-[10px]">{catFolders.length}</Badge>
                    </div>
                    {catFolders.length > 0 && (
                      <Button variant="ghost" size="sm" className="text-xs text-red-500 h-6" onClick={() => {
                        setInspoDefaults(prev => ({ ...prev, [cat.id]: [] }));
                      }}>Clear</Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {catFolders.map(folder => (
                      <Badge key={folder} variant="secondary" className="text-xs gap-1">
                        {folder}
                        <button onClick={() => removeDefaultFolder(cat.id, folder)} className="ml-0.5 opacity-50 hover:opacity-100"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <Input placeholder="Add folder..." className="h-7 text-xs flex-1" value={selectedDefaultCat === cat.id ? newDefaultFolder : ""} onFocus={() => setSelectedDefaultCat(cat.id)} onChange={(e) => { setSelectedDefaultCat(cat.id); setNewDefaultFolder(e.target.value); }} onKeyDown={(e) => { if (e.key === 'Enter') addDefaultFolder(cat.id); }} />
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addDefaultFolder(cat.id)}><Plus className="w-3 h-3" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="metemplate-editor">
      {/* /me Page Template */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">/me Page Template</CardTitle>
              <p className="text-xs text-gray-500 mt-1">Edit text that syncs to all /me pages. Changes apply immediately.</p>
            </div>
            <Button onClick={saveTemplate} disabled={saving} size="sm" data-testid="save-me-template-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templateFields.map(field => (
              <div key={field.key}>
                <Label className="text-xs text-gray-500">{field.label}</Label>
                <Input
                  value={meTemplateState[field.key] || ""}
                  onChange={(e) => setMeTemplateState(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="mt-1"
                  data-testid={`template-field-${field.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Experiences Template Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Experiences</CardTitle>
              <p className="text-xs text-gray-500 mt-1">Control what users see in their Experiences tab - events, planning presets, and linking</p>
            </div>
            <Button onClick={saveTemplate} disabled={saving} size="sm" variant="outline" data-testid="save-experiences-template-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {experienceToggles.map(toggle => (
              <div key={toggle.key} className="flex items-center justify-between p-2.5 border rounded-lg" data-testid={`exp-toggle-${toggle.key}`}>
                <div>
                  <p className="text-sm font-medium">{toggle.label}</p>
                </div>
                <Switch
                  checked={meTemplateState[toggle.key] !== undefined ? meTemplateState[toggle.key] : toggle.default}
                  onCheckedChange={(checked) => setMeTemplateState(prev => ({ ...prev, [toggle.key]: checked }))}
                  data-testid={`exp-switch-${toggle.key}`}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Inspo Subfolder Defaults */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Inspo Subfolder Defaults</CardTitle>
              <p className="text-xs text-gray-500 mt-1">Default subfolder suggestions per category. Users can customize their own.</p>
            </div>
            <Button onClick={saveInspoDefaults} size="sm" variant="outline" data-testid="save-inspo-defaults-btn">
              <Save className="w-4 h-4 mr-1" /> Save Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {inspoCategories.map(cat => {
              const catFolders = inspoDefaults[cat.id] || [];
              return (
                <div key={cat.id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                      <span className="text-sm font-medium">{cat.name}</span>
                      <Badge variant="outline" className="text-[10px]">{catFolders.length}</Badge>
                    </div>
                    {catFolders.length > 0 && (
                      <Button variant="ghost" size="sm" className="text-xs text-red-500 h-6" onClick={() => {
                        const updated = { ...inspoDefaults, [cat.id]: [] };
                        setInspoDefaults(updated);
                      }}>
                        Clear
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {catFolders.map(folder => (
                      <Badge key={folder} variant="secondary" className="text-xs gap-1">
                        {folder}
                        <button onClick={() => removeDefaultFolder(cat.id, folder)} className="ml-0.5 opacity-50 hover:opacity-100">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <Input
                      placeholder="Add folder..."
                      className="h-7 text-xs flex-1"
                      value={selectedDefaultCat === cat.id ? newDefaultFolder : ""}
                      onFocus={() => setSelectedDefaultCat(cat.id)}
                      onChange={(e) => { setSelectedDefaultCat(cat.id); setNewDefaultFolder(e.target.value); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { addDefaultFolder(cat.id); } }}
                    />
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addDefaultFolder(cat.id)}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// Share Copy Section - Per-category share messaging for provider + personal profiles
function ShareCopySection() {
  const [copyData, setCopyData] = useState({});
  const [personalCopy, setPersonalCopy] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [activeTab, setActiveTab] = useState("provider");
  
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  useEffect(() => {
    const token = localStorage.getItem("session_token");
    if (!token) return;
    Promise.all([
      fetch(`${API}/admin/share-copy`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : {}),
      fetch(`${API}/admin/personal-share-copy`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : {}),
    ]).then(([providerData, personalData]) => {
      setCopyData(providerData || {});
      setPersonalCopy(personalData || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [API]);

  const updateField = (category, field, value) => {
    setCopyData(prev => ({ ...prev, [category]: { ...(prev[category] || {}), [field]: value } }));
  };
  
  const updatePersonalField = (category, field, value) => {
    setPersonalCopy(prev => ({ ...prev, [category]: { ...(prev[category] || {}), [field]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem("session_token");
    try {
      await Promise.all([
        fetch(`${API}/admin/share-copy`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(copyData) }),
        fetch(`${API}/admin/personal-share-copy`, { method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(personalCopy) }),
      ]);
      toast.success('All share copy saved!');
    } catch { toast.error('Error saving'); }
    setSaving(false);
  };

  if (loading) return <Card><CardContent className="p-6 text-center"><div className="w-5 h-5 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin mx-auto" /></CardContent></Card>;

  const categories = Object.keys(copyData).length > 0 ? Object.keys(copyData) : 
    ['hair', 'makeup', 'nails', 'lashes', 'culinary', 'wellness', 'travel', 'style', 'instructor', 'event', 'dispensary'];

  const renderFields = (cat, data, updateFn, fields) => (
    <div className="px-3 pb-3 space-y-2 border-t bg-gray-50/50">
      {fields.map(f => (
        <div key={f.key}>
          <label className="text-xs font-medium text-gray-500">{f.label}</label>
          <Input value={data?.[cat]?.[f.key] || ''} onChange={e => updateFn(cat, f.key, e.target.value)} placeholder={f.placeholder} className="text-sm" data-testid={`share-copy-${cat}-${f.key}`} />
        </div>
      ))}
    </div>
  );

  const providerFields = [
    { key: "headline", label: "Headline", placeholder: "Know Your Client's Preferences..." },
    { key: "subtext", label: "Subtext", placeholder: "Your client shared their preferences with you" },
    { key: "cta_text", label: "CTA Text", placeholder: "View Preferences" },
    { key: "provider_cta", label: "Provider Signup CTA", placeholder: "Want to receive preference cards from your clients?" },
    { key: "demo_link", label: "Demo / Learn More Link", placeholder: "/hair or https://..." },
  ];

  const personalFields = [
    { key: "headline", label: "Headline", placeholder: "Share your preferences with..." },
    { key: "subtext", label: "Subtext", placeholder: "Plan experiences effortlessly" },
    { key: "cta_text", label: "CTA Text", placeholder: "Create Your Preference Card" },
    { key: "share_prompt", label: "Share Prompt", placeholder: "Share with a provider or friend..." },
    { key: "inspo_cta", label: "Inspo CTA", placeholder: "Add your inspiration images" },
  ];

  return (
    <Card data-testid="share-copy-section">
      <CardHeader>
        <h3 className="text-base font-semibold">Share Card Copy</h3>
        <p className="text-xs text-gray-500">Configure the messaging when preference cards are shared. Provider copy is what hosts see. Personal copy is what friends/contacts see.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 mb-3">
          <Button size="sm" variant={activeTab === "provider" ? "default" : "outline"} onClick={() => setActiveTab("provider")} data-testid="share-copy-provider-tab">Provider Cards</Button>
          <Button size="sm" variant={activeTab === "personal" ? "default" : "outline"} onClick={() => setActiveTab("personal")} data-testid="share-copy-personal-tab">Personal Profiles</Button>
        </div>
        
        {categories.map(cat => (
          <div key={cat} className="border rounded-lg overflow-hidden">
            <button className="w-full flex items-center justify-between p-3 hover:bg-gray-50 text-left" onClick={() => setExpandedCat(expandedCat === cat ? null : cat)}>
              <span className="text-sm font-medium capitalize">{cat}</span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedCat === cat ? 'rotate-180' : ''}`} />
            </button>
            {expandedCat === cat && (
              activeTab === "provider" 
                ? renderFields(cat, copyData, updateField, providerFields)
                : renderFields(cat, personalCopy, updatePersonalField, personalFields)
            )}
          </div>
        ))}
        
        <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-share-copy-btn">
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : null}
          Save All Share Copy
        </Button>
      </CardContent>
    </Card>
  );
}




// Product Features Manager
function ProductFeaturesManager() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFeature, setEditFeature] = useState(null);
  const [newFeature, setNewFeature] = useState({
    name: "",
    description: "",
    tier: "free",
    status: "active",
    category: "core"
  });

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/product/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFeatures(data.features || getDefaultFeatures());
      } else {
        setFeatures(getDefaultFeatures());
      }
    } catch (err) {
      setFeatures(getDefaultFeatures());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFeatures = () => [
    { id: "f1", name: "Client Profiles", description: "Store client preferences", tier: "free", status: "active", category: "core" },
    { id: "f2", name: "Booking Calendar", description: "Schedule appointments", tier: "free", status: "active", category: "core" },
    { id: "f3", name: "Service Menu", description: "Customize services & pricing", tier: "solo", status: "active", category: "business" },
    { id: "f4", name: "Team Management", description: "Add team members", tier: "team", status: "active", category: "team" },
    { id: "f5", name: "NFC Integration", description: "Tap to check-in", tier: "luxe", status: "active", category: "premium" },
    { id: "f6", name: "Analytics Dashboard", description: "Track performance", tier: "solo", status: "active", category: "analytics" },
    { id: "f7", name: "Client Chat", description: "In-app messaging", tier: "solo", status: "beta", category: "communication" },
    { id: "f8", name: "Automated Reminders", description: "SMS/Email reminders", tier: "solo", status: "active", category: "automation" }
  ];

  const tiers = [
    { id: "free", label: "Free", color: "bg-gray-500" },
    { id: "solo", label: "Solo", color: "bg-blue-500" },
    { id: "team", label: "Team", color: "bg-purple-500" },
    { id: "luxe", label: "Luxe", color: "bg-amber-500" }
  ];

  const categories = ["core", "business", "team", "premium", "analytics", "communication", "automation"];

  const handleAddFeature = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/product/features`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newFeature)
      });
      if (res.ok) {
        toast.success("Feature added!");
        setShowAddDialog(false);
        setNewFeature({ name: "", description: "", tier: "free", status: "active", category: "core" });
        fetchFeatures();
      }
    } catch (err) {
      toast.error("Error adding feature");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Platform Features</CardTitle>
              <CardDescription>Manage features and tier access</CardDescription>
            </div>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {features.map((feature, idx) => (
              <div key={feature.id || idx} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <Zap className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="font-medium">{feature.name}</p>
                    <p className="text-sm text-gray-500">{feature.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={tiers.find(t => t.id === feature.tier)?.color || "bg-gray-500"}>
                    {feature.tier}
                  </Badge>
                  <Badge variant={feature.status === "active" ? "default" : "secondary"}>
                    {feature.status}
                  </Badge>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setEditFeature({...feature}); setShowEditDialog(true); }} data-testid={`edit-feature-${feature.id}`}>
                    <Pencil className="w-3.5 h-3.5 text-gray-400" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:text-red-600" onClick={async () => {
                    if (!window.confirm(`Delete "${feature.name}"?`)) return;
                    try {
                      const token = localStorage.getItem("session_token");
                      await fetch(`${API}/admin/product/features/${feature.id}`, {
                        method: "DELETE",
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success("Feature deleted");
                      fetchFeatures();
                    } catch (err) { toast.error("Error deleting feature"); }
                  }} data-testid={`delete-feature-${feature.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-gray-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Feature Summary by Tier */}
      <div className="grid grid-cols-4 gap-4">
        {tiers.map(tier => {
          const tierFeatures = features.filter(f => f.tier === tier.id);
          return (
            <Card key={tier.id}>
              <CardContent className="pt-6 text-center">
                <div className={`w-10 h-10 rounded-full ${tier.color} flex items-center justify-center mx-auto mb-3`}>
                  <span className="text-white font-bold">{tierFeatures.length}</span>
                </div>
                <p className="font-medium">{tier.label}</p>
                <p className="text-xs text-gray-500">{tierFeatures.length} features</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add Feature Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Feature</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Feature Name</Label>
              <Input
                value={newFeature.name}
                onChange={(e) => setNewFeature(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Client Portfolios"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newFeature.description}
                onChange={(e) => setNewFeature(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Short description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tier</Label>
                <select
                  value={newFeature.tier}
                  onChange={(e) => setNewFeature(prev => ({ ...prev, tier: e.target.value }))}
                  className="w-full p-2 border rounded-md"
                >
                  {tiers.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Category</Label>
                <select
                  value={newFeature.category}
                  onChange={(e) => setNewFeature(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-2 border rounded-md"
                >
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddFeature} disabled={!newFeature.name}>Add Feature</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Feature Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Feature</DialogTitle>
          </DialogHeader>
          {editFeature && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Feature Name</Label>
                <Input value={editFeature.name} onChange={(e) => setEditFeature(prev => ({ ...prev, name: e.target.value }))} />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editFeature.description} onChange={(e) => setEditFeature(prev => ({ ...prev, description: e.target.value }))} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Tier</Label>
                  <select value={editFeature.tier} onChange={(e) => setEditFeature(prev => ({ ...prev, tier: e.target.value }))} className="w-full p-2 border rounded-md">
                    {tiers.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Status</Label>
                  <select value={editFeature.status} onChange={(e) => setEditFeature(prev => ({ ...prev, status: e.target.value }))} className="w-full p-2 border rounded-md">
                    <option value="active">Active</option>
                    <option value="beta">Beta</option>
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="deprecated">Deprecated</option>
                  </select>
                </div>
                <div>
                  <Label>Category</Label>
                  <select value={editFeature.category} onChange={(e) => setEditFeature(prev => ({ ...prev, category: e.target.value }))} className="w-full p-2 border rounded-md">
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                const token = localStorage.getItem("session_token");
                const res = await fetch(`${API}/admin/product/features/${editFeature.id}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify(editFeature)
                });
                if (res.ok) { toast.success("Feature updated!"); setShowEditDialog(false); fetchFeatures(); }
                else { toast.error("Error updating feature"); }
              } catch (err) { toast.error("Error updating feature"); }
            }} disabled={!editFeature?.name}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inspo Admin Section - Upload types and admin collection management
function InspoAdminSection() {
  const [uploadTypes, setUploadTypes] = useState([
    { id: "upload", label: "Upload", description: "Direct photo upload from device", enabled: true, icon: "Camera" },
    { id: "pinterest", label: "Pinterest", description: "Save from Pinterest boards", enabled: true, icon: "Link2" },
    { id: "share_dm", label: "Share to DM", description: "Share via Instagram/TikTok DM", enabled: true, icon: "Send" },
    { id: "saved_profiles", label: "Saved from Profiles", description: "Saved from other user profiles", enabled: false, icon: "Heart" },
    { id: "admin_defaults", label: "Synced from Admin", description: "Admin curated default collections", enabled: true, icon: "Shield" },
  ]);
  const [saving, setSaving] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API_URL}/admin/inspo-config`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (data.upload_types) setUploadTypes(data.upload_types);
        }
      } catch {}
    };
    fetchConfig();
  }, [API_URL]);

  const toggleType = (id) => {
    setUploadTypes(prev => prev.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API_URL}/admin/inspo-config`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ upload_types: uploadTypes })
      });
      toast.success("Inspo config saved");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const iconMap = { Camera, Link2, Send, Heart, Shield };

  return (
    <div className="space-y-6" data-testid="inspo-admin-section">
      {/* Upload Types Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Upload Types</CardTitle>
              <p className="text-xs text-gray-500 mt-1">Control how users can add inspiration to their boards</p>
            </div>
            <Button onClick={handleSave} disabled={saving} size="sm" data-testid="save-inspo-config-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {uploadTypes.map(type => {
              const IconComponent = iconMap[type.icon] || Image;
              return (
                <div key={type.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`inspo-type-${type.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${type.enabled ? "bg-purple-100" : "bg-gray-100"}`}>
                      <IconComponent className={`w-4 h-4 ${type.enabled ? "text-purple-600" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{type.label}</p>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </div>
                  </div>
                  <Switch checked={type.enabled} onCheckedChange={() => toggleType(type.id)} data-testid={`inspo-toggle-${type.id}`} />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Inspo Subfolder Defaults (reuse from MeTemplateEditor logic) */}
      <MeTemplateEditor inspoOnly={true} />

      {/* Admin Collection Browser */}
      <InspoSystemTab />
    </div>
  );
}

// Inspo System Tab - Visual inspiration capture and management
function InspoSystemTab() {
  const [inspoItems, setInspoItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({ title: "", source: "direct", url: "", category: "style", tags: [] });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchInspoItems();
  }, []);

  const fetchInspoItems = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/inspo?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInspoItems(data.items || []);
      }
    } catch (err) {
      console.error("Failed to fetch inspo:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/inspo`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(uploadData)
      });
      if (res.ok) {
        toast.success("Inspo added!");
        setShowUploadDialog(false);
        setUploadData({ title: "", source: "direct", url: "", category: "style", tags: [] });
        fetchInspoItems();
      }
    } catch (err) {
      toast.error("Failed to add inspo");
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/inspo/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Deleted");
        fetchInspoItems();
      }
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleApprove = async (id, approved) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/inspo/${id}`, {
        method: "PATCH",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ approved, status: approved ? "approved" : "rejected" })
      });
      if (res.ok) {
        toast.success(approved ? "Approved" : "Rejected");
        fetchInspoItems();
      }
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const categories = [
    { value: "style", label: "Style", icon: "👗" },
    { value: "venue", label: "Venue", icon: "🏛️" },
    { value: "food", label: "Food & Drink", icon: "🍽️" },
    { value: "decor", label: "Decor", icon: "✨" },
    { value: "tech", label: "Tech", icon: "💻" },
    { value: "other", label: "Other", icon: "📌" }
  ];

  const sources = [
    { value: "direct", label: "Direct Upload" },
    { value: "pinterest", label: "Pinterest" },
    { value: "instagram", label: "Instagram" },
    { value: "url", label: "External URL" }
  ];

  const filteredItems = filter === "all" 
    ? inspoItems 
    : inspoItems.filter(item => item.category === filter || item.status === filter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Inspo System</h3>
          <p className="text-sm text-gray-500">Visual inspiration capture and hive moderation</p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Inspo
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button 
          variant={filter === "all" ? "default" : "outline"} 
          size="sm"
          onClick={() => setFilter("all")}
        >
          All ({inspoItems.length})
        </Button>
        <Button 
          variant={filter === "pending" ? "default" : "outline"} 
          size="sm"
          onClick={() => setFilter("pending")}
        >
          Pending ({inspoItems.filter(i => i.status === "pending").length})
        </Button>
        {categories.map(cat => (
          <Button 
            key={cat.value}
            variant={filter === cat.value ? "default" : "outline"} 
            size="sm"
            onClick={() => setFilter(cat.value)}
          >
            {cat.icon} {cat.label}
          </Button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Image className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No inspiration items yet</p>
            <p className="text-sm text-gray-400 mt-1">Add inspo from Pinterest, Instagram, or direct upload</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <Card key={item.inspo_id} className="overflow-hidden group">
              <div className="aspect-square bg-gray-100 relative">
                {item.image_url ? (
                  <img 
                    src={item.image_url} 
                    alt={item.title} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-8 h-8 text-gray-300" />
                  </div>
                )}
                
                {/* Status Badge */}
                <div className="absolute top-2 left-2">
                  <Badge variant={item.status === "approved" ? "default" : item.status === "rejected" ? "destructive" : "secondary"}>
                    {item.status || "pending"}
                  </Badge>
                </div>
                
                {/* Actions Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  {item.status === "pending" && (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => handleApprove(item.inspo_id, true)}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => handleApprove(item.inspo_id, false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item.inspo_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="font-medium text-sm truncate">{item.title || "Untitled"}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs">
                    {categories.find(c => c.value === item.category)?.icon} {item.category}
                  </Badge>
                  <span className="text-xs text-gray-400">{item.source}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Inspiration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input 
                value={uploadData.title}
                onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                placeholder="What inspires you about this?"
              />
            </div>
            
            <div>
              <Label>Source</Label>
              <Select 
                value={uploadData.source} 
                onValueChange={(v) => setUploadData({...uploadData, source: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sources.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Image URL</Label>
              <Input 
                value={uploadData.url}
                onChange={(e) => setUploadData({...uploadData, url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            
            <div>
              <Label>Category</Label>
              <Select 
                value={uploadData.category} 
                onValueChange={(v) => setUploadData({...uploadData, category: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={handleUpload} className="w-full">
              Add to Inspo Board
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



function IntegrationsTab() {
  const integrations = [
    { name: "Beehiiv", type: "Email & Marketing", status: "Live" },
    { name: "Apple Wallet", type: "Passes", status: "Live" },
    { name: "NFC Reader", type: "Hardware", status: "Pilot" },
    { name: "Tap In", type: "Music / Events", status: "Pilot" },
    { name: "Stripe", type: "Payments", status: "Backlog" },
    { name: "Wave", type: "Contractor Payments", status: "Team Portal" },
    { name: "Hive Moderation", type: "Content Safety", status: "Live" },
    { name: "Google Maps Places", type: "Location", status: "Live" },
    { name: "Sinch", type: "Phone OTP", status: "Live" },
    { name: "Commo", type: "Instagram DM", status: "Live" },
    { name: "Breakcold", type: "CRM", status: "Live" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Integrations</CardTitle>
        <CardDescription>Third-party services and APIs</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((int) => (
            <div key={int.name} className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium text-sm">{int.name}</p>
                <p className="text-xs text-gray-500">{int.type}</p>
              </div>
              <Badge variant={int.status === "Live" ? "default" : int.status === "Pilot" ? "secondary" : "outline"}>
                {int.status}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function QATestingTab() {
  const testCategories = [
    { name: "RSVP Flow", tests: 12, passing: 10, priority: "P0" },
    { name: "VIP Lists", tests: 8, passing: 7, priority: "P0" },
    { name: "Check-In", tests: 6, passing: 6, priority: "P1" },
    { name: "Host Dashboard", tests: 15, passing: 12, priority: "P1" },
    { name: "Admin Panel", tests: 10, passing: 8, priority: "P2" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>QA Testing</CardTitle>
            <CardDescription>End-to-end test flows for all user journeys</CardDescription>
          </div>
          <Button>
            <TestTube className="w-4 h-4 mr-2" />
            Run All Tests
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {testCategories.map((cat) => (
            <div key={cat.name} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  cat.passing === cat.tests ? "bg-green-100" : "bg-yellow-100"
                }`}>
                  <TestTube className={`w-5 h-5 ${
                    cat.passing === cat.tests ? "text-green-600" : "text-yellow-600"
                  }`} />
                </div>
                <div>
                  <p className="font-medium">{cat.name}</p>
                  <p className="text-sm text-gray-500">{cat.passing}/{cat.tests} passing</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={cat.priority === "P0" ? "destructive" : cat.priority === "P1" ? "default" : "secondary"}>
                  {cat.priority}
                </Badge>
                <Button variant="outline" size="sm">Run</Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// MESSAGES SECTION - Uses AdminMessagesSection component
// ============================================================================
function MessagesSection() {
  // Import and render the full AdminMessagesSection component
  const AdminMessagesSection = require("@/components/admin/AdminMessagesSection").default;
  return <AdminMessagesSection />;
}

// ============================================================================
// ANALYTICS SECTION
// ============================================================================
function AnalyticsSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7d");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [liveActivities, setLiveActivities] = useState([]);
  const wsRef = React.useRef(null);
  
  // WebSocket connection for real-time activity
  useEffect(() => {
    const connectWebSocket = () => {
      try {
        // Use WebSocket URL with /api prefix (K8s ingress routes /api/* to backend)
        const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
        console.log('Connecting to WebSocket:', `${wsUrl}/ws/admin-activity`);
        const ws = new WebSocket(`${wsUrl}/ws/admin-activity`);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('Admin Activity WebSocket connected');
          setWsConnected(true);
        };
        
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'activity') {
              setLiveActivities(prev => [message.data, ...prev].slice(0, 20));
            } else if (message.type === 'bulk_activities') {
              setLiveActivities(message.data || []);
            }
          } catch (e) {
            console.error('WebSocket parse error:', e);
          }
        };
        
        ws.onerror = (err) => {
          console.error('WebSocket error:', err);
          setWsConnected(false);
        };
        
        ws.onclose = () => {
          console.log('WebSocket disconnected');
          setWsConnected(false);
          // Reconnect after 5 seconds
          setTimeout(connectWebSocket, 5000);
        };
      } catch (e) {
        console.error('WebSocket connection failed:', e);
        setWsConnected(false);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);
  
  const fetchAnalytics = async () => {
    setLoading(true);
    const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
    
    try {
      const res = await fetch(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error("Failed to fetch analytics:", err);
    }
    setLoading(false);
  };
  
  useEffect(() => {
    fetchAnalytics();
    
    // Auto-refresh every 30 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchAnalytics, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);
  
  // Calculate growth metrics
  const growthMetrics = useMemo(() => {
    if (!stats) return {};
    const users7d = stats.new_users_7d || 0;
    const usersTotal = stats.total_users || 1;
    const events7d = stats.new_events_7d || 0;
    const eventsTotal = stats.total_events || 1;
    
    return {
      userGrowth: Math.round((users7d / usersTotal) * 100),
      eventGrowth: Math.round((events7d / eventsTotal) * 100),
      activeRate: stats.total_users > 0 ? Math.round((stats.active_users_7d || 0) / stats.total_users * 100) : 0
    };
  }, [stats]);

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            Analytics
            {autoRefresh && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" title="Live updating" />}
            {wsConnected ? (
              <Badge variant="outline" className="text-green-600 border-green-300 gap-1 ml-2">
                <Wifi className="w-3 h-3" /> Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-400 border-gray-300 gap-1 ml-2">
                <WifiOff className="w-3 h-3" /> Offline
              </Badge>
            )}
          </h1>
          <p className="text-gray-500">
            Platform metrics & insights
            {lastUpdated && <span className="ml-2 text-xs">• Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input 
              type="checkbox" 
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <Button variant="outline" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Real-time KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Users className="w-8 h-8 text-purple-600" />
              {growthMetrics.userGrowth > 0 && (
                <Badge className="bg-green-100 text-green-700 text-xs">+{growthMetrics.userGrowth}%</Badge>
              )}
            </div>
            <p className="text-3xl font-bold text-purple-700 mt-2">{stats?.total_users?.toLocaleString() || 0}</p>
            <p className="text-xs text-purple-600">Total Users</p>
            <p className="text-[10px] text-purple-500 mt-1">+{stats?.new_users_7d || 0} this week</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Calendar className="w-8 h-8 text-blue-600" />
              {growthMetrics.eventGrowth > 0 && (
                <Badge className="bg-green-100 text-green-700 text-xs">+{growthMetrics.eventGrowth}%</Badge>
              )}
            </div>
            <p className="text-3xl font-bold text-blue-700 mt-2">{stats?.total_events?.toLocaleString() || 0}</p>
            <p className="text-xs text-blue-600">Total Events</p>
            <p className="text-[10px] text-blue-500 mt-1">{stats?.upcoming_events || 0} upcoming</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <Badge className="bg-green-100 text-green-700 text-xs">{growthMetrics.activeRate}%</Badge>
            </div>
            <p className="text-3xl font-bold text-green-700 mt-2">{stats?.active_users_7d?.toLocaleString() || 0}</p>
            <p className="text-xs text-green-600">Active Users (7d)</p>
            <p className="text-[10px] text-green-500 mt-1">{stats?.total_rsvps || 0} total RSVPs</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Building className="w-8 h-8 text-amber-600" />
            </div>
            <p className="text-3xl font-bold text-amber-700 mt-2">{stats?.total_organizations?.toLocaleString() || 0}</p>
            <p className="text-xs text-amber-600">Hosts</p>
            <p className="text-[10px] text-amber-500 mt-1">{stats?.host_intakes || 0} host intakes</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* User Funnel */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              User Conversion Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(() => {
                const total = stats?.total_users || 0;
                const steps = [
                  { label: "Total Signups", value: total, color: "bg-purple-500" },
                  { label: "Profile Complete", value: stats?.profiles_complete || 0, color: "bg-blue-500" },
                  { label: "Preferences Set", value: stats?.with_preferences || 0, color: "bg-green-500" },
                  { label: "RSVP'd Event", value: stats?.users_with_rsvp || 0, color: "bg-amber-500" },
                  { label: "Repeat RSVP", value: stats?.repeat_users || 0, color: "bg-pink-500" }
                ];
                return steps.map((s) => ({ ...s, pct: total > 0 ? Math.round((s.value / total) * 100) : 0 }));
              })().map((step, i) => (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{step.label}</span>
                    <span className="font-medium">{step.value.toLocaleString()} ({step.pct}%)</span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${step.color} rounded-full transition-all duration-500`} style={{ width: `${step.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pilot Category Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-pink-500" />
              Users by Pilot Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: "Hair", count: stats?.pilots?.hair || 0, color: "bg-pink-500" },
                { name: "Makeup", count: stats?.pilots?.makeup || 0, color: "bg-purple-500" },
                { name: "Nails", count: stats?.pilots?.nails || 0, color: "bg-rose-500" },
                { name: "Events", count: stats?.pilots?.events || 0, color: "bg-blue-500" },
                { name: "Instructor", count: stats?.pilots?.instructor || 0, color: "bg-amber-500" },
                { name: "Wellness", count: stats?.pilots?.wellness || 0, color: "bg-green-500" },
                { name: "Culinary", count: stats?.pilots?.culinary || 0, color: "bg-orange-500" },
                { name: "Retail", count: stats?.pilots?.retail || 0, color: "bg-violet-500" }
              ].map((pilot, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50">
                  <div className={`w-3 h-3 rounded-full ${pilot.color}`} />
                  <span className="text-sm text-gray-700 flex-1">{pilot.name}</span>
                  <span className="text-sm font-bold">{pilot.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity & Engagement Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Recent Activity - Unified from all sources */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-500" />
                Recent Activity
                {wsConnected && <Badge className="bg-green-100 text-green-700 text-xs">Live</Badge>}
              </CardTitle>
              <Link to="/admin/super" className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                View All <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <p className="text-xs text-gray-500">
              {wsConnected ? 'Real-time activity via WebSocket' : 'Activity synced from all platform sources'}
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {/* Use live activities from WebSocket if connected, otherwise use stats or fallback */}
              {(wsConnected && liveActivities.length > 0 ? liveActivities : stats?.recent_activity || []).length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">No recent activity yet</div>
              ) : (wsConnected && liveActivities.length > 0 ? liveActivities : stats?.recent_activity || []).map((activity, i) => (
                <div 
                  key={i} 
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    // Navigate to relevant detail page
                    if (activity.user_id) {
                      window.location.href = `/admin/profile-editor?user=${activity.user_id}`;
                    } else if (activity.event_id) {
                      window.location.href = `/admin/portals`;
                    }
                  }}
                  data-testid={`activity-item-${i}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    (activity.type === 'rsvp' || activity.action?.includes('RSVP')) ? 'bg-green-100 text-green-600' :
                    (activity.type === 'signup' || activity.action?.includes('signup')) ? 'bg-blue-100 text-blue-600' :
                    (activity.type === 'intake' || activity.action?.includes('intake') || activity.action?.includes('Intake')) ? 'bg-purple-100 text-purple-600' :
                    (activity.type === 'role_change' || activity.action?.includes('Role')) ? 'bg-red-100 text-red-600' :
                    'bg-amber-100 text-amber-600'
                  }`}>
                    {(activity.type === 'rsvp' || activity.action?.includes('RSVP')) && <CheckCircle className="w-4 h-4" />}
                    {(activity.type === 'signup' || activity.action?.includes('signup')) && <UserPlus className="w-4 h-4" />}
                    {(activity.type === 'intake' || activity.action?.includes('Intake')) && <FileText className="w-4 h-4" />}
                    {(activity.type === 'preference' || activity.action?.includes('Preference')) && <Settings className="w-4 h-4" />}
                    {(activity.type === 'role_change' || activity.action?.includes('Role')) && <Shield className="w-4 h-4" />}
                    {!activity.type && !activity.action && <Activity className="w-4 h-4" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{activity.user || activity.performed_by || 'Unknown'}</p>
                    <p className="text-xs text-gray-500">{activity.event || activity.action || activity.details || activity.type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {activity.time || (activity.timestamp ? new Date(activity.timestamp).toLocaleTimeString() : 'now')}
                    </span>
                    <ChevronRight className="w-3 h-3 text-gray-300" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Referral Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              Referral Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center p-4 bg-rose-50 rounded-xl">
                <p className="text-3xl font-bold text-rose-600">{stats?.total_referrals || 23}</p>
                <p className="text-xs text-rose-500">Total Referrals</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-gray-700">{stats?.advocates_active || 5}</p>
                  <p className="text-[10px] text-gray-500">Active Advocates</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-gray-700">{stats?.viral_coefficient || "1.2"}x</p>
                  <p className="text-[10px] text-gray-500">Viral Coefficient</p>
                </div>
              </div>
              <Button variant="outline" className="w-full text-sm" size="sm">
                View Advocate Portal
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// PROGRESS JOURNAL SECTION - Track development journey
// ============================================================================
function ProgressJournalSection() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    week_label: "",
    month: "February 2026",
    date: new Date().toISOString().split('T')[0],
    title: "",
    content: "",
    category: "feature",
    highlights: [],
    issues: [],
    lessons: []
  });
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("timeline"); // timeline, month, week
  const [selectedMonth, setSelectedMonth] = useState("February 2026");

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem("session_token");
      console.log("[ProgressJournal] Fetching entries with token:", token ? "token present" : "NO TOKEN");
      const res = await fetch(`${API}/admin/progress-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("[ProgressJournal] Response status:", res.status);
      if (res.ok) {
        const data = await res.json();
        console.log("[ProgressJournal] Received entries:", data.entries?.length || 0);
        setEntries(data.entries || []);
      } else {
        const errorText = await res.text();
        console.error("[ProgressJournal] API error:", res.status, errorText);
      }
    } catch (err) {
      console.error("[ProgressJournal] Error fetching progress entries:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEntry = async () => {
    if (!newEntry.title || !newEntry.content) {
      toast.error("Title and content are required");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const endpoint = editingEntry 
        ? `${API}/admin/progress-entries/${editingEntry.entry_id}`
        : `${API}/admin/progress-entries`;
      
      const res = await fetch(endpoint, {
        method: editingEntry ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newEntry)
      });

      if (res.ok) {
        toast.success(editingEntry ? "Entry updated!" : "Entry added!");
        setShowAddEntry(false);
        setEditingEntry(null);
        setNewEntry({
          week_label: "",
          month: "February 2026",
          date: new Date().toISOString().split('T')[0],
          title: "",
          content: "",
          category: "feature",
          highlights: [],
          issues: [],
          lessons: []
        });
        fetchEntries();
      } else {
        toast.error("Failed to save entry");
      }
    } catch (err) {
      toast.error("Error saving entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Delete this progress entry?")) return;

    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/progress-entries/${entryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Entry deleted");
        fetchEntries();
      } else {
        toast.error("Failed to delete entry");
      }
    } catch (err) {
      toast.error("Error deleting entry");
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setNewEntry({
      week_label: entry.week_label || "",
      month: entry.month || "February 2026",
      date: entry.date || new Date().toISOString().split('T')[0],
      title: entry.title || "",
      content: entry.content || "",
      category: entry.category || "feature",
      highlights: entry.highlights || [],
      issues: entry.issues || [],
      lessons: entry.lessons || []
    });
    setShowAddEntry(true);
  };

  const getCategoryColor = (category) => {
    const colors = {
      feature: "bg-green-100 text-green-700 border-green-200",
      bugfix: "bg-red-100 text-red-700 border-red-200",
      improvement: "bg-blue-100 text-blue-700 border-blue-200",
      milestone: "bg-purple-100 text-purple-700 border-purple-200",
      learning: "bg-amber-100 text-amber-700 border-amber-200",
      challenge: "bg-orange-100 text-orange-700 border-orange-200"
    };
    return colors[category] || colors.feature;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      feature: Sparkles,
      bugfix: AlertCircle,
      improvement: TrendingUp,
      milestone: Star,
      learning: BookOpen,
      challenge: AlertCircle
    };
    return icons[category] || Sparkles;
  };

  // Group entries by month
  const entriesByMonth = entries.reduce((acc, entry) => {
    const month = entry.month || "Unknown";
    if (!acc[month]) acc[month] = [];
    acc[month].push(entry);
    return acc;
  }, {});

  const months = ["January 2026", "February 2026", "March 2026"];

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-purple-500" />
            Progress Journal
          </h1>
          <p className="text-gray-500 text-sm mt-1">Track your company building journey - sessions, progress, costs</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="roadmap">Roadmap</SelectItem>
              <SelectItem value="month">By Month</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline"
            onClick={async () => {
              const features = window.prompt("Features completed this session (comma-separated):", "");
              const bugs = window.prompt("Bugs fixed (comma-separated):", "");
              const notes = window.prompt("Session notes:", "");
              const credits = window.prompt("Credits/$ spent:", "0");
              
              if (features || bugs || notes) {
                try {
                  const token = localStorage.getItem("session_token");
                  const res = await fetch(`${API}/admin/progress-entries/auto-generate`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      session_type: "development",
                      features_completed: features ? features.split(",").map(f => f.trim()) : [],
                      bugs_fixed: bugs ? bugs.split(",").map(b => b.trim()) : [],
                      notes: notes,
                      credits_used: parseFloat(credits) || 0
                    })
                  });
                  if (res.ok) {
                    toast.success("Progress entry created!");
                    fetchEntries();
                  }
                } catch (err) {
                  toast.error("Failed to create entry");
                }
              }
            }}
          >
            <Zap className="w-4 h-4 mr-2" />
            Quick Add
          </Button>
          <Button onClick={() => setShowAddEntry(true)} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-purple-600">{entries.length}</p>
            <p className="text-sm text-gray-500">Total Sessions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">
              {entries.filter(e => e.category === "feature").length}
            </p>
            <p className="text-sm text-gray-500">Features Built</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-red-600">
              {entries.filter(e => e.category === "bugfix").length}
            </p>
            <p className="text-sm text-gray-500">Bugs Fixed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-600">
              {entries.filter(e => e.category === "milestone").length}
            </p>
            <p className="text-sm text-gray-500">Milestones</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-700">
              ${entries.reduce((sum, e) => sum + (e.credits_used || 0), 0).toFixed(0)}
            </p>
            <p className="text-sm text-green-600">Total Invested</p>
          </CardContent>
        </Card>
      </div>

      {/* Month Tabs */}
      <Tabs value={selectedMonth} onValueChange={setSelectedMonth}>
        <TabsList>
          {months.map(month => (
            <TabsTrigger key={month} value={month}>
              {month.split(" ")[0]}
              {entriesByMonth[month]?.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {entriesByMonth[month].length}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {months.map(month => (
          <TabsContent key={month} value={month} className="mt-4">
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                <p className="text-gray-500 mt-2">Loading entries...</p>
              </div>
            ) : !entriesByMonth[month]?.length ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium text-gray-700">No entries for {month}</p>
                  <p className="text-sm text-gray-500 mt-1">Start documenting your progress!</p>
                  <Button 
                    onClick={() => {
                      setNewEntry(prev => ({ ...prev, month }));
                      setShowAddEntry(true);
                    }}
                    className="mt-4 bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Entry
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {entriesByMonth[month]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((entry) => {
                    const CategoryIcon = getCategoryIcon(entry.category);
                    return (
                      <Card key={entry.entry_id} className="overflow-hidden">
                        <CardContent className="p-0">
                          <div className="flex">
                            {/* Date sidebar */}
                            <div className="w-20 bg-gray-50 p-4 flex flex-col items-center justify-center border-r shrink-0">
                              <p className="text-2xl font-bold text-gray-700">
                                {new Date(entry.date).getDate()}
                              </p>
                              <p className="text-xs text-gray-500 uppercase">
                                {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short' })}
                              </p>
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 p-4">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Badge className={getCategoryColor(entry.category)}>
                                      <CategoryIcon className="w-3 h-3 mr-1" />
                                      {entry.category}
                                    </Badge>
                                    {entry.week_label && (
                                      <Badge variant="outline" className="text-xs">
                                        {entry.week_label}
                                      </Badge>
                                    )}
                                  </div>
                                  <h3 className="font-semibold text-lg">{entry.title}</h3>
                                  <p className="text-gray-600 mt-2 whitespace-pre-wrap">{entry.content}</p>
                                  
                                  {/* Highlights */}
                                  {entry.highlights?.length > 0 && (
                                    <div className="mt-3">
                                      <p className="text-xs font-medium text-green-700 mb-1">Highlights:</p>
                                      <ul className="text-sm text-gray-600 space-y-1">
                                        {entry.highlights.map((h, i) => (
                                          <li key={i} className="flex items-start gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                            {h}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {/* Issues */}
                                  {entry.issues?.length > 0 && (
                                    <div className="mt-3">
                                      <p className="text-xs font-medium text-red-700 mb-1">Challenges:</p>
                                      <ul className="text-sm text-gray-600 space-y-1">
                                        {entry.issues.map((issue, i) => (
                                          <li key={i} className="flex items-start gap-2">
                                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                            {issue}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  
                                  {/* Lessons */}
                                  {entry.lessons?.length > 0 && (
                                    <div className="mt-3">
                                      <p className="text-xs font-medium text-amber-700 mb-1">Lessons Learned:</p>
                                      <ul className="text-sm text-gray-600 space-y-1">
                                        {entry.lessons.map((lesson, i) => (
                                          <li key={i} className="flex items-start gap-2">
                                            <BookOpen className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                            {lesson}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Actions */}
                                <div className="flex items-center gap-1 shrink-0">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditEntry(entry)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteEntry(entry.entry_id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add/Edit Entry Dialog */}
      <Dialog open={showAddEntry} onOpenChange={(open) => {
        setShowAddEntry(open);
        if (!open) {
          setEditingEntry(null);
          setNewEntry({
            week_label: "",
            month: selectedMonth,
            date: new Date().toISOString().split('T')[0],
            title: "",
            content: "",
            category: "feature",
            highlights: [],
            issues: [],
            lessons: []
          });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? "Edit Progress Entry" : "Add Progress Entry"}
            </DialogTitle>
            <DialogDescription>
              Document what you worked on, challenges faced, and lessons learned
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Month</Label>
                <Select 
                  value={newEntry.month} 
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, month: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Week Label (optional)</Label>
                <Input
                  value={newEntry.week_label}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, week_label: e.target.value }))}
                  placeholder="e.g. Week 1, Week 4"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={newEntry.category} 
                  onValueChange={(v) => setNewEntry(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">Feature</SelectItem>
                    <SelectItem value="bugfix">Bug Fix</SelectItem>
                    <SelectItem value="improvement">Improvement</SelectItem>
                    <SelectItem value="milestone">Milestone</SelectItem>
                    <SelectItem value="learning">Learning</SelectItem>
                    <SelectItem value="challenge">Challenge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={newEntry.title}
                onChange={(e) => setNewEntry(prev => ({ ...prev, title: e.target.value }))}
                placeholder="What did you work on?"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                value={newEntry.content}
                onChange={(e) => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Describe what you did, how it went, any details worth noting..."
                className="mt-1 min-h-[120px]"
              />
            </div>

            <div>
              <Label>Highlights (one per line)</Label>
              <Textarea
                value={newEntry.highlights?.join('\n') || ''}
                onChange={(e) => setNewEntry(prev => ({ 
                  ...prev, 
                  highlights: e.target.value.split('\n').filter(h => h.trim()) 
                }))}
                placeholder="Key wins or accomplishments..."
                className="mt-1 min-h-[80px]"
              />
            </div>

            <div>
              <Label>Challenges/Issues (one per line)</Label>
              <Textarea
                value={newEntry.issues?.join('\n') || ''}
                onChange={(e) => setNewEntry(prev => ({ 
                  ...prev, 
                  issues: e.target.value.split('\n').filter(i => i.trim()) 
                }))}
                placeholder="Problems encountered, blockers..."
                className="mt-1 min-h-[80px]"
              />
            </div>

            <div>
              <Label>Lessons Learned (one per line)</Label>
              <Textarea
                value={newEntry.lessons?.join('\n') || ''}
                onChange={(e) => setNewEntry(prev => ({ 
                  ...prev, 
                  lessons: e.target.value.split('\n').filter(l => l.trim()) 
                }))}
                placeholder="What you learned, insights gained..."
                className="mt-1 min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEntry(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveEntry}
              disabled={saving || !newEntry.title || !newEntry.content}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              {editingEntry ? "Update Entry" : "Add Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ============================================================================
// ADMIN EXPERIENCE PRESETS SECTION - Manage service categories, occasion types, linked defaults
// ============================================================================
function AdminExperiencePresetsSection() {
  const [serviceCategories, setServiceCategories] = useState([]);
  const [occasionTypes, setOccasionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOccasion, setEditingOccasion] = useState(null);
  const [newOccasionName, setNewOccasionName] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("session_token");
    Promise.all([
      fetch(`${API}/me/defaults-schema`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/me/occasion-types`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null)
    ]).then(([schema, occasions]) => {
      if (schema?.service_categories) setServiceCategories(schema.service_categories);
      if (occasions?.types) setOccasionTypes(occasions.types);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSaveOccasionTypes = async (types) => {
    const token = localStorage.getItem("session_token");
    try {
      const res = await fetch(`${API}/admin/settings/occasion-types`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ types })
      });
      if (res.ok) { toast.success("Occasion types saved"); setOccasionTypes(types); }
    } catch { toast.error("Failed to save"); }
  };

  const addOccasion = () => {
    if (!newOccasionName.trim()) return;
    const id = newOccasionName.toLowerCase().replace(/\s+/g, '_');
    const updated = [...occasionTypes, { id, label: newOccasionName, icon: "calendar", suggestedExperiences: [] }];
    handleSaveOccasionTypes(updated);
    setNewOccasionName("");
  };

  const removeOccasion = (id) => {
    handleSaveOccasionTypes(occasionTypes.filter(t => t.id !== id));
  };

  const toggleSuggestedExperience = (occasionId, serviceId) => {
    const updated = occasionTypes.map(t => {
      if (t.id !== occasionId) return t;
      const suggested = t.suggestedExperiences || [];
      return { ...t, suggestedExperiences: suggested.includes(serviceId) ? suggested.filter(s => s !== serviceId) : [...suggested, serviceId] };
    });
    handleSaveOccasionTypes(updated);
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6" data-testid="experience-presets-section">
      <div>
        <h1 className="text-xl font-bold mb-1">Experience Presets</h1>
        <p className="text-sm text-gray-500">Configure service categories and personal event types that sync to /me Experiences tab</p>
      </div>

      {/* Service Categories from admin CATEGORY_DEFINITIONS */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-500" /> Service Categories</CardTitle>
          <CardDescription>These are synced from Category Presets and drive the /me experience planner. Edit presets in each category&apos;s Host Dashboard.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="service-categories-grid">
            {serviceCategories.map(cat => (
              <div key={cat.id} className="border rounded-lg p-3 hover:shadow-sm transition-shadow group relative" data-testid={`service-cat-${cat.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gray-100">
                    <Sparkles className="w-3.5 h-3.5 text-gray-600" />
                  </div>
                  <span className="font-medium text-sm">{cat.label}</span>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-500">Linked defaults: {(cat.usesDefaults || []).join(', ') || 'none'}</p>
                  <div className="flex items-center gap-1">
                    <p className="text-xs text-gray-500">Lead time:</p>
                    <input
                      type="number"
                      min="0"
                      className="w-10 h-5 text-xs text-center border rounded px-1"
                      value={cat.suggestedDaysBefore || 0}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 0;
                        const updated = serviceCategories.map(c => c.id === cat.id ? { ...c, suggestedDaysBefore: val } : c);
                        setServiceCategories(updated);
                        // Save via admin settings
                        const token = localStorage.getItem('session_token');
                        fetch(`${API}/admin/settings/service-categories`, {
                          method: "PUT",
                          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                          body: JSON.stringify({ categories: updated })
                        });
                      }}
                      data-testid={`service-cat-lead-time-${cat.id}`}
                    />
                    <span className="text-xs text-gray-500">d before</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Personal Event / Occasion Types */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-amber-500" /> Personal Event Types</CardTitle>
              <CardDescription>When a user plans a personal event, these types suggest which linked experiences to auto-create</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {occasionTypes.map(occasion => (
              <div key={occasion.id} className="border rounded-lg p-3" data-testid={`occasion-${occasion.id}`}>
                <div className="flex items-center justify-between mb-2">
                  {editingOccasion === occasion.id ? (
                    <div className="flex items-center gap-2 flex-1 mr-2">
                      <Input
                        className="h-7 text-sm flex-1"
                        value={occasion.label}
                        onChange={(e) => {
                          const updated = occasionTypes.map(t => t.id === occasion.id ? { ...t, label: e.target.value } : t);
                          setOccasionTypes(updated);
                        }}
                        onBlur={() => { handleSaveOccasionTypes(occasionTypes); setEditingOccasion(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { handleSaveOccasionTypes(occasionTypes); setEditingOccasion(null); } }}
                        autoFocus
                        data-testid={`occasion-edit-input-${occasion.id}`}
                      />
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { handleSaveOccasionTypes(occasionTypes); setEditingOccasion(null); }}>
                        <Check className="w-3 h-3 text-emerald-600" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{occasion.label}</span>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setEditingOccasion(occasion.id)} data-testid={`occasion-edit-btn-${occasion.id}`}>
                        <Edit3 className="w-3 h-3 text-gray-400" />
                      </Button>
                    </div>
                  )}
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500 hover:text-red-700" onClick={() => removeOccasion(occasion.id)} data-testid={`occasion-delete-${occasion.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {serviceCategories.filter(c => c.id !== 'custom').map(cat => {
                    const isLinked = (occasion.suggestedExperiences || []).includes(cat.id);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => toggleSuggestedExperience(occasion.id, cat.id)}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${isLinked ? 'bg-black text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                        data-testid={`occasion-link-${occasion.id}-${cat.id}`}
                      >
                        {cat.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Add new occasion type */}
          <div className="flex items-center gap-2 mt-4 pt-3 border-t">
            <Input
              placeholder="New event type name..."
              value={newOccasionName}
              onChange={(e) => setNewOccasionName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addOccasion()}
              className="flex-1 h-8 text-sm"
              data-testid="add-occasion-input"
            />
            <Button size="sm" className="h-8" onClick={addOccasion} data-testid="add-occasion-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// ADMIN CATEGORY ANALYTICS - Per-category stats, defaults fill rates, org counts
// ============================================================================
function AdminCategoryAnalytics() {
  const [stats, setStats] = useState(null);
  const [defaultsStats, setDefaultsStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("session_token");
    Promise.all([
      fetch(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/admin/analytics/defaults`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null).catch(() => null)
    ]).then(([s, d]) => {
      setStats(s);
      setDefaultsStats(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  const catKeys = ["hair", "nails", "makeup", "lash", "wellness", "events", "instructor", "culinary", "style", "travel", "dispensary"];
  const catMeta = Object.fromEntries(PILOT_CATEGORIES.map(c => [c.key, c]));

  return (
    <div className="space-y-6" data-testid="category-analytics-section">
      <div>
        <h1 className="text-xl font-bold mb-1">Category Analytics</h1>
        <p className="text-sm text-gray-500">Per-category breakdown of hosts, RSVPs, orgs, and defaults fill rates</p>
        {/* Iter 9.40 Pass F - explicit sync connections so admins know where this data comes from */}
        <div className="mt-2 flex flex-wrap gap-2 text-[11px]" data-testid="analytics-connections">
          <span className="text-gray-500">Synced with:</span>
          <button onClick={() => window.location.assign("/admin/product?section=insights-atlas")} className="text-purple-600 hover:underline">
            🔗 Insights Atlas
          </button>
          <button onClick={() => window.location.assign("/admin/product?section=launch-control")} className="text-purple-600 hover:underline">
            🔗 Launch Control
          </button>
          <button onClick={() => window.location.assign("/admin/product?section=master-features")} className="text-purple-600 hover:underline">
            🔗 Master Features
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-gray-500 mb-1">Total Users</p>
          <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
          <p className="text-xs text-green-600 mt-1">+{stats?.new_users_7d || 0} this week</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-gray-500 mb-1">Total RSVPs</p>
          <p className="text-2xl font-bold">{stats?.total_rsvps || 0}</p>
          <p className="text-xs text-gray-500 mt-1">{stats?.repeat_users || 0} repeat users</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-gray-500 mb-1">Users with Defaults</p>
          <p className="text-2xl font-bold">{stats?.with_preferences || 0}</p>
          <p className="text-xs text-gray-500 mt-1">{stats?.total_users ? Math.round((stats.with_preferences || 0) / stats.total_users * 100) : 0}% fill rate</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-gray-500 mb-1">Active Advocates</p>
          <p className="text-2xl font-bold">{stats?.advocates_active || 0}</p>
          <p className="text-xs text-gray-500 mt-1">{stats?.total_referrals || 0} referrals</p>
        </Card>
      </div>

      {/* Per-Category Table */}
      <Card>
        <CardHeader>
          <CardTitle>Per-Category Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Hosts</TableHead>
                <TableHead className="text-right">RSVPs</TableHead>
                <TableHead className="text-right">Intakes</TableHead>
                <TableHead className="text-right">Fill Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {catKeys.map(key => {
                const meta = catMeta[key];
                if (!meta) return null;
                const Icon = meta.icon;
                const hosts = stats?.pilots?.[key] || 0;
                const rsvps = stats?.rsvps_by_category?.[key] || 0;
                const intakes = stats?.intake_by_category?.[key] || 0;
                const defaults = defaultsStats?.categories?.[key] || 0;
                const fillPct = stats?.total_users ? Math.round(defaults / Math.max(stats.total_users, 1) * 100) : 0;
                return (
                  <TableRow key={key} data-testid={`analytics-row-${key}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: meta.bgColor }}>
                          <Icon className="w-3 h-3" style={{ color: meta.iconColor }} />
                        </div>
                        <span className="font-medium text-sm">{meta.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">{hosts}</TableCell>
                    <TableCell className="text-right">{rsvps}</TableCell>
                    <TableCell className="text-right">{intakes}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 bg-gray-100 rounded-full h-1.5">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(fillPct, 100)}%`, backgroundColor: meta.iconColor }} />
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">{fillPct}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Defaults Fill Breakdown */}
      {defaultsStats?.field_counts && (
        <Card>
          <CardHeader>
            <CardTitle>Defaults Fill Breakdown</CardTitle>
            <CardDescription>How many users have filled each default category in /me</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {Object.entries(defaultsStats.field_counts).map(([field, count]) => (
                <div key={field} className="border rounded-lg p-3 text-center">
                  <p className="text-lg font-bold">{count}</p>
                  <p className="text-xs text-gray-500 capitalize">{field.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ============================================================================
// PRODUCT INTEGRATIONS SECTION - Inside Product sidebar
// Experience integrations (host-facing) + Architecture/Tech Stack
// ============================================================================
function ProductIntegrationsSection() {
  const [beehiivStatus, setBeehiivStatus] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const [intRes, bhRes] = await Promise.all([
        fetch(`${API}/admin/integrations`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/beehiiv/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (intRes.ok) {
        const data = await intRes.json();
        setIntegrations(data.integrations || []);
      }
      if (bhRes.ok) setBeehiivStatus(await bhRes.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchIntegrations(); }, []);

  const saveIntegration = async () => {
    if (!editingIntegration) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/integrations/${editingIntegration.integration_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        toast.success("Integration updated");
        setEditingIntegration(null);
        fetchIntegrations();
      } else {
        toast.error("Failed to save");
      }
    } catch { toast.error("Error saving"); }
  };

  const openEdit = (item) => {
    setEditingIntegration(item);
    setEditForm({
      name: item.name,
      description: item.description,
      status: item.status,
      details: item.details || [],
      logo_url: item.logo_url || "",
      cover_url: item.cover_url || "",
      color: item.color,
      bg: item.bg,
    });
  };

  const ICON_MAP = { Music, Calendar, Mail, Image, CreditCard, Database, Globe, Zap, Shield };

  const experienceIntegrations = integrations.filter(i => i.type === "experience");
  const architectureIntegrations = integrations.filter(i => i.type === "architecture");

  const StatusBadge = ({ status }) => {
    const styles = {
      connected: "bg-green-100 text-green-700",
      not_configured: "bg-amber-100 text-amber-700",
      toggle: "bg-blue-100 text-blue-700",
    };
    const labels = {
      connected: "Connected",
      not_configured: "Not Configured",
      toggle: "Host Toggle",
    };
    return (
      <Badge className={`${styles[status] || styles.connected} text-xs font-medium`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const IntegrationCard = ({ item }) => {
    const IconComp = ICON_MAP[item.icon] || Zap;
    // For beehiiv, show subscriber count
    const stats = item.slug === "beehiiv" && beehiivStatus ? `${beehiivStatus.total_subscribers || 0} subscribers synced` : null;
    return (
      <Card className="hover:shadow-md transition-shadow" data-testid={`integration-${item.slug}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden ${item.logo_url ? "" : "p-1.5"}`} style={item.logo_url ? {} : { backgroundColor: item.bg }}>
                {item.logo_url ? (
                  <img src={item.logo_url} alt={item.name} className="w-full h-full object-contain" />
                ) : (
                  <IconComp className="w-6 h-6" style={{ color: item.color }} />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-sm">{item.name}</h3>
                {stats && <p className="text-xs text-gray-500">{stats}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={item.status} />
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(item)} data-testid={`edit-integration-${item.slug}`}>
                <Edit2 className="w-3.5 h-3.5 text-gray-400" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-3 leading-relaxed">{item.description}</p>
          <div className="space-y-1">
            {(item.details || []).map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
                <div className="w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                {d}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-8" data-testid="product-integrations">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">Experience integrations for hosts and platform architecture connections</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchIntegrations} data-testid="refresh-integrations-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Experience Integrations */}
      <div>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Experience Integrations</h2>
        <p className="text-sm text-gray-500 mb-4">Tools that hosts use to enhance their client experiences</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {experienceIntegrations.map(item => (
            <IntegrationCard key={item.integration_id || item.slug} item={item} />
          ))}
        </div>
      </div>

      {/* Architecture / Tech Stack */}
      <div>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Architecture & Tech Stack</h2>
        <p className="text-sm text-gray-500 mb-4">Core infrastructure powering the Phygital platform</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {architectureIntegrations.map(item => (
            <IntegrationCard key={item.integration_id || item.slug} item={item} />
          ))}
        </div>
      </div>

      {/* Edit Integration Dialog */}
      {editingIntegration && (
        <Dialog open={!!editingIntegration} onOpenChange={() => setEditingIntegration(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit {editingIntegration.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input value={editForm.name || ""} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-integration-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea rows={3} value={editForm.description || ""} onChange={e => setEditForm({...editForm, description: e.target.value})} data-testid="edit-integration-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <select className="w-full border rounded-md p-2 text-sm" value={editForm.status || "connected"} onChange={e => setEditForm({...editForm, status: e.target.value})} data-testid="edit-integration-status">
                    <option value="connected">Connected</option>
                    <option value="not_configured">Not Configured</option>
                    <option value="toggle">Host Toggle</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Icon Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editForm.color || "#000000"} onChange={e => setEditForm({...editForm, color: e.target.value})} className="w-8 h-8 rounded cursor-pointer" />
                    <Input value={editForm.color || ""} onChange={e => setEditForm({...editForm, color: e.target.value})} className="flex-1" />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Logo Image</label>
                <div className="flex items-center gap-3 mt-1">
                  {editForm.logo_url && <img src={editForm.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover border" />}
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <Upload className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">{editForm.logo_url ? "Replace" : "Upload"} Logo</span>
                    <input type="file" accept="image/*" className="hidden" data-testid="edit-integration-logo" onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      const formData = new FormData(); formData.append("file", file);
                      try {
                        const res = await fetch(`${API}/upload/image`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }, body: formData });
                        if (res.ok) { const data = await res.json(); setEditForm({...editForm, logo_url: data.url || data.secure_url || data.image_url}); }
                      } catch {}
                    }} />
                  </label>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Cover Image</label>
                <div className="flex items-center gap-3 mt-1">
                  {editForm.cover_url && <img src={editForm.cover_url} alt="" className="w-full h-20 rounded-lg object-cover border" />}
                  <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors w-full justify-center">
                    <Upload className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">{editForm.cover_url ? "Replace" : "Upload"} Cover</span>
                    <input type="file" accept="image/*" className="hidden" data-testid="edit-integration-cover" onChange={async (e) => {
                      const file = e.target.files[0]; if (!file) return;
                      const formData = new FormData(); formData.append("file", file);
                      try {
                        const res = await fetch(`${API}/upload/image`, { method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }, body: formData });
                        if (res.ok) { const data = await res.json(); setEditForm({...editForm, cover_url: data.url || data.secure_url || data.image_url}); }
                      } catch {}
                    }} />
                  </label>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Details (one per line)</label>
                <Textarea rows={4} value={(editForm.details || []).join("\n")} onChange={e => setEditForm({...editForm, details: e.target.value.split("\n").filter(Boolean)})} data-testid="edit-integration-details" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditingIntegration(null)}>Cancel</Button>
              <Button onClick={saveIntegration} data-testid="save-integration-btn">Save Changes</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// ============================================================================
// INTEGRATIONS SECTION - Beehiiv Field Mapping, Acuity, etc.
// ============================================================================
function IntegrationsSection() {
  const [activeTab, setActiveTab] = useState("beehiiv");
  const [beehiivStatus, setBeehiivStatus] = useState(null);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // All user fields that could sync to Beehiiv
  const USER_FIELDS = [
    { key: "email", label: "Email", syncs: true, beehiiv_type: "email" },
    { key: "first_name", label: "First Name", syncs: true, beehiiv_type: "first_name" },
    { key: "last_name", label: "Last Name", syncs: true, beehiiv_type: "last_name" },
    { key: "company", label: "Company", syncs: true, beehiiv_type: "tag" },
    { key: "city", label: "City", syncs: true, beehiiv_type: "tag" },
    { key: "role", label: "Role", syncs: true, beehiiv_type: "tag" },
    { key: "tier", label: "Tier", syncs: true, beehiiv_type: "tag" },
    { key: "pilot_category", label: "Pilot Category", syncs: true, beehiiv_type: "tag" },
    { key: "pilot_status", label: "Pilot Status", syncs: true, beehiiv_type: "tag" },
    { key: "intake_completed", label: "Intake Completed", syncs: true, beehiiv_type: "tag" },
    { key: "nda_signed", label: "NDA Signed", syncs: true, beehiiv_type: "tag" },
    { key: "source", label: "Source", syncs: true, beehiiv_type: "tag" },
    { key: "phone", label: "Phone", syncs: false, beehiiv_type: "none" },
    { key: "instagram", label: "Instagram", syncs: false, beehiiv_type: "none" },
    { key: "linkedin", label: "LinkedIn", syncs: false, beehiiv_type: "none" },
    { key: "bio", label: "Bio", syncs: false, beehiiv_type: "none" },
  ];
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/admin/beehiiv/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setBeehiivStatus(await res.json());
        }
      } catch (err) {
        console.error("Failed to fetch Beehiiv status:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  const handleSyncNow = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/beehiiv/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Synced ${data.synced_to_beehiiv} users to Beehiiv`);
        // Refresh status
        const statusRes = await fetch(`${API}/admin/beehiiv/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (statusRes.ok) {
          setBeehiivStatus(await statusRes.json());
        }
      }
    } catch (err) {
      toast.error("Sync failed");
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Plug className="w-6 h-6 text-purple-500" />
          Integrations
        </h1>
        <p className="text-gray-500 text-sm mt-1">Manage your external service connections</p>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="beehiiv">
            <Mail className="w-4 h-4 mr-2" />
            Beehiiv
          </TabsTrigger>
          <TabsTrigger value="acuity">
            <Calendar className="w-4 h-4 mr-2" />
            Acuity
          </TabsTrigger>
          <TabsTrigger value="automations">
            <Zap className="w-4 h-4 mr-2" />
            Automations
          </TabsTrigger>
        </TabsList>
        
        {/* Beehiiv Tab */}
        <TabsContent value="beehiiv" className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-orange-500" />
                  Beehiiv Newsletter
                </span>
                <Badge variant={beehiivStatus?.configured ? "default" : "secondary"}>
                  {beehiivStatus?.configured ? "Connected" : "Not Configured"}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {beehiivStatus?.configured ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-orange-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-600">{beehiivStatus?.subscriber_count || 0}</p>
                      <p className="text-sm text-gray-600">Subscribers</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{beehiivStatus?.synced_count || 0}</p>
                      <p className="text-sm text-gray-600">Synced Users</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{beehiivStatus?.tags_count || 0}</p>
                      <p className="text-sm text-gray-600">Tags</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium">Last Sync</p>
                      <p className="text-xs text-gray-500">
                        {beehiivStatus?.last_sync 
                          ? new Date(beehiivStatus.last_sync).toLocaleString()
                          : "Never"}
                      </p>
                    </div>
                    <Button onClick={handleSyncNow} disabled={saving}>
                      {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Sync Now
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Beehiiv not configured</p>
                  <p className="text-sm text-gray-500">Add BEEHIIV_API_KEY and BEEHIIV_PUBLICATION_ID to environment</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Field Mapping */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Field Sync Mapping
              </CardTitle>
              <CardDescription>
                Which user fields sync to Beehiiv as tags or custom fields
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-2 p-2 bg-gray-100 rounded font-medium text-sm">
                  <span>User Field</span>
                  <span>Syncs to Beehiiv</span>
                  <span>Beehiiv Type</span>
                  <span>Status</span>
                </div>
                {USER_FIELDS.map(field => (
                  <div key={field.key} className="grid grid-cols-4 gap-2 p-2 rounded hover:bg-gray-50 items-center text-sm">
                    <span className="font-medium">{field.label}</span>
                    <span>
                      {field.syncs ? (
                        <Badge className="bg-green-100 text-green-700">Yes</Badge>
                      ) : (
                        <Badge variant="secondary">No</Badge>
                      )}
                    </span>
                    <span className="text-gray-600 capitalize">{field.beehiiv_type}</span>
                    <span>
                      {field.syncs ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-gray-300" />
                      )}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-medium text-amber-800 mb-2">Need a new field synced?</h4>
                <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                  <li>Make sure the tag exists in Beehiiv (Audience → Tags)</li>
                  <li>The field will sync as a tag automatically on next sync</li>
                  <li>For custom fields, contact support</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Acuity Tab */}
        <TabsContent value="acuity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Acuity Scheduling
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-6 bg-blue-50 rounded-lg text-center">
                <Calendar className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">Acuity Integration</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Acuity connections are managed per-pilot in Private Portals settings.
                  Each pilot participant can connect their own Acuity account.
                </p>
                <Button 
                  variant="outline"
                  onClick={() => window.location.href = "/admin/portals"}
                >
                  Go to Private Portals
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Automations Tab */}
        <TabsContent value="automations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Automation Rules
              </CardTitle>
              <CardDescription>
                Automated actions triggered by user events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { trigger: "User Signs Up", action: "Add to Beehiiv with 'new_signup' tag", enabled: true },
                  { trigger: "NDA Signed", action: "Add 'nda_signed' tag to Beehiiv", enabled: true },
                  { trigger: "Intake Complete", action: "Add 'intake_complete' tag, Update status", enabled: true },
                  { trigger: "User Updates Profile", action: "Notify admin, Log activity", enabled: true },
                  { trigger: "Message Sent", action: "Notify admin via email", enabled: false },
                ].map((rule, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">{rule.trigger}</p>
                      <p className="text-sm text-gray-500">{rule.action}</p>
                    </div>
                    <Switch checked={rule.enabled} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AdminHubSection() {
  const [activeTab, setActiveTab] = useState("business");
  const [settings, setSettings] = useState({
    company_name: "Phygital",
    contact_email: "hello@phygital.systems",
    support_email: "support@phygital.systems",
    legal_email: "legal@phygital.systems",
    website_url: "https://phygital.systems",
    company_address: "",
    city: "",
    state: "",
    zip_code: "",
    country: "United States",
    phone: "",
    footer_year: new Date().getFullYear().toString(),
    instagram_url: "",
    twitter_url: "",
    linkedin_url: "",
    tiktok_url: "",
    pinterest_url: ""
  });
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Database management state
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [dbSearch, setDbSearch] = useState("");
  const [dbLoading, setDbLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editedData, setEditedData] = useState("");
  
  // Access Links state
  const [accessLinks, setAccessLinks] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [previewTokens, setPreviewTokens] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSubTab, setAccessSubTab] = useState("admin-access");
  const [showCreateLinkDialog, setShowCreateLinkDialog] = useState(false);
  const [showInviteAdminDialog, setShowInviteAdminDialog] = useState(false);
  const [showPreviewTokenDialog, setShowPreviewTokenDialog] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [newLink, setNewLink] = useState({
    recipient_name: "",
    email: "",
    expiration: "never",
    portal_access: [],
    is_super_admin: false,
    redirect_url: ""
  });
  const [newInvite, setNewInvite] = useState({ email: "", role: "admin" });
  const [newPreviewToken, setNewPreviewToken] = useState({
    name: "",
    expires_hours: 0,
    redirect_page: ""
  });
  
  // Fetch settings and NDA templates
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("session_token");
        
        // Fetch settings
        const settingsRes = await fetch(`${API}/admin/settings/global`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSettings(data);
        }
        
        // Fetch NDA templates
        const templatesRes = await fetch(`${API}/admin/nda/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (templatesRes.ok) {
          const data = await templatesRes.json();
          setTemplates(data.templates || []);
        }
        
        // Fetch database collections
        const collectionsRes = await fetch(`${API}/admin/database/collections`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (collectionsRes.ok) {
          const data = await collectionsRes.json();
          setCollections(data.collections || []);
        }
        
        // Fetch access links
        const accessRes = await fetch(`${API}/admin/access-links`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (accessRes.ok) {
          const data = await accessRes.json();
          setAccessLinks(data.access_links || []);
        }
        
        // Fetch admin users
        const usersRes = await fetch(`${API}/admin/team`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (usersRes.ok) {
          const data = await usersRes.json();
          setAdminUsers(data.members || []);
        }
        
        // Fetch preview tokens
        const previewRes = await fetch(`${API}/preview/tokens`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (previewRes.ok) {
          const data = await previewRes.json();
          setPreviewTokens(data || []);
        }
      } catch (err) {
        console.error("Failed to fetch admin data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  
  // Fetch records when collection is selected
  const fetchRecords = async (collectionName, search = "") => {
    setDbLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const params = new URLSearchParams({ limit: "50" });
      if (search) params.append("search", search);
      
      const res = await fetch(`${API}/admin/database/${collectionName}/records?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecords(data.records || []);
        setRecordsTotal(data.total || 0);
      }
    } catch (err) {
      toast.error("Failed to fetch records");
    } finally {
      setDbLoading(false);
    }
  };
  
  // Handle collection selection
  const handleSelectCollection = (coll) => {
    setSelectedCollection(coll);
    setDbSearch("");
    fetchRecords(coll.name);
  };
  
  // Delete record
  const handleDeleteRecord = async (recordId) => {
    if (!confirm("Are you sure you want to delete this record?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/database/${selectedCollection.name}/record/${recordId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Record deleted");
        fetchRecords(selectedCollection.name, dbSearch);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to delete");
      }
    } catch (err) {
      toast.error("Error deleting record");
    }
  };
  
  // Update record
  const handleUpdateRecord = async () => {
    try {
      const parsedData = JSON.parse(editedData);
      const token = localStorage.getItem("session_token");
      
      // Get the record ID
      const recordId = editingRecord.user_id || editingRecord.event_id || 
                       editingRecord.sub_category_id || editingRecord.tier_id ||
                       editingRecord.share_id || editingRecord.link_id;
      
      const res = await fetch(`${API}/admin/database/${selectedCollection.name}/record/${recordId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(parsedData)
      });
      
      if (res.ok) {
        toast.success("Record updated");
        setEditingRecord(null);
        fetchRecords(selectedCollection.name, dbSearch);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to update");
      }
    } catch (err) {
      toast.error("Invalid JSON format");
    }
  };
  
  // Save settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/admin/settings/global`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      toast.success("Settings saved!");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };
  
  // Activate NDA template
  const handleActivateTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/admin/nda/templates/${templateId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: true })
      });
      
      // Refresh templates
      const res = await fetch(`${API}/admin/nda/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data.templates || []);
      }
      toast.success("Template activated!");
    } catch (err) {
      toast.error("Failed to activate template");
    }
  };
  
  // Access Links functions
  const fetchAccessLinks = async () => {
    setAccessLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccessLinks(data.access_links || []);
      }
    } catch (err) {
      console.error("Error fetching access links:", err);
    } finally {
      setAccessLoading(false);
    }
  };
  
  const handleCreateLink = async () => {
    if (!newLink.email) {
      toast.error("Email is required");
      return;
    }
    
    setCreatingLink(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newLink)
      });
      
      if (res.ok) {
        const data = await res.json();
        const link = data.access_link;
        const fullUrl = `${window.location.origin}/auth/magic?token=${link.token}${link.redirect_url && link.redirect_url !== "/me" ? `&redirect=${link.redirect_url}` : ""}`;
        navigator.clipboard.writeText(fullUrl);
        toast.success("Access link created and copied to clipboard!");
        setShowCreateLinkDialog(false);
        setNewLink({ recipient_name: "", email: "", expiration: "never", portal_access: [], is_super_admin: false, redirect_url: "" });
        fetchAccessLinks();
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Failed to create link");
      }
    } catch (err) {
      toast.error("Error creating access link");
    } finally {
      setCreatingLink(false);
    }
  };
  
  const handleCopyLink = (link) => {
    const fullUrl = `${window.location.origin}/auth/magic?token=${link.token}${link.redirect_url && link.redirect_url !== "/me" ? `&redirect=${link.redirect_url}` : ""}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedLink(link.link_id);
    toast.success("Link copied!");
    setTimeout(() => setCopiedLink(null), 3000);
  };
  
  const handleRevokeLink = async (linkId) => {
    if (!confirm("Are you sure you want to revoke this access link?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links/${linkId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: "revoked" })
      });
      
      if (res.ok) {
        toast.success("Access link revoked");
        fetchAccessLinks();
      } else {
        toast.error("Failed to revoke link");
      }
    } catch (err) {
      toast.error("Error revoking link");
    }
  };
  
  const handleDeleteLink = async (linkId) => {
    if (!confirm("Are you sure you want to permanently delete this access link?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links/${linkId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Access link deleted");
        fetchAccessLinks();
      } else {
        toast.error("Failed to delete link");
      }
    } catch (err) {
      toast.error("Error deleting link");
    }
  };
  
  // Admin Users functions
  const fetchAdminUsers = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminUsers(data.members || []);
      }
    } catch (err) {
      console.error("Error fetching admin users:", err);
    }
  };
  
  const handleInviteAdmin = async () => {
    if (!newInvite.email) {
      toast.error("Email is required");
      return;
    }
    
    setCreatingLink(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/team/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newInvite)
      });
      
      if (res.ok) {
        toast.success("Admin invite sent!");
        setShowInviteAdminDialog(false);
        setNewInvite({ email: "", role: "admin" });
        fetchAdminUsers();
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Failed to send invite");
      }
    } catch (err) {
      toast.error("Error sending invite");
    } finally {
      setCreatingLink(false);
    }
  };
  
  const handleRemoveAdmin = async (userId) => {
    if (!confirm("Are you sure you want to remove this admin?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/team/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Admin removed");
        fetchAdminUsers();
      } else {
        toast.error("Failed to remove admin");
      }
    } catch (err) {
      toast.error("Error removing admin");
    }
  };
  
  // Preview Token functions
  const fetchPreviewTokens = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/preview/tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewTokens(data || []);
      }
    } catch (err) {
      console.error("Error fetching preview tokens:", err);
    }
  };
  
  const handleCreatePreviewToken = async () => {
    if (!newPreviewToken.name) {
      toast.error("Token name is required");
      return;
    }
    if (!newPreviewToken.redirect_page) {
      toast.error("Please select a redirect page");
      return;
    }
    
    setCreatingLink(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/preview/tokens`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPreviewToken)
      });
      
      if (res.ok) {
        const data = await res.json();
        const previewUrl = `${window.location.origin}${data.preview_url}`;
        navigator.clipboard.writeText(previewUrl);
        toast.success("Preview token created and URL copied!");
        setShowPreviewTokenDialog(false);
        setNewPreviewToken({ name: "", expires_hours: 0, redirect_page: "" });
        fetchPreviewTokens();
      } else {
        const errData = await res.json().catch(() => ({}));
        toast.error(errData.detail || "Failed to create preview token");
      }
    } catch (err) {
      toast.error("Error creating preview token");
    } finally {
      setCreatingLink(false);
    }
  };
  
  const handleDeletePreviewToken = async (tokenValue) => {
    if (!confirm("Are you sure you want to delete this preview token?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/preview/tokens/${tokenValue}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Preview token deleted");
        fetchPreviewTokens();
      } else {
        toast.error("Failed to delete preview token");
      }
    } catch (err) {
      toast.error("Error deleting preview token");
    }
  };
  
  // Filter access links by type
  const adminAccessLinks = accessLinks.filter(link => link.is_super_admin || link.redirect_url === "/admin");
  const magicLinks = accessLinks.filter(link => !link.is_super_admin && link.redirect_url !== "/admin" && !link.token?.startsWith("PREVIEW"));
  const previewLinks = accessLinks.filter(link => link.token?.startsWith("PREVIEW"));
  
  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">Admin Hub</h1>
          <p className="text-gray-500">Global settings, NDA templates, and configuration</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="business">Business Info</TabsTrigger>
          <TabsTrigger value="access-links">Access Links</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="database">Database</TabsTrigger>
          {/* Iter 9.40 Slice C³: Files / Branding / Integrations moved into Admin → Product
              (Master Components → Branding tab + Core Files admin nav + Integrations admin nav). */}
        </TabsList>

        <TabsContent value="business" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Business Information</CardTitle>
              <CardDescription>Company contacts and details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Core Details */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Contact Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={settings.company_name}
                        onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Website URL</Label>
                      <Input
                        value={settings.website_url || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, website_url: e.target.value }))}
                        placeholder="https://phygital.systems"
                      />
                    </div>
                    <div>
                      <Label>Contact Email</Label>
                      <Input
                        value={settings.contact_email}
                        onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Support Email</Label>
                      <Input
                        value={settings.support_email}
                        onChange={(e) => setSettings(prev => ({ ...prev, support_email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Legal Email</Label>
                      <Input
                        value={settings.legal_email}
                        onChange={(e) => setSettings(prev => ({ ...prev, legal_email: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={settings.phone || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Company Address</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Street Address</Label>
                      <Input
                        value={settings.company_address || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, company_address: e.target.value }))}
                        placeholder="123 Main St, Suite 100"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={settings.city || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, city: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>State / Region</Label>
                      <Input
                        value={settings.state || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, state: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>ZIP / Postal Code</Label>
                      <Input
                        value={settings.zip_code || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, zip_code: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input
                        value={settings.country || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, country: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Social Media</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Instagram</Label>
                      <Input
                        value={settings.instagram_url || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, instagram_url: e.target.value }))}
                        placeholder="https://instagram.com/phygital"
                      />
                    </div>
                    <div>
                      <Label>TikTok</Label>
                      <Input
                        value={settings.tiktok_url || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, tiktok_url: e.target.value }))}
                        placeholder="https://tiktok.com/@phygital"
                      />
                    </div>
                    <div>
                      <Label>Pinterest</Label>
                      <Input
                        value={settings.pinterest_url || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, pinterest_url: e.target.value }))}
                        placeholder="https://pinterest.com/phygital"
                      />
                    </div>
                    <div>
                      <Label>LinkedIn</Label>
                      <Input
                        value={settings.linkedin_url || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, linkedin_url: e.target.value }))}
                        placeholder="https://linkedin.com/company/phygital"
                      />
                    </div>
                    <div>
                      <Label>Twitter / X</Label>
                      <Input
                        value={settings.twitter_url || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, twitter_url: e.target.value }))}
                        placeholder="https://x.com/phygital"
                      />
                    </div>
                  </div>
                </div>

                {/* Footer & Display */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Display Settings</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Footer Copyright Year</Label>
                      <Input
                        value={settings.footer_year || ""}
                        onChange={(e) => setSettings(prev => ({ ...prev, footer_year: e.target.value }))}
                        placeholder="2026"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveSettings} disabled={saving} className="mt-6">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Cloudinary */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                    <Globe className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">Cloudinary</h3>
                      <Badge className="bg-green-500 text-white">Connected</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">Image and media management with CDN delivery for all uploads.</p>
                    <p className="text-xs text-gray-400">Used for profile images, portal assets, and invite customization</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Stripe */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                    <DollarSign className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">Stripe</h3>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">Planned</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">Payment processing with multi-currency support for subscriptions and one-time payments.</p>
                    <p className="text-xs text-gray-400">Angel Number pricing: Solo $11/mo, Team $44/mo, Luxe $111/mo</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Beehiiv */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                    <Mail className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">Beehiiv</h3>
                      <Badge className="bg-green-100 text-green-700 border-green-300">Connected</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">Newsletter management and email campaigns. Syncs subscriber data and engagement metrics.</p>
                    <p className="text-xs text-gray-400">Publication ID configured. API key active.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Acuity */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center shrink-0">
                    <Calendar className="w-6 h-6 text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">Acuity Scheduling</h3>
                      <Badge className="bg-green-100 text-green-700 border-green-300">Connected</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">Appointment booking and scheduling integration for service providers.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Breakcold */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">Breakcold</h3>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">Planned</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">CRM and VIP list management with social selling and tracking capabilities.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Kommo */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                    <MessageSquare className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">Kommo</h3>
                      <Badge variant="outline" className="text-gray-500 border-gray-300">Low Priority</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">Instagram DM integration for sharing and social engagement tracking.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Google Maps */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold">Google Maps Places</h3>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">Planned</Badge>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">Address autocomplete and location services for events and providers.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Access Links Tab */}
        <TabsContent value="access-links" className="mt-6">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Access Control</h2>
                <p className="text-gray-500">Manage admin access, magic links, and preview tokens</p>
              </div>
            </div>
            
            {/* Sub-tabs */}
            <Tabs value={accessSubTab} onValueChange={setAccessSubTab}>
              <TabsList>
                <TabsTrigger value="admin-access">Admin Access ({adminAccessLinks.length})</TabsTrigger>
                <TabsTrigger value="magic-links">Magic Links ({magicLinks.length})</TabsTrigger>
                <TabsTrigger value="preview-tokens">Preview Tokens ({previewLinks.length + previewTokens.length})</TabsTrigger>
              </TabsList>
              
              {/* Admin Access Sub-tab */}
              <TabsContent value="admin-access" className="mt-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="w-5 h-5 text-purple-600" />
                          Admin Access
                        </CardTitle>
                        <CardDescription>Super admin and admin access links</CardDescription>
                      </div>
                      <Button onClick={() => {
                        setNewLink({ ...newLink, is_super_admin: true, redirect_url: "/admin" });
                        setShowCreateLinkDialog(true);
                      }} className="bg-purple-600 hover:bg-purple-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Admin Access
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {accessLoading ? (
                      <div className="flex justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                      </div>
                    ) : adminAccessLinks.length === 0 ? (
                      <div className="py-8 text-center">
                        <Shield className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No admin access links</p>
                        <p className="text-sm text-gray-500">Create admin access for team members</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {adminAccessLinks.map((link) => (
                          <div key={link.link_id} className={`p-4 border rounded-lg ${link.status === "revoked" ? "opacity-60 bg-gray-50" : "bg-white hover:bg-gray-50"}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium">{link.recipient_name || "Unnamed"}</p>
                                  <Badge className={link.status === "active" ? "bg-green-500" : "bg-gray-400"}>
                                    {link.status}
                                  </Badge>
                                  {link.is_super_admin && (
                                    <Badge variant="outline" className="border-purple-500 text-purple-600">
                                      Super Admin
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500">{link.email}</p>
                                <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                                  <code className="bg-gray-100 px-2 py-0.5 rounded">{link.token}</code>
                                  <span>•</span>
                                  <span>Used: {link.use_count || 0}x</span>
                                </div>
                                {copiedLink === link.link_id && (
                                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs font-mono text-green-700">
                                    {`${window.location.origin}/auth/magic?token=${link.token}`}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleCopyLink(link)}>
                                  <Copy className="w-4 h-4" />
                                </Button>
                                {link.status === "active" && (
                                  <Button variant="outline" size="sm" onClick={() => handleRevokeLink(link.link_id)} className="text-amber-600">
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button variant="outline" size="sm" onClick={() => handleDeleteLink(link.link_id)} className="text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Admin Users */}
                <Card className="mt-4">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-600" />
                          Admin Users
                        </CardTitle>
                        <CardDescription>Team members with admin access</CardDescription>
                      </div>
                      <Button variant="outline" onClick={() => setShowInviteAdminDialog(true)}>
                        <Mail className="w-4 h-4 mr-2" />
                        Invite Admin
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {adminUsers.length === 0 ? (
                      <div className="py-8 text-center">
                        <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No admin users yet</p>
                        <p className="text-sm text-gray-500">Invite team members to manage the platform</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {adminUsers.map((user) => (
                          <div key={user.user_id || user.email} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-gray-500" />
                              </div>
                              <div>
                                <p className="font-medium">{user.name || user.email}</p>
                                <p className="text-xs text-gray-500">{user.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{user.role || "admin"}</Badge>
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveAdmin(user.user_id)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Magic Links Sub-tab */}
              <TabsContent value="magic-links" className="mt-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Link2 className="w-5 h-5 text-green-600" />
                          Magic Links
                        </CardTitle>
                        <CardDescription>Access codes for pilots and portals</CardDescription>
                      </div>
                      <Button onClick={() => {
                        setNewLink({ recipient_name: "", email: "", expiration: "never", portal_access: [], is_super_admin: false, redirect_url: "" });
                        setShowCreateLinkDialog(true);
                      }} className="bg-green-600 hover:bg-green-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Magic Link
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {magicLinks.length === 0 ? (
                      <div className="py-8 text-center">
                        <Link2 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No magic links yet</p>
                        <p className="text-sm text-gray-500">Create access codes for pilots and portals</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {magicLinks.map((link) => (
                          <div key={link.link_id} className={`p-4 border rounded-lg ${link.status === "revoked" ? "opacity-60 bg-gray-50" : "bg-white hover:bg-gray-50"}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium">{link.recipient_name || "Unnamed"}</p>
                                  <Badge className={link.status === "active" ? "bg-green-500" : "bg-gray-400"}>
                                    {link.status}
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-500">{link.email}</p>
                                <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                                  <code className="bg-gray-100 px-2 py-0.5 rounded font-bold">{link.token}</code>
                                  {link.redirect_url && link.redirect_url !== "/me" && (
                                    <>
                                      <span>•</span>
                                      <span className="text-blue-600">→ {link.redirect_url}</span>
                                    </>
                                  )}
                                  <span>•</span>
                                  <span>Used: {link.use_count || 0}x</span>
                                </div>
                                {copiedLink === link.link_id && (
                                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs font-mono text-green-700">
                                    {`${window.location.origin}/auth/magic?token=${link.token}${link.redirect_url && link.redirect_url !== "/me" ? `&redirect=${link.redirect_url}` : ""}`}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleCopyLink(link)}>
                                  <Copy className="w-4 h-4" />
                                </Button>
                                {link.status === "active" && (
                                  <Button variant="outline" size="sm" onClick={() => handleRevokeLink(link.link_id)} className="text-amber-600">
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button variant="outline" size="sm" onClick={() => handleDeleteLink(link.link_id)} className="text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Preview Tokens Sub-tab */}
              <TabsContent value="preview-tokens" className="mt-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Eye className="w-5 h-5 text-blue-600" />
                          Preview Tokens
                        </CardTitle>
                        <CardDescription>Shareable preview links for demos</CardDescription>
                      </div>
                      <Button onClick={() => setShowPreviewTokenDialog(true)} variant="outline">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Preview Token
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {previewLinks.length === 0 && previewTokens.length === 0 ? (
                      <div className="py-8 text-center">
                        <Eye className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No preview tokens yet</p>
                        <p className="text-sm text-gray-500">Create shareable preview links for demos</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Preview Links from access_links */}
                        {previewLinks.map((link) => (
                          <div key={link.link_id} className={`p-4 border rounded-lg ${link.status === "revoked" ? "opacity-60 bg-gray-50" : "bg-white hover:bg-gray-50"}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium">{link.recipient_name || "Preview Link"}</p>
                                  <Badge className={link.status === "active" ? "bg-blue-500" : "bg-gray-400"}>
                                    {link.status}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                                  <code className="bg-gray-100 px-2 py-0.5 rounded">{link.token}</code>
                                  {link.redirect_url && (
                                    <>
                                      <span>•</span>
                                      <span className="text-blue-600">→ {link.redirect_url}</span>
                                    </>
                                  )}
                                </div>
                                {copiedLink === link.link_id && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs font-mono text-blue-700">
                                    {`${window.location.origin}/auth/magic?token=${link.token}${link.redirect_url ? `&redirect=${link.redirect_url}` : ""}`}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => handleCopyLink(link)}>
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleDeleteLink(link.link_id)} className="text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Preview Tokens from preview/tokens API */}
                        {previewTokens.map((token, idx) => (
                          <div key={token.token || idx} className="p-4 border rounded-lg bg-white hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium">{token.name || "Preview Token"}</p>
                                  <Badge className="bg-blue-500">active</Badge>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                                  <code className="bg-gray-100 px-2 py-0.5 rounded">{token.token}</code>
                                  {token.redirect_page && (
                                    <>
                                      <span>•</span>
                                      <span className="text-blue-600">→ {token.redirect_page}</span>
                                    </>
                                  )}
                                  {token.expires_at && (
                                    <>
                                      <span>•</span>
                                      <span>Expires: {new Date(token.expires_at).toLocaleDateString()}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => {
                                  navigator.clipboard.writeText(`${window.location.origin}/preview?token=${token.token}`);
                                  toast.success("Preview URL copied!");
                                }}>
                                  <Copy className="w-4 h-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleDeletePreviewToken(token.token)} className="text-red-500">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Create Link Dialog */}
          <Dialog open={showCreateLinkDialog} onOpenChange={setShowCreateLinkDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{newLink.is_super_admin ? "Create Admin Access" : "Create Magic Link"}</DialogTitle>
                <DialogDescription>Create a magic link to grant access</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={newLink.recipient_name}
                    onChange={(e) => setNewLink(prev => ({ ...prev, recipient_name: e.target.value }))}
                    placeholder="e.g., HTHF Event Access"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newLink.email}
                    onChange={(e) => setNewLink(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="admin@phygital.systems"
                  />
                </div>
                <div>
                  <Label>Redirect URL</Label>
                  <Input
                    value={newLink.redirect_url}
                    onChange={(e) => setNewLink(prev => ({ ...prev, redirect_url: e.target.value }))}
                    placeholder="/admin, /mua, /e/hthf-lafw-2026"
                  />
                  <p className="text-xs text-gray-500 mt-1">Where users go after authenticating</p>
                </div>
                <div>
                  <Label>Expiration</Label>
                  <select 
                    className="w-full p-2 border rounded-md"
                    value={newLink.expiration}
                    onChange={(e) => setNewLink(prev => ({ ...prev, expiration: e.target.value }))}
                  >
                    <option value="never">Never</option>
                    <option value="7_days">7 Days</option>
                    <option value="30_days">30 Days</option>
                    <option value="90_days">90 Days</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_super_admin_new"
                    checked={newLink.is_super_admin}
                    onChange={(e) => setNewLink(prev => ({ ...prev, is_super_admin: e.target.checked }))}
                    className="rounded"
                  />
                  <Label htmlFor="is_super_admin_new" className="cursor-pointer">Grant Super Admin access</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateLinkDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateLink} disabled={creatingLink}>
                  {creatingLink ? "Creating..." : "Create Link"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Invite Admin Dialog */}
          <Dialog open={showInviteAdminDialog} onOpenChange={setShowInviteAdminDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Admin</DialogTitle>
                <DialogDescription>Send an invitation to a team member</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newInvite.email}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="team@phygital.systems"
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <select 
                    className="w-full p-2 border rounded-md"
                    value={newInvite.role}
                    onChange={(e) => setNewInvite(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteAdminDialog(false)}>Cancel</Button>
                <Button onClick={handleInviteAdmin} disabled={creatingLink}>
                  {creatingLink ? "Sending..." : "Send Invite"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Preview Token Dialog */}
          <Dialog open={showPreviewTokenDialog} onOpenChange={setShowPreviewTokenDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Preview Token</DialogTitle>
                <DialogDescription>Create a shareable preview link</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={newPreviewToken.name}
                    onChange={(e) => setNewPreviewToken(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Investor Demo"
                  />
                </div>
                <div>
                  <Label>Redirect Page *</Label>
                  <select 
                    className="w-full p-2 border rounded-md"
                    value={newPreviewToken.redirect_page}
                    onChange={(e) => setNewPreviewToken(prev => ({ ...prev, redirect_page: e.target.value }))}
                  >
                    <option value="">Select a page...</option>
                    <option value="/e/hthf-lafw-2026">HTHF Event Page</option>
                    <option value="/mua">MUA Portal</option>
                    <option value="/hair">Hair Portal</option>
                    <option value="/admin">Admin Dashboard</option>
                    <option value="/intake/makeup">Intake Form - Makeup</option>
                  </select>
                </div>
                <div>
                  <Label>Expires In (hours, 0 = never)</Label>
                  <Input
                    type="number"
                    value={newPreviewToken.expires_hours}
                    onChange={(e) => setNewPreviewToken(prev => ({ ...prev, expires_hours: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPreviewTokenDialog(false)}>Cancel</Button>
                <Button onClick={handleCreatePreviewToken} disabled={creatingLink}>
                  {creatingLink ? "Creating..." : "Create Token"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <Card>
            <CardContent className="py-12 text-center">
              <Lock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">Security Settings</p>
              <p className="text-sm text-gray-500 mt-1">OTP settings, session management, audit logs</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="files" className="mt-6">
          <CoreFilesSection />
        </TabsContent>

        <TabsContent value="database" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Collections List */}
            <Card className="md:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Collections</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-96 overflow-y-auto">
                  {collections.map((coll) => (
                    <button
                      key={coll.name}
                      onClick={() => handleSelectCollection(coll)}
                      className={`w-full px-4 py-3 text-left border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
                        selectedCollection?.name === coll.name ? "bg-purple-50 border-l-4 border-l-purple-500" : ""
                      }`}
                    >
                      <p className="font-medium text-sm">{coll.display_name}</p>
                      <p className="text-xs text-gray-500">{coll.count} records</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Records Display */}
            <Card className="md:col-span-3">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm">
                      {selectedCollection ? selectedCollection.display_name : "Select a collection"}
                    </CardTitle>
                    {selectedCollection && (
                      <CardDescription>{recordsTotal} total records</CardDescription>
                    )}
                  </div>
                  {selectedCollection && (
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Search..."
                        value={dbSearch}
                        onChange={(e) => setDbSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && fetchRecords(selectedCollection.name, dbSearch)}
                        className="w-48 h-8 text-sm"
                      />
                      <Button size="sm" variant="outline" onClick={() => fetchRecords(selectedCollection.name, dbSearch)}>
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!selectedCollection ? (
                  <div className="py-12 text-center">
                    <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">Select a collection to view records</p>
                  </div>
                ) : dbLoading ? (
                  <div className="py-12 text-center">
                    <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                  </div>
                ) : records.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-500">No records found</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {records.map((record, idx) => {
                      const recordId = record.user_id || record.event_id || 
                                      record.sub_category_id || record.tier_id ||
                                      record.share_id || record.link_id || record._id;
                      return (
                        <div key={idx} className="p-3 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-xs text-gray-500 mb-1">{recordId}</p>
                              <p className="font-medium text-sm truncate">
                                {record.name || record.email || record.title || record.tier_id || "Record"}
                              </p>
                              {record.email && <p className="text-xs text-gray-500">{record.email}</p>}
                              {record.role && <Badge className="text-xs mt-1">{record.role}</Badge>}
                            </div>
                            <div className="flex items-center gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setEditingRecord(record);
                                  setEditedData(JSON.stringify(record, null, 2));
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRecord(recordId)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Edit Record Dialog */}
          <Dialog open={!!editingRecord} onOpenChange={(open) => !open && setEditingRecord(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Edit Record</DialogTitle>
                <DialogDescription>Edit the JSON data and save</DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Textarea
                  value={editedData}
                  onChange={(e) => setEditedData(e.target.value)}
                  className="font-mono text-xs h-96"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingRecord(null)}>Cancel</Button>
                <Button onClick={handleUpdateRecord}>Save Changes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// NDA Template Editor Component
function NDATemplateEditor() {
  const [ndaContent, setNdaContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchNDATemplate();
  }, []);

  const fetchNDATemplate = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/nda/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNdaContent(data.content || getDefaultNDA());
      } else {
        setNdaContent(getDefaultNDA());
      }
    } catch (err) {
      setNdaContent(getDefaultNDA());
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/nda/template`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: ndaContent })
      });
      if (res.ok) {
        toast.success("NDA template saved!");
      } else {
        toast.error("Failed to save");
      }
    } catch (err) {
      toast.error("Error saving template");
    } finally {
      setSaving(false);
    }
  };

  const getDefaultNDA = () => `<h2>NON-DISCLOSURE AGREEMENT</h2>
<p>This Non-Disclosure Agreement ("Agreement") is entered into as of the date of electronic signature...</p>
<h3>1. CONFIDENTIAL INFORMATION</h3>
<p>For the purposes of this Agreement, "Confidential Information" means...</p>
<h3>2. NON-DISCLOSURE OBLIGATIONS</h3>
<p>The Receiving Party agrees to hold all Confidential Information in strict confidence...</p>`;

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>NDA Template Editor</CardTitle>
            <CardDescription>Edit the NDA template for pilot partners (supports HTML)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="w-4 h-4 mr-2" />
              {showPreview ? "Edit" : "Preview"}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
              Save Template
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {showPreview ? (
          <div 
            className="prose max-w-none p-6 border rounded-lg bg-white min-h-[400px]"
            dangerouslySetInnerHTML={{ __html: ndaContent }}
          />
        ) : (
          <textarea
            value={ndaContent}
            onChange={(e) => setNdaContent(e.target.value)}
            className="w-full h-[400px] p-4 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Enter NDA content (HTML supported)..."
          />
        )}
      </CardContent>
    </Card>
  );
}

// Core Files Editor Component - Editable .md files
function CoreFilesEditor() {
  const [files, setFiles] = useState([
    { id: "prd", name: "PRD.md", label: "Product Requirements" },
    { id: "roadmap", name: "ROADMAP.md", label: "Roadmap" },
    { id: "changelog", name: "CHANGELOG.md", label: "Changelog" },
    { id: "current_state", name: "CURRENT_STATE.md", label: "Current State (All Screens & Flows)" },
    { id: "all_defaults", name: "ALL_DEFAULTS_REFERENCE.md", label: "All Defaults Reference" },
    { id: "next_fork", name: "NEXT_FORK_INSTRUCTIONS.md", label: "Next Fork Instructions" },
    { id: "master_plan", name: "PHYGITAL_MASTER_PLAN_v7_COMPLETE.md", label: "Master Plan v7" },
    { id: "beehiiv", name: "BEEHIIV.md", label: "Beehiiv Integration (84 Fields)" },
    { id: "logo_map", name: "LOGO_MAP.md", label: "Logo Map" },
    { id: "test_credentials", name: "test_credentials.md", label: "Test Credentials" },
    { id: "design", name: "design_guidelines.md", label: "Design Guidelines" },
    { id: "cloudinary_setup", name: "CLOUDINARY_SETUP.md", label: "Cloudinary Setup" },
  ]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchFileContent = async (fileId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/core-files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFileContent(data.content || "");
        setSelectedFile(fileId);
      } else {
        toast.error("Failed to load file");
      }
    } catch (err) {
      toast.error("Error loading file");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedFile) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/core-files/${selectedFile}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: fileContent })
      });
      if (res.ok) {
        toast.success("File saved!");
      } else {
        toast.error("Failed to save");
      }
    } catch (err) {
      toast.error("Error saving file");
    } finally {
      setSaving(false);
    }
  };

  // Simple markdown to HTML converter
  const markdownToHtml = (md) => {
    return md
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      .replace(/^\- (.*$)/gim, '<li>$1</li>')
      .replace(/^\* (.*$)/gim, '<li>$1</li>')
      .replace(/\n/gim, '<br>');
  };

  return (
    <div className="space-y-4">
      {/* File Selector */}
      <div className="flex flex-wrap gap-2">
        {files.map(file => (
          <Button
            key={file.id}
            variant={selectedFile === file.id ? "default" : "outline"}
            onClick={() => fetchFileContent(file.id)}
            className={selectedFile === file.id ? "bg-purple-600 hover:bg-purple-700" : ""}
          >
            <FileText className="w-4 h-4 mr-2" />
            {file.label}
          </Button>
        ))}
      </div>

      {/* Editor */}
      {selectedFile && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{files.find(f => f.id === selectedFile)?.label}</CardTitle>
                <CardDescription>{files.find(f => f.id === selectedFile)?.name}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`${API}/admin/core-files/${selectedFile}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  data-testid="download-file-btn"
                >
                  <ExternalLink className="w-3 h-3" /> Download
                </a>
                <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? "Edit" : "Preview"}
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                  Save
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : showPreview ? (
              <div 
                className="prose max-w-none p-6 border rounded-lg bg-white min-h-[400px] overflow-auto"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(fileContent) }}
              />
            ) : (
              <textarea
                value={fileContent}
                onChange={(e) => setFileContent(e.target.value)}
                className="w-full h-[500px] p-4 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="File content..."
              />
            )}
          </CardContent>
        </Card>
      )}

      {!selectedFile && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="font-medium">Select a file to edit</p>
            <p className="text-sm text-gray-500 mt-1">PRD, Changelog, Roadmap, Design Guidelines</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ============================================================================
// BEEHIIV SYNC SECTION - Full Dashboard with All Data Fields
// ============================================================================
function BeehiivSyncSection() {
  const [status, setStatus] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDirection, setSyncDirection] = useState("both");
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch status and subscribers
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch status
      const statusRes = await fetch(`${API}/admin/beehiiv/status`, { headers });
      if (statusRes.ok) {
        setStatus(await statusRes.json());
      }
      
      // Fetch subscribers
      const subsRes = await fetch(`${API}/admin/beehiiv/subscribers?limit=100`, { headers });
      if (subsRes.ok) {
        const data = await subsRes.json();
        setSubscribers(data.subscribers || []);
      }
    } catch (err) {
      console.error("Failed to fetch Beehiiv data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/beehiiv/sync`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ direction: syncDirection })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Synced: ${data.synced_to_beehiiv} to Beehiiv, ${data.synced_from_beehiiv} from Beehiiv`);
        fetchData(); // Refresh data
      } else {
        toast.error("Sync failed");
      }
    } catch (err) {
      toast.error("Error syncing");
    } finally {
      setSyncing(false);
    }
  };

  const filteredSubscribers = subscribers.filter(sub => 
    (sub.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (sub.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Beehiiv Sync Dashboard</h1>
          <p className="text-gray-500">Full bidirectional sync with all user data fields</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="text-xl font-bold">{status?.configured ? "Connected" : "Not Configured"}</p>
              </div>
              <div className={`w-3 h-3 rounded-full ${status?.configured ? "bg-green-500" : "bg-red-500"}`} />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Beehiiv Subscribers</p>
            <p className="text-2xl font-bold">{status?.subscriber_count || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm text-green-700">Linked to Phygital</p>
            <p className="text-2xl font-bold text-green-800">{subscribers.filter(s => s.is_linked).length}</p>
          </CardContent>
        </Card>
        
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <p className="text-sm text-orange-700">Not Linked</p>
            <p className="text-2xl font-bold text-orange-800">{subscribers.filter(s => !s.is_linked).length}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Last Sync</p>
            <p className="text-sm font-medium">
              {status?.last_sync 
                ? new Date(status.last_sync).toLocaleString() 
                : "Never"
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Sync Controls */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Manual Sync</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Select value={syncDirection} onValueChange={setSyncDirection}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Bidirectional (Both)</SelectItem>
                <SelectItem value="to_beehiiv">To Beehiiv Only</SelectItem>
                <SelectItem value="from_beehiiv">From Beehiiv Only</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              onClick={handleSync} 
              disabled={syncing}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {syncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">What gets synced:</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
              <div>• Name, Email, Phone</div>
              <div>• Dietary Restrictions</div>
              <div>• Allergies</div>
              <div>• Accessibility Needs</div>
              <div>• Interests</div>
              <div>• Role & Tier</div>
              <div>• Event Attendance</div>
              <div>• Social Handles</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscribers Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Synced Subscribers</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search subscribers..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phygital Link</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscribers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      {searchTerm ? "No matching subscribers" : "No subscribers synced yet. Run a sync to populate."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubscribers.map((sub, idx) => (
                    <TableRow key={sub.email || idx} className={sub.is_linked ? "" : "bg-orange-50/50"}>
                      <TableCell className="font-medium">{sub.email}</TableCell>
                      <TableCell>
                        {sub.phygital_user?.name || sub.name || sub.custom_fields?.name || "-"}
                      </TableCell>
                      <TableCell>
                        {sub.is_linked ? (
                          <Badge variant="default" className="bg-green-600">
                            <Check className="w-3 h-3 mr-1" />
                            Linked
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-orange-600 border-orange-300">
                            Not Linked
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={sub.status === "active" ? "default" : "secondary"}>
                          {sub.status || "unknown"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                          {(sub.tags || []).slice(0, 2).map((tag, tidx) => (
                            <Badge key={tidx} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {(sub.tags || []).length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{sub.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedSubscriber(sub)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Subscriber Detail Modal */}
      {selectedSubscriber && (
        <Dialog open={!!selectedSubscriber} onOpenChange={() => setSelectedSubscriber(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Subscriber Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Phygital Link Status */}
              {selectedSubscriber.is_linked && selectedSubscriber.phygital_user && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                      {(selectedSubscriber.phygital_user.name || "U")[0].toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-green-900">Linked to Phygital Account</p>
                      <p className="text-sm text-green-700">{selectedSubscriber.phygital_user.name}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{selectedSubscriber.phygital_user.role}</Badge>
                        {selectedSubscriber.phygital_user.subscription_tier && (
                          <Badge variant="outline" className="text-xs">{selectedSubscriber.phygital_user.subscription_tier}</Badge>
                        )}
                      </div>
                    </div>
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              )}
              
              {!selectedSubscriber.is_linked && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="font-medium text-orange-900">Not Linked to Phygital</p>
                  <p className="text-sm text-orange-700">This subscriber hasn't created a Phygital account yet.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Email</p>
                  <p className="font-medium">{selectedSubscriber.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <Badge variant={selectedSubscriber.status === "active" ? "default" : "secondary"}>
                    {selectedSubscriber.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Created</p>
                  <p>{selectedSubscriber.created ? new Date(selectedSubscriber.created).toLocaleDateString() : "-"}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Last Updated</p>
                  <p>{selectedSubscriber.updated_at ? new Date(selectedSubscriber.updated_at).toLocaleDateString() : "-"}</p>
                </div>
              </div>
              
              {/* Tags */}
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Tags ({(selectedSubscriber.tags || []).length})</p>
                <div className="flex flex-wrap gap-2">
                  {(selectedSubscriber.tags || []).map((tag, idx) => (
                    <Badge key={idx} variant="outline">{tag}</Badge>
                  ))}
                  {(selectedSubscriber.tags || []).length === 0 && (
                    <span className="text-gray-400 text-sm">No tags</span>
                  )}
                </div>
              </div>
              
              {/* All Custom Fields */}
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">
                  All Data Fields ({Object.keys(selectedSubscriber.custom_fields || {}).length})
                </p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 max-h-[200px] overflow-y-auto">
                  {Object.entries(selectedSubscriber.custom_fields || {}).length === 0 ? (
                    <span className="text-gray-400 text-sm">No custom fields</span>
                  ) : (
                    Object.entries(selectedSubscriber.custom_fields || {}).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-1 border-b border-gray-200 last:border-0">
                        <span className="text-sm font-medium text-gray-600">{key}</span>
                        <span className="text-sm text-gray-900 max-w-[300px] truncate">{String(value)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// ============================================================================
// ACCESS CONTROL SECTION - Magic Links Management
// ============================================================================
function AccessControlSection() {
  const [activeTab, setActiveTab] = useState("magic-links");
  const [accessLinks, setAccessLinks] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showPreviewTokenDialog, setShowPreviewTokenDialog] = useState(false);
  const [showDuplicateTokenDialog, setShowDuplicateTokenDialog] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [categories, setCategories] = useState([]);
  const [previewTokens, setPreviewTokens] = useState([]);
  const [newPreviewToken, setNewPreviewToken] = useState({
    name: "",
    token: "",
    expires_hours: 0,
    redirect_page: ""
  });
  const [duplicateToken, setDuplicateToken] = useState({
    name: "",
    token: "",
    expires_hours: 0
  });
  const [newLink, setNewLink] = useState({
    recipient_name: "",
    email: "",
    expiration: "never",
    portal_access: [],
    is_super_admin: false,
    redirect_url: ""
  });
  const [newInvite, setNewInvite] = useState({ email: "", role: "admin" });
  const [creating, setCreating] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [selectedPortals, setSelectedPortals] = useState([]);

  useEffect(() => {
    fetchAccessLinks();
    fetchCategories();
    fetchAdminUsers();
    fetchPreviewTokens();
  }, []);

  const fetchPreviewTokens = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/preview/tokens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewTokens(data || []);
      }
    } catch (err) {
      console.error("Error fetching preview tokens:", err);
    }
  };

  const handleCreatePreviewToken = async () => {
    if (!newPreviewToken.name) {
      toast.error("Token name is required");
      return;
    }
    if (!newPreviewToken.redirect_page) {
      toast.error("Please select a page to redirect to");
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/preview/tokens`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPreviewToken)
      });
      
      if (res.ok) {
        const data = await res.json();
        const previewUrl = `${window.location.origin}${data.preview_url}`;
        navigator.clipboard.writeText(previewUrl);
        toast.success("Preview token created and URL copied!");
        setShowPreviewTokenDialog(false);
        setNewPreviewToken({ name: "", token: "", expires_hours: 0, redirect_page: "" });
        fetchPreviewTokens();
      } else {
        const errData = await res.json().catch(() => ({}));
        console.error("Failed to create preview token:", errData);
        toast.error(errData.detail || "Failed to create preview token");
      }
    } catch (err) {
      toast.error("Error creating preview token");
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePreviewToken = async (tokenValue) => {
    if (!confirm("Are you sure you want to delete this preview token?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/preview/tokens/${tokenValue}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Preview token deleted");
        fetchPreviewTokens();
      } else {
        toast.error("Failed to delete preview token");
      }
    } catch (err) {
      toast.error("Error deleting preview token");
    }
  };

  const handleCopyPreviewUrl = async (tokenValue) => {
    // Find the token to check for redirect_page
    const token = previewTokens.find(pt => pt.token === tokenValue);
    let previewUrl;
    
    if (token?.redirect_page) {
      previewUrl = `${window.location.origin}${token.redirect_page}?preview=${tokenValue}`;
    } else {
      previewUrl = `${window.location.origin}/preview/${tokenValue}`;
    }
    
    try {
      await navigator.clipboard.writeText(previewUrl);
      toast.success("Preview URL copied to clipboard!");
    } catch (err) {
      toast.info("Select and copy the URL");
    }
  };

  const handleOpenDuplicateDialog = (sourceToken, isBuiltin = false) => {
    setDuplicateSource({ token: sourceToken, isBuiltin });
    setDuplicateToken({
      name: `${sourceToken} Copy`,
      token: "",
      expires_hours: 0
    });
    setShowDuplicateTokenDialog(true);
  };

  const handleDuplicateToken = async () => {
    if (!duplicateToken.name || !duplicateSource) {
      toast.error("Token name is required");
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/preview/tokens/duplicate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          source_token: duplicateSource.token,
          name: duplicateToken.name,
          token: duplicateToken.token || undefined,
          expires_hours: duplicateToken.expires_hours
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const previewUrl = `${window.location.origin}${data.preview_url}`;
        navigator.clipboard.writeText(previewUrl);
        toast.success(`Token duplicated from "${duplicateSource.token}" and URL copied!`);
        setShowDuplicateTokenDialog(false);
        setDuplicateSource(null);
        setDuplicateToken({ name: "", token: "", expires_hours: 0 });
        fetchPreviewTokens();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to duplicate token");
      }
    } catch (err) {
      toast.error("Error duplicating preview token");
    } finally {
      setCreating(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-control/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdminUsers(data.users || []);
      }
    } catch (err) {
      console.error("Error fetching admin users:", err);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-control/users/${userId}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        toast.success("Role updated");
        fetchAdminUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to update role");
      }
    } catch (err) {
      toast.error("Error updating role");
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!confirm("Remove admin access from this user?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-control/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Admin access removed");
        fetchAdminUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to remove admin");
      }
    } catch (err) {
      toast.error("Error removing admin");
    }
  };

  const handleInviteAdmin = async () => {
    if (!newInvite.email) {
      toast.error("Email is required");
      return;
    }
    
    setCreating(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-control/invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newInvite)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.invite_link) {
          const fullUrl = `${window.location.origin}${data.invite_link}`;
          navigator.clipboard.writeText(fullUrl);
          toast.success("Admin invite created and copied!");
        } else {
          toast.success(data.message || "User promoted to admin");
        }
        setShowInviteDialog(false);
        setNewInvite({ email: "", role: "admin" });
        fetchAdminUsers();
      } else {
        toast.error("Failed to invite admin");
      }
    } catch (err) {
      toast.error("Error inviting admin");
    } finally {
      setCreating(false);
    }
  };

  const fetchAccessLinks = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccessLinks(data.access_links || []);
      }
    } catch (err) {
      console.error("Error fetching access links:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const handleCreateLink = async () => {
    if (!newLink.email) {
      toast.error("Email is required");
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newLink,
          portal_access: selectedPortals
        })
      });

      if (res.ok) {
        const data = await res.json();
        const fullUrl = `${window.location.origin}${data.magic_link_url}`;
        navigator.clipboard.writeText(fullUrl);
        toast.success("Magic link created and copied!");
        setShowCreateDialog(false);
        setNewLink({ recipient_name: "", email: "", expiration: "never", portal_access: [], is_super_admin: false, redirect_url: "" });
        setSelectedPortals([]);
        fetchAccessLinks();
      } else {
        toast.error("Failed to create link");
      }
    } catch (err) {
      toast.error("Error creating link");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async (link) => {
    // Include redirect URL in the copied link if it exists
    let fullUrl = `${window.location.origin}/auth/magic?token=${link.token}`;
    if (link.redirect_url && link.redirect_url !== "/me") {
      fullUrl += `&redirect=${link.redirect_url}`;
    }
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      // Fallback - show the link anyway
      toast.info("Select and copy the link below");
    }
    setCopiedLink(link.link_id);
    // Keep showing for 5 seconds so user can manually copy if needed
    setTimeout(() => setCopiedLink(null), 5000);
  };

  const handleDeleteLink = async (linkId) => {
    if (!confirm("Are you sure you want to delete this access link?")) return;

    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links/${linkId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Access link deleted");
        fetchAccessLinks();
      }
    } catch (err) {
      toast.error("Failed to delete link");
    }
  };

  const handleToggleStatus = async (link) => {
    const newStatus = link.status === "active" ? "revoked" : "active";
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links/${link.link_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Link ${newStatus}`);
        fetchAccessLinks();
      }
    } catch (err) {
      toast.error("Failed to update link");
    }
  };

  const togglePortalSelection = (subCatId) => {
    setSelectedPortals(prev => 
      prev.includes(subCatId) 
        ? prev.filter(id => id !== subCatId)
        : [...prev, subCatId]
    );
  };

  const selectAllPortals = () => {
    const allIds = categories.flatMap(cat => 
      cat.sub_categories?.map(sub => sub.sub_category_id) || []
    );
    setSelectedPortals(allIds);
  };

  const clearPortalSelection = () => {
    setSelectedPortals([]);
  };

  const expirationOptions = [
    { value: "1_day", label: "1 day" },
    { value: "7_days", label: "7 days" },
    { value: "30_days", label: "30 days" },
    { value: "90_days", label: "90 days" },
    { value: "never", label: "Never expires" }
  ];

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">Access Control</h1>
          <p className="text-sm text-gray-500">Manage magic links and admin permissions</p>
        </div>
        <div className="flex gap-2">
          {activeTab === "admin-users" && (
            <Button onClick={() => setShowInviteDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Invite Admin
            </Button>
          )}
          {activeTab === "magic-links" && (
            <Button onClick={() => setShowCreateDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Magic Link
            </Button>
          )}
          {activeTab === "preview-tokens" && (
            <Button onClick={() => setShowPreviewTokenDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Preview Token
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="magic-links">Magic Links</TabsTrigger>
          <TabsTrigger value="admin-users">Admin Users</TabsTrigger>
          <TabsTrigger value="preview-tokens">Preview Tokens</TabsTrigger>
        </TabsList>

        {/* Preview Tokens Tab */}
        <TabsContent value="preview-tokens">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">What are Preview Tokens?</p>
                <p className="text-sm text-blue-700 mt-1">
                  Preview tokens allow you to share public-facing pages (landing pages, demo pages) with others 
                  without requiring them to create an account. They cannot access protected areas like /me.
                </p>
              </div>
            </div>
          </div>
          
          {/* Built-in Tokens */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-600 uppercase mb-3">Built-in Tokens (Always Active)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {['demo2026', 'investor2026', 'partner2026', 'preview', 'showme'].map((tokenValue) => (
                <Card key={tokenValue} className="border-gray-200 bg-gray-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Built-in
                        </Badge>
                        <code className="text-sm font-mono text-gray-700">{tokenValue}</code>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDuplicateDialog(tokenValue, true)}
                          title="Duplicate token"
                        >
                          <Files className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyPreviewUrl(tokenValue)}
                          title="Copy URL"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 truncate">
                      {window.location.origin}/preview/{tokenValue}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          {/* Custom Tokens */}
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase mb-3">Custom Tokens</h3>
            {previewTokens.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Globe className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-700">No custom preview tokens</p>
                  <p className="text-sm text-gray-500 mt-1">Create custom tokens for specific audiences</p>
                  <Button 
                    onClick={() => setShowPreviewTokenDialog(true)} 
                    className="mt-4 bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Custom Token
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {previewTokens.map((pt) => (
                  <Card key={pt.token} data-testid={`preview-token-${pt.token}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{pt.name}</p>
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              Custom
                            </Badge>
                            {pt.expires_hours > 0 && (
                              <Badge variant="outline" className="text-amber-600 border-amber-200">
                                <Clock className="w-3 h-3 mr-1" />
                                {pt.expires_hours}h
                              </Badge>
                            )}
                            {pt.redirect_page && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                → {pt.redirect_page}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 font-mono truncate">
                            {pt.redirect_page ? `${pt.redirect_page}?preview=${pt.token}` : `/preview/${pt.token}`}
                          </p>
                          {pt.created_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              Created: {new Date(pt.created_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyPreviewUrl(pt.token)}
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copy URL
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDuplicateDialog(pt.token, false)}
                            title="Duplicate token"
                          >
                            <Files className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePreviewToken(pt.token)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Admin Users Tab */}
        <TabsContent value="admin-users">
          {adminUsers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">No admin users found</p>
                <p className="text-sm text-gray-500 mt-1">Invite admins to help manage the platform</p>
                <Button onClick={() => setShowInviteDialog(true)} className="mt-4 bg-purple-600 hover:bg-purple-700">
                  Invite First Admin
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {adminUsers.map((user) => (
                <Card key={user.user_id} data-testid={`admin-user-${user.user_id}`}>
                  <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{user.name || "Unnamed"}</p>
                            <Badge 
                              className={user.role === "super_admin" ? "bg-purple-500" : "bg-blue-500"}
                            >
                              {user.role === "super_admin" ? "Super Admin" : "Admin"}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Joined: {user.created_at ? new Date(user.created_at).toLocaleDateString() : "Unknown"}
                            {user.last_login && ` • Last login: ${new Date(user.last_login).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem("session_token");
                              // Generate a magic link for this admin
                              const res = await fetch(`${API}/admin/generate-magic-link`, {
                                method: "POST",
                                headers: { 
                                  Authorization: `Bearer ${token}`,
                                  "Content-Type": "application/json"
                                },
                                body: JSON.stringify({ 
                                  email: user.email,
                                  user_id: user.user_id,
                                  expires_hours: 168 // 7 days
                                })
                              });
                              if (res.ok) {
                                const data = await res.json();
                                const baseUrl = window.location.origin;
                                const magicLink = `${baseUrl}/auth/magic?token=${data.token}`;
                                await navigator.clipboard.writeText(magicLink);
                                toast.success(`Magic link copied for ${user.name || user.email}!`);
                              } else {
                                toast.error("Failed to generate magic link");
                              }
                            } catch (err) {
                              toast.error("Error generating link");
                            }
                          }}
                          className="text-purple-600 hover:text-purple-700"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                        <Select 
                          value={user.role} 
                          onValueChange={(v) => handleUpdateRole(user.user_id, v)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="super_admin">Super Admin</SelectItem>
                            <SelectItem value="user">Regular User</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveAdmin(user.user_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Magic Links Tab */}
        <TabsContent value="magic-links">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : accessLinks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Link2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">No access links yet</p>
                <p className="text-sm text-gray-500 mt-1">Create magic links to grant access to specific portals</p>
                <Button onClick={() => setShowCreateDialog(true)} className="mt-4 bg-purple-600 hover:bg-purple-700">
                  Create First Link
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {accessLinks.map((link) => (
                <Card key={link.link_id} className={link.status === "revoked" ? "opacity-60" : ""}>
                  <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-medium">{link.recipient_name || "Unnamed"}</p>
                          <Badge 
                            variant={link.status === "active" ? "default" : "secondary"}
                            className={link.status === "active" ? "bg-green-500" : "bg-gray-400"}
                          >
                            {link.status}
                          </Badge>
                          {link.is_super_admin && (
                            <Badge variant="outline" className="border-purple-500 text-purple-600">
                              Super Admin
                            </Badge>
                          )}
                          {link.is_expired && (
                            <Badge variant="destructive">Expired</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{link.email}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-400">
                          <span>Expires: {link.expiration_type === "never" ? "Never" : new Date(link.expires_at).toLocaleDateString()}</span>
                          <span>•</span>
                          <span className={link.use_count > 0 ? "text-green-600 font-medium" : ""}>
                            Used: {link.use_count || 0} times
                          </span>
                          {link.last_used_at && (
                            <>
                              <span>•</span>
                              <span>Last: {new Date(link.last_used_at).toLocaleDateString()}</span>
                            </>
                          )}
                          {link.portal_access?.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{link.portal_access.length} portal{link.portal_access.length !== 1 ? "s" : ""}</span>
                            </>
                          )}
                          {link.redirect_url && link.redirect_url !== "/me" && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600">→ {link.redirect_url}</span>
                            </>
                          )}
                        </div>
                        {copiedLink === link.link_id && (
                          <div className="mt-3 p-2 bg-gray-50 rounded border text-xs font-mono break-all">
                            {`${window.location.origin}/auth/magic?token=${link.token}${link.redirect_url && link.redirect_url !== "/me" ? `&redirect=${link.redirect_url}` : ""}`}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyLink(link)}
                          title="Copy link"
                        >
                          {copiedLink === link.link_id ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleStatus(link)}
                        >
                          {link.status === "active" ? (
                            <Lock className="w-4 h-4" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteLink(link.link_id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invite Admin Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite Admin</DialogTitle>
            <DialogDescription>
              Add a new admin user or promote an existing user
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={newInvite.email}
                onChange={(e) => setNewInvite(prev => ({ ...prev, email: e.target.value }))}
                placeholder="admin@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newInvite.role} onValueChange={(v) => setNewInvite(prev => ({ ...prev, role: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Super Admins can manage other admins and all platform settings
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button onClick={handleInviteAdmin} disabled={creating}>
              {creating && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Magic Link</DialogTitle>
            <DialogDescription>
              Generate a magic link to grant access to specific portals
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Recipient Name</Label>
              <Input
                value={newLink.recipient_name}
                onChange={(e) => setNewLink(prev => ({ ...prev, recipient_name: e.target.value }))}
                placeholder="e.g. John Smith"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={newLink.email}
                onChange={(e) => setNewLink(prev => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. john@example.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Link Expiration</Label>
              <select
                value={newLink.expiration}
                onChange={(e) => setNewLink(prev => ({ ...prev, expiration: e.target.value }))}
                className="mt-1 w-full h-10 px-3 border rounded-md bg-white"
              >
                {expirationOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label>Redirect Page (Optional)</Label>
              <select
                value={newLink.redirect_url}
                onChange={(e) => setNewLink(prev => ({ ...prev, redirect_url: e.target.value }))}
                className="mt-1 w-full h-10 px-3 border rounded-md bg-white"
              >
                <option value="">Default (/me - Profile)</option>
                <option value="/home">Home Page</option>
                <option value="/wellness">Wellness Page</option>
                <option value="/hair">Hair Page</option>
                <option value="/nail">Nail Page</option>
                <option value="/mua">MUA Page</option>
                <option value="/events">Events Page</option>
                <option value="/culinary">Culinary Page</option>
                <option value="/travel">Travel Page</option>
                <option value="/instructor">Instructor Page</option>
                <option value="/dispensaries">Dispensaries Page</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Where the user lands after clicking the link</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Portal Access</Label>
                <div className="flex gap-2 text-xs">
                  <button 
                    type="button"
                    onClick={selectAllPortals}
                    className="text-purple-600 hover:underline"
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button 
                    type="button"
                    onClick={clearPortalSelection}
                    className="text-gray-500 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {categories.map((cat) => (
                  <div key={cat.type} className="p-2 border-b last:border-b-0">
                    <p className="text-xs font-semibold text-gray-400 uppercase mb-2">{cat.type}</p>
                    <div className="space-y-1">
                      {cat.sub_categories?.map((sub) => (
                        <label
                          key={sub.sub_category_id}
                          className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPortals.includes(sub.sub_category_id)}
                            onChange={() => togglePortalSelection(sub.sub_category_id)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <span className="text-sm">{sub.name}</span>
                        </label>
                      ))}
                      {(!cat.sub_categories || cat.sub_categories.length === 0) && (
                        <p className="text-xs text-gray-400 italic p-1">No portals in this category</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 border rounded-lg bg-purple-50">
              <input
                type="checkbox"
                id="super_admin"
                checked={newLink.is_super_admin}
                onChange={(e) => setNewLink(prev => ({ ...prev, is_super_admin: e.target.checked }))}
                className="w-4 h-4"
              />
              <div>
                <Label htmlFor="super_admin" className="cursor-pointer">Grant Super Admin Access</Label>
                <p className="text-xs text-gray-500">Full access to all admin features and portals</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateLink} 
              disabled={creating || !newLink.email}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creating ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Create & Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Token Creation Dialog */}
      <Dialog open={showPreviewTokenDialog} onOpenChange={setShowPreviewTokenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Preview Token</DialogTitle>
            <DialogDescription>
              Create a shareable link for viewing public pages without authentication
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Token Name *</Label>
              <Input
                value={newPreviewToken.name}
                onChange={(e) => setNewPreviewToken(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. VIP Client Preview"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                A friendly name to identify this token
              </p>
            </div>

            <div>
              <Label>Custom Token (optional)</Label>
              <Input
                value={newPreviewToken.token}
                onChange={(e) => setNewPreviewToken(prev => ({ ...prev, token: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                placeholder="e.g. vip-client"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to auto-generate. Will be used in URL: /preview/[token]
              </p>
            </div>

            <div>
              <Label>Expiration (hours)</Label>
              <Input
                type="number"
                value={newPreviewToken.expires_hours}
                onChange={(e) => setNewPreviewToken(prev => ({ ...prev, expires_hours: parseInt(e.target.value) || 0 }))}
                placeholder="0"
                min="0"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set to 0 for never expires
              </p>
            </div>

            <div>
              <Label>Redirect to Page *</Label>
              <select
                value={newPreviewToken.redirect_page}
                onChange={(e) => setNewPreviewToken(prev => ({ ...prev, redirect_page: e.target.value }))}
                className="mt-1 w-full h-10 px-3 border rounded-md bg-white"
              >
                <option value="">Select a page...</option>
                <option value="/home">Home Page</option>
                <option value="/wellness">Wellness Page</option>
                <option value="/hair">Hair Page</option>
                <option value="/nail">Nail Page</option>
                <option value="/mua">MUA Page</option>
                <option value="/events">Events Page</option>
                <option value="/culinary">Culinary Page</option>
                <option value="/travel">Travel Page</option>
                <option value="/instructor">Instructor Page</option>
                <option value="/dispensaries">Dispensaries Page</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Where the link takes visitors directly
              </p>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-4 h-4" />
                <p className="text-sm font-medium">What they can access:</p>
              </div>
              <ul className="text-xs text-amber-700 mt-2 space-y-1 ml-6">
                <li>• Home page, landing pages, sales pages</li>
                <li>• Demo pages and public content</li>
                <li className="text-red-600">• Cannot access /me, dashboards, or private portals</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewTokenDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePreviewToken} 
              disabled={creating || !newPreviewToken.name}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creating ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Create & Copy URL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Token Dialog */}
      <Dialog open={showDuplicateTokenDialog} onOpenChange={setShowDuplicateTokenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicate Preview Token</DialogTitle>
            <DialogDescription>
              Create a new token based on "{duplicateSource?.token}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Files className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Duplicating from:</span>
                <code className="text-sm font-mono bg-white px-2 py-0.5 rounded border">
                  {duplicateSource?.token}
                </code>
                {duplicateSource?.isBuiltin && (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                    Built-in
                  </Badge>
                )}
              </div>
            </div>

            <div>
              <Label>New Token Name *</Label>
              <Input
                value={duplicateToken.name}
                onChange={(e) => setDuplicateToken(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. VIP Client Preview"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Custom Token URL (optional)</Label>
              <Input
                value={duplicateToken.token}
                onChange={(e) => setDuplicateToken(prev => ({ ...prev, token: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                placeholder="e.g. vip-client"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to auto-generate
              </p>
            </div>

            <div>
              <Label>Expiration (hours)</Label>
              <Input
                type="number"
                value={duplicateToken.expires_hours}
                onChange={(e) => setDuplicateToken(prev => ({ ...prev, expires_hours: parseInt(e.target.value) || 0 }))}
                placeholder="0"
                min="0"
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Set to 0 for never expires. Useful for time-limited campaigns.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateTokenDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDuplicateToken} 
              disabled={creating || !duplicateToken.name}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creating ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Files className="w-4 h-4 mr-2" />
              )}
              Duplicate & Copy URL
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================================
// NDA & INTAKE ACTIVITY SECTION
// ============================================================================
function NDAIntakeActivitySection() {
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState({ nda_activity: [], intake_activity: [], stats: {} });
  const [activeTab, setActiveTab] = useState("nda");

  useEffect(() => {
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/activity/nda-intake?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActivity(data);
      }
    } catch (err) {
      console.error("Failed to fetch activity:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">NDA & Intake Activity</h1>
        <p className="text-gray-500 mt-1">Track NDA signatures and intake form submissions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{activity.stats?.total_nda_signatures || 0}</p>
                <p className="text-sm text-gray-500">NDA Signatures</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-3xl font-bold">{activity.stats?.total_pilot_intakes || 0}</p>
                <p className="text-sm text-gray-500">Pilot Intakes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="nda" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            NDA Signatures ({activity.nda_activity?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="intake" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Pilot Intakes ({activity.intake_activity?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="nda">
          <Card>
            <CardContent className="p-0">
              {activity.nda_activity?.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No NDA signatures yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {activity.nda_activity.map((item, idx) => (
                    <div key={item.id || idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-medium">
                          {(item.name || "N")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{item.name || "Unknown"}</p>
                          <p className="text-sm text-gray-500">{item.email}</p>
                          {item.company && (
                            <p className="text-xs text-gray-400">{item.company}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem("admin_token") || localStorage.getItem("session_token");
                              const res = await fetch(`${API}/admin/nda/${item.id}/download`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (res.ok) {
                                // Backend now returns a proper PDF
                                const blob = await res.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                const dateStr = item.signed_at ? new Date(item.signed_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
                                a.download = `NDA_${(item.name || 'document').replace(/\s+/g, '_')}_${dateStr}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                                toast.success("NDA downloaded as PDF");
                              } else {
                                toast.error("Failed to download NDA");
                              }
                            } catch (err) {
                              toast.error("Error downloading NDA");
                            }
                          }}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          PDF
                        </Button>
                        <div className="text-right">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            Signed
                          </Badge>
                          <p className="text-xs text-gray-400 mt-1">
                            {item.signed_at ? new Date(item.signed_at).toLocaleDateString() : "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="intake">
          <Card>
            <CardContent className="p-0">
              {activity.intake_activity?.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No pilot intakes yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {activity.intake_activity.map((item, idx) => (
                    <IntakeActivityItem key={item.id || idx} item={item} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-4 text-center">
        <Button variant="outline" onClick={fetchActivity}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
    </div>
  );
}

// Intake Activity Item with expandable detail view
function IntakeActivityItem({ item }) {
  const [expanded, setExpanded] = useState(false);
  const responses = item.all_responses || item.responses || {};
  const hasResponses = Object.keys(responses).length > 0;
  
  // Format field name for display
  const formatFieldName = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };
  
  // Format field value for display
  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value) || '-';
  };
  
  return (
    <div className="border-b last:border-b-0">
      <div 
        className={`p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${hasResponses ? '' : 'cursor-default'}`}
        onClick={() => hasResponses && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium">
            {(item.name || "G")[0].toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-gray-900">{item.name || "Guest"}</p>
            <p className="text-sm text-gray-500">{item.email}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {item.fields_completed?.map((field, fidx) => (
                <Badge key={fidx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  {field}
                </Badge>
              ))}
              {hasResponses && (
                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                  +{Object.keys(responses).length} fields
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasResponses && (
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
              <Eye className="w-4 h-4 mr-1" />
              {expanded ? 'Hide' : 'View All'}
            </Button>
          )}
          <div className="text-right">
            <p className="text-xs text-gray-400">
              {item.submitted_at ? new Date(item.submitted_at).toLocaleDateString() : "N/A"}
            </p>
          </div>
        </div>
      </div>
      
      {/* Expanded detail view showing ALL responses */}
      {expanded && hasResponses && (
        <div className="px-4 pb-4 bg-gray-50">
          <div className="bg-white rounded-lg border p-4 mt-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              All Intake Responses ({Object.keys(responses).length} fields)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(responses).map(([key, value]) => (
                <div key={key} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                    {formatFieldName(key)}
                  </p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                    {formatFieldValue(value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ============================================================================
// ACCESS SECTION - Magic Links, Admin Users, Preview Tokens (Per Master Plan §3)
// ============================================================================
function AccessSection() {
  const [accessLinks, setAccessLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState("magic-links");
  const [newLinkName, setNewLinkName] = useState("");
  const [newLinkEmail, setNewLinkEmail] = useState("");
  const [newLinkType, setNewLinkType] = useState("instructor");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchAccessLinks();
  }, []);

  const fetchAccessLinks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/access-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccessLinks(data.access_links || []);
      }
    } catch (err) {
      console.error("Error fetching access links:", err);
    } finally {
      setLoading(false);
    }
  };

  const createMagicLink = async () => {
    if (!newLinkName || !newLinkEmail) return;
    setCreating(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/create-pilot-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name: newLinkName, email: newLinkEmail, pilot_type: newLinkType })
      });
      if (res.ok) {
        toast.success("Magic link created!");
        setNewLinkName("");
        setNewLinkEmail("");
        fetchAccessLinks();
      }
    } catch (err) {
      toast.error("Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-8" data-testid="access-section">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Access</h1>
        <p className="text-gray-500">Magic Links, Admin Users, Preview Tokens</p>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="magic-links">Magic Links</TabsTrigger>
          <TabsTrigger value="admin-users">Admin Users</TabsTrigger>
          <TabsTrigger value="preview-tokens">Preview Tokens</TabsTrigger>
        </TabsList>

        <TabsContent value="magic-links">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create Magic Link</CardTitle>
              <CardDescription>Generate permanent or temporary access tokens</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input placeholder="Name" value={newLinkName} onChange={e => setNewLinkName(e.target.value)} data-testid="magic-link-name" />
                <Input placeholder="Email" value={newLinkEmail} onChange={e => setNewLinkEmail(e.target.value)} data-testid="magic-link-email" />
                <Select value={newLinkType} onValueChange={setNewLinkType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="makeup">Makeup</SelectItem>
                    <SelectItem value="hair">Hair</SelectItem>
                    <SelectItem value="nail">Nails</SelectItem>
                    <SelectItem value="events">Events</SelectItem>
                    <SelectItem value="wellness">Wellness</SelectItem>
                    <SelectItem value="culinary">Culinary</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={createMagicLink} disabled={creating} data-testid="create-magic-link-btn">
                  <Plus className="w-4 h-4 mr-2" /> Create
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Magic Links ({accessLinks.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-gray-400 text-sm">Loading...</p>
              ) : accessLinks.length === 0 ? (
                <p className="text-gray-400 text-sm">No access links found</p>
              ) : (
                <div className="space-y-2">
                  {accessLinks.map((link, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{link.name || link.email || "Unknown"}</p>
                        <p className="text-xs text-gray-500">{link.pilot_type || link.type} - {link.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{link.is_permanent ? "Permanent" : "Temporary"}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => {
                          const url = `${window.location.origin}/auth/magic?token=${link.token}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Link copied!");
                        }}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="admin-users">
          <Card>
            <CardHeader>
              <CardTitle>Admin Users</CardTitle>
              <CardDescription>Users with admin panel access</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-500 text-sm">Admin user management is available in the Super Admin section (Settings icon in top bar).</p>
              <Link to="/admin/super" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
                Go to Super Admin →
              </Link>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview-tokens">
          <Card>
            <CardHeader>
              <CardTitle>Preview Tokens</CardTitle>
              <CardDescription>Generate preview links for demos and investor access</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {["demo2026", "investor2026", "partner2026", "preview", "showme"].map(token => (
                  <div key={token} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{token}</p>
                      <p className="text-xs text-gray-500">{window.location.origin}/preview/{token}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/preview/${token}`);
                      toast.success("Preview link copied!");
                    }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// ADMIN EVENTS SECTION - Event management (Per Master Plan §3)
// ============================================================================
function AdminEventsSection() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/events/${eventId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Event deleted");
        fetchEvents();
      }
    } catch (err) {
      toast.error("Failed to delete event");
    }
  };

  const filteredEvents = events.filter(e => {
    if (filter === "all") return true;
    if (filter === "published") return e.status === "published";
    if (filter === "draft") return e.status === "draft";
    if (filter === "past") return new Date(e.date) < new Date();
    return true;
  });

  return (
    <div className="p-8" data-testid="events-section">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">Events</h1>
          <p className="text-gray-500">Manage all events across the platform</p>
        </div>
        <Button onClick={() => navigate("/host/events/new")} data-testid="create-event-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Event
        </Button>
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2 mb-6">
        {["all", "published", "draft", "past"].map(f => (
          <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)} {f === "all" ? `(${events.length})` : `(${events.filter(e => f === "published" ? e.status === "published" : f === "draft" ? e.status === "draft" : f === "past" ? new Date(e.date) < new Date() : true).length})`}
          </Button>
        ))}
      </div>

      {/* Events List */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-gray-200 border-t-black rounded-full animate-spin" /></div>
      ) : filteredEvents.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400">No events found</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filteredEvents.map(event => (
            <Card key={event.event_id || event._id} className="hover:shadow-md transition-shadow" data-testid={`event-card-${event.event_id}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: event.category_color || "#6366F1" }}>
                      {(event.pilot_category || event.category || "EVT").substring(0, 3).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-semibold">{event.title || "Untitled Event"}</h3>
                      <p className="text-sm text-gray-500">
                        {event.date ? new Date(event.date).toLocaleDateString() : "No date"} - {event.pilot_category || event.category || "General"}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Slug: <code className="bg-gray-100 px-1 rounded">/e/{event.slug || "N/A"}</code>
                        {event.rsvp_count !== undefined && ` - ${event.rsvp_count} RSVPs`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={event.status === "published" ? "default" : "secondary"}>
                      {event.status || "draft"}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/host/v2/${event.event_id}`)} data-testid={`manage-event-${event.event_id}`}>
                      <Settings className="w-4 h-4 mr-1" /> Manage
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => {
                      const url = `${window.location.origin}/e/${event.slug}`;
                      navigator.clipboard.writeText(url);
                      toast.success("Event link copied!");
                    }}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteEvent(event.event_id)} data-testid={`delete-event-${event.event_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// SUPPORT SECTION
// ============================================================================
function SupportSection() {
  return (
    <div className="space-y-6" data-testid="support-section">
      <div>
        <h1 className="text-xl font-bold">Support</h1>
        <p className="text-sm text-gray-500 mt-1">Help resources, documentation, and support tools</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium">Documentation</h3>
                <p className="text-xs text-gray-500">Platform guides and API docs</p>
              </div>
            </div>
            <a href="/admin/hub" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              View Docs <ChevronRight className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium">Help Widget Config</h3>
                <p className="text-xs text-gray-500">Configure the help widget settings</p>
              </div>
            </div>
            <a href="/admin/hub/help" className="text-sm text-purple-600 hover:underline flex items-center gap-1">
              Manage Widget <ChevronRight className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium">Activity Log</h3>
                <p className="text-xs text-gray-500">NDA, intake, and RSVP activity</p>
              </div>
            </div>
            <a href="/admin/activity" className="text-sm text-amber-600 hover:underline flex items-center gap-1">
              View Activity <ChevronRight className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium">Progress Journal</h3>
                <p className="text-xs text-gray-500">Track development progress</p>
              </div>
            </div>
            <a href="/admin/progress" className="text-sm text-green-600 hover:underline flex items-center gap-1">
              View Journal <ChevronRight className="w-3 h-3" />
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


// ============================================================
// ADMIN SETTINGS SECTION - Platform defaults, UI assets, integrations
// ============================================================
function AdminSettingsSection() {
  const API = process.env.REACT_APP_BACKEND_URL + "/api";
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const res = await fetch(`${API}/canonical/ui-assets`, { credentials: 'include' });
        const data = await res.json();
        setAssets(data.assets || {});
      } catch {}
      setLoading(false);
    };
    fetchAssets();
  }, []);

  const updateAsset = async (assetId, config) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API}/canonical/ui-assets/${assetId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config })
      });
      setAssets(prev => ({
        ...prev,
        [assetId]: { ...prev[assetId], config }
      }));
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    }
    setSaving(false);
  };

  if (loading) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const profileConfig = assets.default_profile_pic?.config || {};
  const orgConfig = assets.default_org_icon?.config || {};
  const hostingConfig = assets.default_hosting_cover?.config || {};

  return (
    <div className="p-6 max-w-6xl" data-testid="admin-settings-section">
      <h1 className="text-xl font-bold mb-2">Platform Settings</h1>
      <p className="text-sm text-gray-500 mb-8">Control default visuals, integrations, and platform behavior</p>

      {/* Copy & Content Editor link */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-4 h-4 inline-flex items-center justify-center rounded bg-blue-600 text-white text-[10px] font-bold">A</span>
            Copy & Content Editor
          </CardTitle>
          <CardDescription>Edit every UI string across login, /me, RSVP, host dashboard, and admin surfaces. Grouped by user-flow order.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => window.location.href = "/admin/copy-editor"}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="open-copy-editor-btn"
          >
            Open Copy Editor
          </Button>
        </CardContent>
      </Card>

      {/* Default Profile Picture */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4" />
            Default Profile Picture
            <span className="ml-2 cursor-help" title="Shows initials on colored background when user has no uploaded photo. Appears on: PHY card, nav bar, preference cards, client lists, admin People table.">
              <Info className="w-3.5 h-3.5 text-gray-400" />
            </span>
          </CardTitle>
          <CardDescription>Controls the default avatar shown when users have no uploaded profile photo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            {/* Preview */}
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-lg font-semibold"
              style={{ backgroundColor: profileConfig.background_color || '#000', color: profileConfig.text_color || '#fff' }}
            >
              MT
            </div>
            {/* Controls */}
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Background Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={profileConfig.background_color || '#000000'}
                      onChange={(e) => updateAsset('default_profile_pic', { ...profileConfig, background_color: e.target.value })}
                      className="w-8 h-8 rounded border cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">{profileConfig.background_color || '#000000'}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Text Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={profileConfig.text_color || '#FFFFFF'}
                      onChange={(e) => updateAsset('default_profile_pic', { ...profileConfig, text_color: e.target.value })}
                      className="w-8 h-8 rounded border cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">{profileConfig.text_color || '#FFFFFF'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Default Org Icon */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building className="w-4 h-4" />
            Default Organization Icon
            <span className="ml-2 cursor-help" title="Shows first letter of org name on colored background when org has no uploaded logo. Appears on: /me Hosting section, org settings, admin People > Organizations.">
              <Info className="w-3.5 h-3.5 text-gray-400" />
            </span>
          </CardTitle>
          <CardDescription>Controls the default icon for organizations without uploaded logos</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            <div
              className="w-16 h-16 flex items-center justify-center text-lg font-bold"
              style={{ backgroundColor: orgConfig.background_color || '#000', color: orgConfig.text_color || '#fff', borderRadius: orgConfig.border_radius || '12px' }}
            >
              M
            </div>
            <div className="space-y-3 flex-1">
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Background Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={orgConfig.background_color || '#000000'}
                      onChange={(e) => updateAsset('default_org_icon', { ...orgConfig, background_color: e.target.value })}
                      className="w-8 h-8 rounded border cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">{orgConfig.background_color || '#000000'}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Text Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={orgConfig.text_color || '#FFFFFF'}
                      onChange={(e) => updateAsset('default_org_icon', { ...orgConfig, text_color: e.target.value })}
                      className="w-8 h-8 rounded border cursor-pointer"
                    />
                    <span className="text-xs text-gray-500">{orgConfig.text_color || '#FFFFFF'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Default Hosting Cover */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Image className="w-4 h-4" />
            Default Hosting Card Background
            <span className="ml-2 cursor-help" title="Background color for hosting section cards when host has no uploaded cover image. Appears on: /me Hosting cards, host dashboard header.">
              <Info className="w-3.5 h-3.5 text-gray-400" />
            </span>
          </CardTitle>
          <CardDescription>Controls the background color for host dashboard cards without cover images</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            <div
              className="w-24 h-16 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: hostingConfig.background_color || '#1A1A1A' }}
            >
              <span className="text-white/80 text-xs">Preview</span>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Background Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={hostingConfig.background_color || '#1A1A1A'}
                  onChange={(e) => updateAsset('default_hosting_cover', { ...hostingConfig, background_color: e.target.value })}
                  className="w-8 h-8 rounded border cursor-pointer"
                />
                <span className="text-xs text-gray-500">{hostingConfig.background_color || '#1A1A1A'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integrations Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plug className="w-4 h-4" />
            Integrations
          </CardTitle>
          <CardDescription>Connected services and their sync status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { name: "Cloudinary", status: "active", desc: "Media uploads and asset management" },
              { name: "Beehiiv", status: "pending_rebuild", desc: "Email and lifecycle messaging (bidirectional sync)" },
              { name: "Acuity", status: "not_configured", desc: "Booking and scheduling (plug and play per host)" },
              { name: "Sinch", status: "not_configured", desc: "SMS OTP and notifications" },
            ].map(integration => (
              <div key={integration.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{integration.name}</p>
                  <p className="text-xs text-gray-500">{integration.desc}</p>
                </div>
                <Badge variant={integration.status === 'active' ? 'default' : 'secondary'}>
                  {integration.status === 'active' ? 'Active' : integration.status === 'pending_rebuild' ? 'Rebuilding' : 'Not Connected'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Super-admin migrated content: Company info, Legal docs, Help config, Portals */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="w-4 h-4 text-blue-500" />
            Company, Legal, Help & Portals
          </CardTitle>
          <CardDescription>Company address syncs to footer and privacy. Legal docs sync to /privacy and /terms. Help articles power on-platform info icons.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/legacy-config#company'} data-testid="settings-company-btn">
            <Building className="w-4 h-4 mr-2" /> Company Settings (address, contact, legal entity)
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/legacy-config#legal'} data-testid="settings-legal-btn">
            <Shield className="w-4 h-4 mr-2" /> Legal Files (Privacy, Terms, NDA)
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/legacy-config#help'} data-testid="settings-help-btn">
            <Info className="w-4 h-4 mr-2" /> Help Center (info-icon content per surface)
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/legacy-config#portals'} data-testid="settings-portals-btn">
            <Key className="w-4 h-4 mr-2" /> Private Portals (advocate, investor)
          </Button>
          <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/admin/copy-editor'} data-testid="settings-copy-btn">
            <Sparkles className="w-4 h-4 mr-2" /> Copy & Content Editor (every UI string)
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
